import axios from 'axios';
import { useAuthStore } from '../store/auth';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if already on login/register/init pages
      const currentPath = window.location.pathname;
      const publicPaths = ['/login', '/register', '/init'];
      if (!publicPaths.includes(currentPath)) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export interface LoginRequest {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

export interface LoginResponse {
  token: string;
  user: User;
  tenant: Tenant;
}

export interface InitRequest {
  email: string;
  password: string;
  tenantName: string;
}

export interface RegisterTenantRequest {
  email: string;
  password: string;
  companyName: string;
}

export interface ShippingMethodMapping {
  wooMethodId: string;
  wooMethodTitle: string;
  inpostService: 'inpost_locker_standard' | 'inpost_courier_standard' | 'inpost_courier_express' | null;
  isCOD: boolean;
}

export interface StoreSettings {
  syncToStatus?: string;
  syncStatuses?: string[];
  syncFromDate?: string | null;
  statusMappings?: Array<{
    wooStatus: string;
    ecomflowStatus: string;
  }>;
  defaultManualOrderStatusId?: string | null; // Domyślny status dla zamówień ręcznych
  shippingMethodMappings?: ShippingMethodMapping[];
}

export interface Store {
  id: string;
  name: string;
  platform: 'WOOCOMMERCE';
  url: string;
  isActive: boolean;
  syncEnabled: boolean;
  lastSyncAt: string | null;
  createdAt: string;
  settings?: StoreSettings;
  _count?: {
    orders: number;
  };
  engineIntegrationId?: string | null;
  engineEnabled?: boolean;
}

export interface WcShippingMethodDefinition {
  id: string;
  title: string;
  description: string;
}

export interface WcCountry {
  code: string;
  name: string;
  states?: Array<{
    code: string;
    name: string;
  }>;
}

export type WcGeneralSettingValue = string | number | boolean | null | string[];

export interface WcGeneralSetting {
  id: string;
  label: string;
  description: string;
  type: string;
  value: WcGeneralSettingValue;
  default?: WcGeneralSettingValue;
  tip?: string;
  placeholder?: string;
  options?: Record<string, string>;
}

export interface WcShippingZoneLocation {
  code: string;
  type: string;
}

export interface WcShippingMethodSetting {
  id?: string;
  label?: string;
  description?: string;
  type?: string;
  value?: unknown;
  default?: unknown;
  tip?: string;
  placeholder?: string;
  options?: Record<string, string>;
}

export interface WcShippingZoneMethod {
  instance_id: number;
  title: string;
  order: number;
  enabled: boolean;
  method_id: string;
  method_title: string;
  method_description: string;
  settings: Record<string, WcShippingMethodSetting>;
}

export interface WcShippingZone {
  id: number;
  name: string;
  order: number;
  isFallback?: boolean;
  locations: WcShippingZoneLocation[];
  methods: WcShippingZoneMethod[];
}

export interface WcShippingConfig {
  availableMethods: WcShippingMethodDefinition[];
  zones: WcShippingZone[];
}

export interface WcShippingMethodAsset {
  dispatchEstimate?: string;
  deliveryKind?: 'address' | 'pickup';
  pickupProvider?: 'plugin' | 'inpost-geowidget';
  pickupPointTypes?: 'lockers' | 'points' | 'all';
  pickupStorage?: 'native' | 'furgonetka';
  logoUrl?: string | null;
  logoMaxWidth?: number | null;
}

export interface WcShippingAssetsConfig {
  assets: Record<string, WcShippingMethodAsset>;
}

export interface WcPaymentMethod extends WcPaymentGateway {
  order: number;
  methodTitle: string;
  logoUrl: string;
  logoMaxWidth: number;
}

export interface WcPaymentGateway {
  id: string;
  title: string;
  description?: string;
  enabled: boolean;
}

export interface WcShippingCostRule {
  id: string;
  enabled: boolean;
  shippingMethodId?: string;
  minSubtotal?: string;
  maxSubtotal?: string;
  cost: string;
}

export interface WcShippingPaymentRule {
  paymentGatewayId: string;
  shippingMethodIds: string[];
}

export interface WcShippingRulesConfig {
  costRules: WcShippingCostRule[];
  paymentRules: WcShippingPaymentRule[];
  hiddenPaymentGatewayIds: string[];
  paymentGateways?: WcPaymentGateway[];
}

export interface WcTaxClass {
  slug: string;
  name: string;
}

export interface WcTaxRate {
  id?: number;
  country: string;
  state: string;
  postcode: string;
  city: string;
  rate: string;
  name: string;
  priority: number;
  compound: boolean;
  shipping: boolean;
  order?: number;
  class?: string;
}

export interface WcTaxConfig {
  enableTaxes: WcGeneralSetting | null;
  settings: WcGeneralSetting[];
  classes: WcTaxClass[];
  ratesByClass: Record<string, WcTaxRate[]>;
}

export interface WpSiteOptions {
  siteTitle: string;
  tagline: string;
  siteIconId: number | null;
  siteIconUrl: string;
  timezoneString: string;
  gmtOffset: number;
  permalinkStructure: string;
}

export interface CreateStoreRequest {
  name: string;
  platform?: 'WOOCOMMERCE';
  url: string;
  apiKey: string;
  apiSecret: string;
}

export interface Order {
  id: string;
  externalId: string;
  orderNumber: string;
  status: string;
  statusId: string | null;
  externalStatus: string;
  source?: 'WOOCOMMERCE' | 'MANUAL' | 'RESEND'; // Źródło zamówienia
  parentOrderId?: string | null;
  totalAmount: number;
  currency: string;
  customerEmail: string;
  customerName: string;
  customerPhone: string | null;
  billingAddress: Record<string, unknown>;
  shippingAddress: Record<string, unknown>;
  items: Record<string, unknown>[];
  totals?: {
    shippingTotal?: string;
    shippingTax?: string;
  };
  rawData?: {
    shipping_lines?: Array<{
      id: number;
      method_title: string;
      method_id: string;
      total: string;
      total_tax?: string;
    }>;
    shipping_total?: string;
    shipping_tax?: string;
  };
  paymentMethod: string | null;
  shippingMethod: string | null;
  notes: string | null;
  internalNotes: string | InternalNote[] | null;  // Can be string (legacy) or array (new)
  isOnHold?: boolean; // P10 (2026-04-30): Poczekalnia - pomijane przy bulk nadawaniu listów
  customerNotesCount?: number; // PB2 (2026-04-30): liczba CustomerNote dla tego zamówienia
  externalCreatedAt: string;
  statusChangedAt?: string | null; // When order was moved to current status
  createdAt: string;
  store: {
    id: string;
    name: string;
    platform: string;
  };
  shipments?: Array<{
    id: string;
    status: string;
    trackingNumber: string | null;
    carrier: string | null;
    createdAt: string;
  }>;
  invoices?: Array<{
    id: string;
    invoiceNumber: string | null;
    type: string;
    status: string;
    issueDate?: string | null;
    grossAmount?: number | null;
    currency?: string;
    ksefStatus?: string;
    viewUrl?: string | null;
    createdAt?: string;
  }>;
}

export interface OrdersResponse {
  data: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface OrderSyncRecoveryResponse {
  status:
    | 'worker_healthy'
    | 'completed'
    | 'recently_completed'
    | 'running'
    | 'disabled'
    | 'no_stores'
    | 'failed'
    | 'unavailable';
  workerStatus: 'healthy' | 'stale' | 'missing' | 'malformed' | 'unstable' | 'unknown';
  created: number;
  updated: number;
  storesProcessed: number;
  storesFailed: number;
  durationMs?: number;
}

export interface OrderStats {
  todayOrders: number;
  monthOrders: number;
  pendingShipment: number;
  monthRevenue: number;
  statusBreakdown: Record<string, number>;
}

export const authApi = {
  login: (data: LoginRequest) =>
    api.post<LoginResponse>('/auth/login', data),

  init: (data: InitRequest) =>
    api.post<LoginResponse>('/auth/init', data),

  registerTenant: (data: RegisterTenantRequest) =>
    api.post<LoginResponse>('/auth/register-tenant', data),

  me: () =>
    api.get<{ user: User; tenant: Tenant }>('/auth/me'),

  logout: () =>
    api.post('/auth/logout'),
};

export const storeApi = {
  getAll: () =>
    api.get<Store[]>('/stores'),

  getOne: (id: string) =>
    api.get<Store>(`/stores/${id}`),

  create: (data: CreateStoreRequest) =>
    api.post<Store>('/stores', data),

  update: (id: string, data: Partial<CreateStoreRequest & { isActive?: boolean; syncEnabled?: boolean; settings?: StoreSettings }>) =>
    api.patch<Store>(`/stores/${id}`, data),

  delete: (id: string) =>
    api.delete(`/stores/${id}`),

  test: (id: string) =>
    api.post<{ success: boolean; storeName?: string; error?: string }>(`/stores/${id}/test`),

  sync: (id: string) =>
    api.post<{ success: boolean; created: number; updated: number; total: number; message?: string }>(`/stores/${id}/sync`),

  getShippingMethods: (id: string) =>
    api.get<Array<{ id: string; title: string; description: string }>>(`/stores/${id}/shipping-methods`),

  getPaymentGateways: (id: string) =>
    api.get<Array<{ id: string; title: string; description: string; enabled: boolean }>>(`/stores/${id}/payment-gateways`),

  getShippingZoneMethods: (id: string) =>
    api.get<Array<{
      methodId: string;
      title: string;
      zoneName: string;
      enabled: boolean;
      baseMethodId: string;
    }>>(`/stores/${id}/shipping-zone-methods`),

  getShippingMethodsFromOrders: (id: string) =>
    api.get<Array<{
      methodId: string;
      methodTitle: string;
      orderCount: number;
    }>>(`/stores/${id}/shipping-methods-from-orders`),

  enableEngine: (storeId: string) =>
    api.post<{ apiKey: string; integrationId: string }>(`/stores/${storeId}/enable-engine`),

  disableEngine: (storeId: string) =>
    api.post<{ success: boolean }>(`/stores/${storeId}/disable-engine`),

  regenerateEngineKey: (storeId: string) =>
    api.post<{ apiKey: string }>(`/stores/${storeId}/regenerate-engine-key`),
};

export const orderApi = {
  recoverSync: () =>
    api.post<OrderSyncRecoveryResponse>('/orders/sync-recovery'),

  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    storeId?: string;
    shipmentBatchId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: 'externalCreatedAt' | 'orderNumber' | 'totalAmount' | 'shippingMethod' | 'status' | 'paymentMethod' | 'customerName' | 'statusChangedAt';
    sortOrder?: 'asc' | 'desc';
    // Advanced filters
    orderNumber?: string;
    customerName?: string;
    productName?: string;
    amountFrom?: number;
    amountTo?: number;
    paymentStatus?: string;
    deliveryStatus?: string;
  }) =>
    api.get<OrdersResponse>('/orders', { params }),

  // Create manual order
  create: (data: {
    storeId: string;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    shippingAddress?: {
      first_name?: string;
      last_name?: string;
      company?: string;
      address_1?: string;
      address_2?: string;
      city?: string;
      postcode?: string;
      country?: string;
      phone?: string;
      email?: string;
    };
    billingAddress?: {
      first_name?: string;
      last_name?: string;
      company?: string;
      address_1?: string;
      address_2?: string;
      city?: string;
      postcode?: string;
      country?: string;
      phone?: string;
      email?: string;
    };
    sameAsShipping?: boolean;
    items: Array<{
      name: string;
      quantity: number;
      price: string | number;
      total: string | number;
      total_tax?: string | number;
      sku?: string;
      productId?: string | null;
    }>;
    shippingMethod?: string;
    shippingMethodId?: string;
    shippingCost?: string | number;
    shippingTax?: string | number;
    paymentMethod?: string;
    isPaid?: boolean;
    paczkomatNumber?: string;
    statusId?: string | null;
    notes?: string;
    internalNotes?: string;
  }) =>
    api.post<Order>('/orders', data),

  getOne: (id: string) =>
    api.get<Order>(`/orders/${id}`),

  getIds: (params?: {
    status?: string;
    storeId?: string;
    shipmentBatchId?: string;
    search?: string;
    sortBy?: 'externalCreatedAt' | 'orderNumber' | 'totalAmount' | 'shippingMethod' | 'status' | 'paymentMethod' | 'customerName' | 'statusChangedAt';
    sortOrder?: 'asc' | 'desc';
    orderNumber?: string;
    customerName?: string;
    productName?: string;
    dateFrom?: string;
    dateTo?: string;
    amountFrom?: number;
    amountTo?: number;
    paymentStatus?: string;
    deliveryStatus?: string;
  }) =>
    api.get<{ ids: string[]; total: number }>('/orders/ids', { params }),

  getStats: () =>
    api.get<OrderStats>('/orders/stats'),

  update: (id: string, data: {
    status?: string;
    statusId?: string;
    notes?: string;
    internalNotes?: string;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    shippingAddress?: Record<string, string>;
    billingAddress?: Record<string, string>;
  }) =>
    api.patch<Order>(`/orders/${id}`, data),

  bulkUpdate: (orderIds: string[], statusOrId: string, useStatusId = false) =>
    api.post<{ success: boolean; updated: number }>('/orders/bulk-update',
      useStatusId ? { orderIds, statusId: statusOrId } : { orderIds, status: statusOrId }
    ),

  getHistory: (id: string) =>
    api.get<OrderHistoryEntry[]>(`/orders/${id}/history`),

  getCustomerHistory: (id: string) =>
    api.get<CustomerPurchaseHistory>(`/orders/${id}/customer-history`),

  duplicate: (id: string) =>
    api.post<Order>(`/orders/${id}/duplicate`),

  // Order items operations
  updateItem: (orderId: string, itemIndex: number, item: {
    name: string;
    quantity: number;
    price?: string;
    total: string;
    total_tax?: string;
    sku?: string;
    weight?: number;
    tax?: string;
  }) =>
    api.put<Order>(`/orders/${orderId}/items/${itemIndex}`, item),

  duplicateItem: (orderId: string, itemIndex: number) =>
    api.post<Order>(`/orders/${orderId}/items/${itemIndex}/duplicate`),

  deleteItem: (orderId: string, itemIndex: number) =>
    api.delete<Order>(`/orders/${orderId}/items/${itemIndex}`),

  // Shipping cost update
  updateShipping: (orderId: string, data: { shippingTotal: string | number; shippingTax?: string | number }) =>
    api.put<Order>(`/orders/${orderId}/shipping`, data),

  // Notes operations
  addNote: (orderId: string, content: string) =>
    api.post<{ success: boolean; note: InternalNote; order: Order }>(`/orders/${orderId}/notes`, { content }),

  updateNote: (orderId: string, noteId: string, content: string) =>
    api.patch<{ success: boolean; note: InternalNote; order: Order }>(`/orders/${orderId}/notes/${noteId}`, { content }),

  deleteNote: (orderId: string, noteId: string) =>
    api.delete<{ success: boolean; order: Order }>(`/orders/${orderId}/notes/${noteId}`),

  // P10 (2026-04-30): Toggle Poczekalnia
  toggleHold: (orderId: string) =>
    api.post<Order>(`/orders/${orderId}/toggle-hold`),
};

export interface InternalNote {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;
}

export interface OrderHistoryEntry {
  id: string;
  action: string;
  description?: string;
  metadata?: Record<string, unknown>;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  createdAt: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  } | null;
}

export interface CustomerPurchaseHistory {
  orders: Array<{
    id: string;
    orderNumber: string;
    totalAmount: number;
    currency: string;
    createdAt: string;
    status: {
      name: string;
      color: string;
    } | null;
  }>;
  totalLTV: number;
  currency: string;
  orderCount: number;
}

// Courier/Shipment types
export interface InPostSettings {
  organizationId: string;
  sandbox: boolean;
  defaultParcelSize: 'small' | 'medium' | 'large';
  defaultService: 'inpost_locker_standard' | 'inpost_courier_standard' | 'inpost_courier_express';
  senderName?: string;
  senderPhone?: string;
  senderEmail?: string;
  senderStreet?: string;
  senderBuildingNumber?: string;
  senderCity?: string;
  senderPostCode?: string;
  dropoffPoint?: string;
}

export interface InPostConfig extends InPostSettings {
  token: string;
}

export interface CourierIntegration {
  id: string;
  type: 'INPOST';
  isActive: boolean;
  settings: InPostSettings;
  hasCredentials: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Shipment {
  id: string;
  orderId: string;
  carrier: 'INPOST' | 'DPD' | 'DHL' | 'FEDEX' | 'OTHER';
  trackingNumber: string | null;
  status: 'CREATED' | 'CONFIRMED' | 'DISPATCHED' | 'IN_TRANSIT' | 'OUT_FOR_DELIVERY' | 'DELIVERED' | 'RETURNED' | 'FAILED';
  labelUrl: string | null;
  shipmentData: Record<string, unknown>;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
  order?: {
    id: string;
    orderNumber: string;
    customerName: string;
    totalAmount: number;
  };
}

export interface CreateShipmentRequest {
  orderId: string;
  batchId?: string;
  service?: 'inpost_locker_standard' | 'inpost_courier_standard' | 'inpost_courier_express';
  parcelSize?: 'small' | 'medium' | 'large';
  targetPoint?: string;
  weight?: number;
  codAmount?: number;
  insurance?: number;
  reference?: string;
  comments?: string;
  skipPreValidation?: boolean;
}

export interface ShipmentBatchDetails {
  id: string;
  status: 'CREATING' | 'PROCESSING' | 'COMPLETED' | 'COMPLETED_WITH_ERRORS';
  creationFinishedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  counts: {
    total: number;
    waitingForShipment: number;
    shipmentsCreated: number;
    shipmentFailures: number;
    postProcessingCompleted: number;
    postProcessingFailed: number;
    postProcessingPending: number;
  };
  destinationStatuses: Array<{ id: string | null; name: string; count: number }>;
  errors: Array<{
    orderId: string;
    orderNumber: string;
    stage: 'shipment' | 'post_processing';
    message: string;
  }>;
  retrying?: Array<{ orderId: string; orderNumber: string; message: string }>;
}

export interface ShipmentsResponse {
  shipments: Shipment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PaczkomatPoint {
  name: string;
  address: {
    line1: string;
    line2: string;
  };
  address_details: {
    city: string;
    post_code: string;
    street: string;
    building_number: string;
  };
  location: {
    latitude: number;
    longitude: number;
  };
  type: string[];
  status: string;
}

export const courierApi = {
  // Get all courier integrations
  getAll: () =>
    api.get<CourierIntegration[]>('/couriers'),

  // Get InPost configuration
  getInPost: () =>
    api.get<{
      configured: boolean;
      isActive: boolean;
      settings: InPostSettings;
      hasToken: boolean;
    }>('/couriers/inpost'),

  // Save InPost configuration
  saveInPost: (data: InPostConfig) =>
    api.put<{ success: boolean; message: string }>('/couriers/inpost', data),

  // Test InPost connection
  testInPost: (data: { token: string; organizationId: string; sandbox?: boolean }) =>
    api.post<{ success: boolean; message?: string; organizationName?: string; error?: string }>('/couriers/inpost/test', data),

  // Toggle InPost active state
  toggleInPost: () =>
    api.patch<{ success: boolean; isActive: boolean; message: string }>('/couriers/inpost/toggle'),

  // Search paczkomat points
  searchPoints: (params?: { city?: string; postCode?: string; name?: string; type?: string }) =>
    api.get<{ items: PaczkomatPoint[]; count: number }>('/couriers/inpost/points', { params }),

  // Get sender configuration
  getSender: () =>
    api.get<{
      configured: boolean;
      sender: {
        name: string;
        phone: string;
        email: string;
        street: string;
        buildingNumber: string;
        city: string;
        postCode: string;
      };
      dropoffPoint: string;
      defaultParcelSize: string;
      defaultService: string;
    }>('/couriers/inpost/sender'),
};

export const shipmentApi = {
  // Get all shipments
  getAll: (params?: { orderId?: string; status?: string; page?: number; limit?: number }) =>
    api.get<ShipmentsResponse>('/shipments', { params }),

  // Get single shipment
  getOne: (id: string) =>
    api.get<Shipment>(`/shipments/${id}`),

  // Create shipment
  create: (data: CreateShipmentRequest) =>
    api.post<{ success: boolean; message: string; shipment: Shipment }>('/shipments', data),

  createBatch: (orderIds: string[]) =>
    api.post<{ success: boolean; batch: ShipmentBatchDetails }>('/shipments/batches', { orderIds }),

  getBatch: (batchId: string) =>
    api.get<{ success: boolean; batch: ShipmentBatchDetails }>(`/shipments/batches/${batchId}`, { timeout: 15_000 }),

  finishBatch: (batchId: string, skippedOrderIds: string[] = []) =>
    api.post<{ success: boolean; batch: ShipmentBatchDetails }>(`/shipments/batches/${batchId}/finish`, { skippedOrderIds }),

  // Bulk create shipments
  bulkCreate: (data: {
    orderIds: string[];
    service?: 'inpost_locker_standard' | 'inpost_courier_standard' | 'inpost_courier_express';
    parcelSize?: 'small' | 'medium' | 'large';
  }) =>
    api.post<{
      success: boolean;
      message: string;
      results: Array<{
        orderId: string;
        orderNumber: string;
        success: boolean;
        trackingNumber?: string;
        error?: string;
      }>;
    }>('/shipments/bulk', data),

  // Get shipment label as base64 PDF
  getLabel: (id: string) =>
    api.get<{ data: string; contentType: string; filename: string }>(`/shipments/${id}/label`),

  // Buy/confirm shipment
  buy: (id: string) =>
    api.post<{ success: boolean; message: string }>(`/shipments/${id}/buy`),

  // Cancel shipment
  cancel: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/shipments/${id}`),

  // Sync shipment status from InPost
  syncStatus: (id: string) =>
    api.post<{ success: boolean; status: string; inpostStatus: string }>(`/shipments/${id}/sync`),
};

// Invoice types
export type InvoiceType = 'VAT' | 'PROFORMA' | 'CORRECTION' | 'ADVANCE' | 'FINAL' | 'RECEIPT';
export type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'SENT' | 'PAID' | 'CANCELLED' | 'DELETED';
export type KsefStatus = 'NOT_APPLICABLE' | 'PENDING' | 'PROCESSING' | 'OK' | 'SEND_ERROR' | 'SERVER_ERROR';

export interface Invoice {
  id: string;
  tenantId: string;
  orderId: string;
  externalId: string | null;
  fakturowniaId: string | null;
  invoiceNumber: string | null;
  type: InvoiceType;
  status: InvoiceStatus;
  issueDate: string | null;
  sellDate: string | null;
  paymentDueDate: string | null;
  netAmount: number | null;
  grossAmount: number | null;
  vatAmount: number | null;
  currency: string;
  buyerName: string | null;
  buyerTaxNo: string | null;
  buyerStreet: string | null;
  buyerPostCode: string | null;
  buyerCity: string | null;
  buyerCountry: string;
  buyerEmail: string | null;
  buyerCompany: boolean;
  ksefStatus: KsefStatus;
  ksefId: string | null;
  ksefNumber: string | null;
  ksefSentAt: string | null;
  ksefError: string | null;
  departmentId: string | null;
  viewUrl: string | null;
  pdfUrl: string | null;
  positions: unknown[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  order?: {
    id: string;
    orderNumber: string;
    customerName: string;
    customerEmail?: string;
    store?: { name: string };
  };
}

export interface FakturowniaDepartment {
  id: number;
  name: string;
  shortcut: string;
  tax_no: string;
  main: boolean;
}

export interface FakturowniaSettings {
  domain: string;
  autoSendToKsef: 'disabled' | 'pl_companies' | 'all_companies' | 'all';
  autoMarkAsPaid: boolean;
  deleteFromFakturownia: boolean;
  defaultPaymentDays: number;
  defaultTaxRate: number;
  defaultUnit: string;
  issueDateSource: 'order_date' | 'today';
  sellDateSource: 'order_date' | 'shipment_date' | 'delivery_date' | 'today';
  storeBindings: Record<string, number>;
  departments: FakturowniaDepartment[];
}

export const invoiceApi = {
  // Get Fakturownia configuration
  getConfig: () =>
    api.get<{
      configured: boolean;
      isActive: boolean;
      settings: FakturowniaSettings;
      hasToken: boolean;
    }>('/invoices/config'),

  // Save Fakturownia configuration
  saveConfig: (data: {
    apiToken: string;
    domain: string;
    autoSendToKsef?: string;
    autoMarkAsPaid?: boolean;
    deleteFromFakturownia?: boolean;
    defaultPaymentDays?: number;
    defaultTaxRate?: number;
    defaultUnit?: string;
    issueDateSource?: string;
    sellDateSource?: string;
    storeBindings?: Record<string, number>;
  }) =>
    api.put<{ success: boolean; message: string }>('/invoices/config', data),

  // Test Fakturownia connection
  testConnection: (data: { apiToken: string; domain: string }) =>
    api.post<{ success: boolean; message?: string; accountName?: string; error?: string }>('/invoices/config/test', data),

  // Get departments
  getDepartments: () =>
    api.get<{ departments: FakturowniaDepartment[] }>('/invoices/departments'),

  // Toggle active state
  toggle: () =>
    api.patch<{ success: boolean; isActive: boolean; message: string }>('/invoices/config/toggle'),

  // Get all invoices
  getAll: (params?: {
    page?: number;
    limit?: number;
    type?: InvoiceType;
    status?: InvoiceStatus;
    ksefStatus?: KsefStatus;
  }) =>
    api.get<{
      invoices: Invoice[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>('/invoices', { params }),

  // Get invoices for order
  getForOrder: (orderId: string) =>
    api.get<{ invoices: Invoice[] }>(`/invoices/order/${orderId}`),

  // Get single invoice
  getOne: (id: string) =>
    api.get<Invoice>(`/invoices/${id}`),

  // Create invoice
  create: (data: {
    orderId: string;
    departmentId?: number;
    issueDate?: string;
    sellDate?: string;
    sendToKsef?: boolean;
  }) =>
    api.post<{ success: boolean; message: string; invoice: Invoice }>('/invoices', data),

  // Bulk create invoices
  bulkCreate: (data: {
    orderIds: string[];
    departmentId?: number;
    sendToKsef?: boolean;
  }) =>
    api.post<{
      success: boolean;
      message: string;
      results: Array<{
        orderId: string;
        orderNumber: string;
        success: boolean;
        invoiceNumber?: string;
        error?: string;
      }>;
    }>('/invoices/bulk', data),

  // Delete invoice
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/invoices/${id}`),

  // Download PDF
  getPdf: (id: string) =>
    api.get<{ data: string; contentType: string; filename: string }>(`/invoices/${id}/pdf`),

  // Send to KSeF
  sendToKsef: (id: string) =>
    api.post<{ success: boolean; message: string; ksefStatus: string; ksefId?: string }>(`/invoices/${id}/send-ksef`),

  // Check KSeF status
  checkKsefStatus: (id: string) =>
    api.get<{ ksefStatus: string; ksefId?: string; ksefError?: string[] }>(`/invoices/${id}/ksef-status`),

  // Mark invoice as paid
  markAsPaid: (id: string, paidDate?: string) =>
    api.post<{ success: boolean; message: string }>(`/invoices/${id}/mark-paid`, { paidDate }),

  // Create correction invoice
  createCorrection: (originalInvoiceId: string, options?: {
    reason?: string;
    positions?: Array<{
      name: string;
      quantity: number;
      price: number;
      tax: number;
    }>;
  }) =>
    api.post<{ success: boolean; message: string; invoice: Invoice }>(`/invoices/${originalInvoiceId}/correction`, options),

  // Get correction settings
  getCorrectionConfig: () =>
    api.get<{
      series: string;
      defaultReason: string;
      zeroQuantities: boolean;
    }>('/invoices/correction/config'),

  // Save correction settings
  saveCorrectionConfig: (data: {
    series: string;
    defaultReason: string;
    zeroQuantities: boolean;
  }) =>
    api.put<{ success: boolean; message: string }>('/invoices/correction/config', data),
};

// Email types
export interface SmtpSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password?: string;
  fromEmail: string;
  fromName: string;
}

export interface DeliverabilityRecommendation {
  host: string;
  type: 'TXT' | 'CNAME' | 'MX';
  value: string;
  instructions: string;
}

export interface DeliverabilityRecordCheck {
  found: boolean;
  valid: boolean;
  value: string | null;
  notes: string[];
  recommendation: DeliverabilityRecommendation | null;
}

export interface DeliverabilityDkimCheck extends DeliverabilityRecordCheck {
  selectorTried: string[];
  selectorFound: string | null;
}

export interface DeliverabilityCheckResult {
  domain: string;
  spf: DeliverabilityRecordCheck;
  dkim: DeliverabilityDkimCheck;
  dmarc: DeliverabilityRecordCheck;
  mtaSts: DeliverabilityRecordCheck;
  bimi: DeliverabilityRecordCheck;
  score: number;
  summary: string;
}

export interface DeliverabilityStatsResult {
  domain: string;
  days: number;
  since: string;
  counts: {
    total: number;
    delivered: number;
    bouncedHard: number;
    bouncedSoft: number;
    suppressed: number;
    failed: number;
    pending: number;
    unsubscribed: number;
  };
}

export interface DmarcReportsResult {
  domain: string;
  days: number;
  since: string;
  reportCount: number;
  aggregate: {
    totalMessages: number;
    totalAligned: number;
    totalDkimPass: number;
    totalSpfPass: number;
    alignedPercent: number;
    dkimPassPercent: number;
    spfPassPercent: number;
  };
  topFailSources: Array<{ ip: string; failCount: number; passCount: number }>;
  reports: Array<{
    id: string;
    orgName: string;
    dateBegin: string;
    dateEnd: string;
    totalCount: number;
    alignedCount: number;
    alignedPercent: number;
  }>;
}

// DNS Wizard - kontekstowe rekomendacje SPF/DMARC/DKIM dla operatora
// niezaznajomionego z protokołami. Zwraca gotowe wartości do podmiany +
// listę wykrytych kanałów wysyłki (SMTP klienta, MailerLite itd.).
export interface DnsRecommendationsChannel {
  provider: string;
  evidence: 'dkim_selector' | 'store_config';
  selectors?: string[];
  status?: string;
  host?: string;
  fromEmail?: string;
  requiresInSpf?: string;
  authStatus?: 'authorized' | 'missing_spf' | 'unknown';
  authStatusReason?: string;
  isActive?: boolean;
}

export interface DnsRecommendationsOverallStatus {
  level: 'ok' | 'warnings' | 'critical';
  title: string;
  message: string;
  changesRequired: number;
}

export interface DnsRecommendationRecord {
  action: 'create' | 'edit' | 'no_change';
  currentValue: string | null;
  newValue: string | null;
  diff?: { added: string[]; preserved: string[] };
  reason: string;
  warning?: string;
}

export interface DnsRecommendationDkim {
  selector: string;
  action: 'no_change';
  provider: string | null;
  recordType: 'TXT' | 'CNAME';
  warning?: string;
}

export interface DnsRecommendationsResult {
  domain: string;
  channels: DnsRecommendationsChannel[];
  activeSendingMode: 'smtp' | 'unknown';
  activeChannelDescription?: string;
  records: {
    spf: DnsRecommendationRecord;
    dmarc: DnsRecommendationRecord;
    dkim: DnsRecommendationDkim[];
  };
  overallStatus: DnsRecommendationsOverallStatus;
  warnings: string[];
}

export interface EmailTemplate {
  id: string;
  tenantId: string;
  storeId?: string | null;
  name: string;
  slug: string;
  subject: string;
  bodyHtml: string;
  variables: string[];
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  store?: {
    id: string;
    name: string;
  };
}

export interface EmailLog {
  id: string;
  tenantId: string;
  orderId?: string;
  templateId?: string;
  toEmail: string;
  subject: string;
  bodyHtml: string;
  status: 'pending' | 'sent' | 'failed';
  error?: string;
  sentAt?: string;
  createdAt: string;
  order?: {
    orderNumber: string;
    customerName: string;
    customerEmail?: string;
  };
  template?: {
    name: string;
  };
}

export interface TemplateVariable {
  key: string;
  description: string;
  example: string;
}

export interface StoreEmailConfig {
  id: string;
  tenantId: string;
  storeId: string;
  name: string;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
  phone?: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  store?: {
    id: string;
    name: string;
    url: string;
  };
}

export interface EmailRoutingSummary {
  storeId: string;
  operatorReplyConfigId: string | null;
  notificationMode: 'INBOX' | 'CUSTOM';
  notificationEmailConfigId: string | null;
  configs: Array<{
    id: string;
    name: string;
    host: string;
    fromEmail: string;
    fromName: string;
    isActive: boolean;
  }>;
  inboxConfigs: Array<{
    id: string;
    name: string;
    fromEmail: string;
    isActive: boolean;
    smtpConfigId: string | null;
  }>;
  operator: EmailRouteChannelSummary;
  notification: EmailRouteChannelSummary;
}

export interface EmailRouteChannelSummary {
  ready: boolean;
  configId: string | null;
  fromEmail: string | null;
  fromName: string | null;
  provider: string | null;
}

export const emailApi = {
  // SMTP Settings
  getSettings: () =>
    api.get<{
      configured: boolean;
      isActive: boolean;
      settings: SmtpSettings | null;
    }>('/email/settings'),

  saveSettings: (data: SmtpSettings) =>
    api.post<{ success: boolean; message: string }>('/email/settings', data),

  testSettings: (data: SmtpSettings & { testEmail?: string }) =>
    api.post<{ success: boolean; message?: string; error?: string }>('/email/settings/test', data),

  toggleSettings: () =>
    api.patch<{ success: boolean; isActive: boolean; message: string }>('/email/settings/toggle'),

  getRouting: (storeId: string) =>
    api.get<{ data: EmailRoutingSummary }>(`/email/routing/${storeId}`),

  updateRouting: (storeId: string, data: {
    operatorReplyConfigId?: string | null;
    notificationMode: 'INBOX' | 'CUSTOM';
    notificationEmailConfigId?: string | null;
  }) => api.put<{ success: boolean; data: EmailRoutingSummary }>(`/email/routing/${storeId}`, data),

  testRouting: (storeId: string, channel: 'operator' | 'notification') =>
    api.post<{ success: boolean; message?: string; error?: string }>(`/email/routing/${storeId}/test/${channel}`),

  // Deliverability — sprawdzenie konfiguracji SPF/DKIM/DMARC/MTA-STS dla domeny
  checkDeliverability: (domain: string) =>
    api.get<DeliverabilityCheckResult>('/email/deliverability/check', { params: { domain } }),

  // Deliverability stats — agregacja EmailLog za N dni dla domeny wysyłającej
  getDeliverabilityStats: (domain: string, days = 30) =>
    api.get<DeliverabilityStatsResult>('/email/deliverability/stats', { params: { domain, days } }),

  // DMARC aggregate reports — Google/Yahoo/Microsoft codzienne raporty
  getDmarcReports: (domain: string, days = 30) =>
    api.get<DmarcReportsResult>('/email/dmarc/reports', { params: { domain, days } }),

  // DNS Wizard — kontekstowe rekomendacje SPF/DMARC/DKIM (cross-reference z
  // aktywnymi kanałami wysyłki tenanta). Auto-fetch przy mount karty domeny.
  getDnsRecommendations: (domain: string) =>
    api.get<DnsRecommendationsResult>('/email/deliverability/recommendations', { params: { domain } }),

  // Templates
  getTemplates: (storeId?: string) =>
    api.get<{ templates: EmailTemplate[] }>('/email/templates', { params: storeId ? { storeId } : undefined }),

  getTemplate: (id: string) =>
    api.get<{ template: EmailTemplate }>(`/email/templates/${id}`),

  createTemplate: (data: Partial<EmailTemplate>) =>
    api.post<{ success: boolean; message: string; template: EmailTemplate }>('/email/templates', data),

  updateTemplate: (id: string, data: Partial<EmailTemplate>) =>
    api.put<{ success: boolean; message: string; template: EmailTemplate }>(`/email/templates/${id}`, data),

  deleteTemplate: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/email/templates/${id}`),

  duplicateTemplate: (id: string) =>
    api.post<{ success: boolean; message: string; template: EmailTemplate }>(`/email/templates/${id}/duplicate`),

  previewTemplate: (id: string, orderId?: string) =>
    api.post<{
      preview: { subject: string; bodyHtml: string };
      variables: Record<string, string>;
    }>(`/email/templates/${id}/preview`, { orderId }),

  initTemplates: (storeId?: string) =>
    api.post<{ success: boolean; message: string; templates: EmailTemplate[] }>('/email/templates/init', { storeId }),

  getVariables: () =>
    api.get<{ variables: TemplateVariable[] }>('/email/templates/variables'),

  // Sending
  sendFromTemplate: (data: { templateId: string; orderId: string; customVariables?: Record<string, string> }) =>
    api.post<{ success: boolean; message?: string; error?: string; logId?: string }>('/email/send', data),

  sendCustom: (data: { orderId: string; toEmail: string; subject: string; bodyHtml: string; attachInvoice?: boolean }) =>
    api.post<{
      success: boolean;
      message?: string;
      error?: string;
      logId?: string;
      debug?: {
        smtpHost: string | null;
        fromEmail: string | null;
        messageId: string | null;
        sentFolder: { appended: boolean; imapHost?: string; error?: string };
      };
    }>('/email/send-custom', data),

  // Logs
  getLogs: (params?: { page?: number; limit?: number; status?: string; orderId?: string }) =>
    api.get<{
      logs: EmailLog[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>('/email/logs', { params }),

  getLog: (id: string) =>
    api.get<{ log: EmailLog }>(`/email/logs/${id}`),

  resend: (id: string) =>
    api.post<{ success: boolean; message?: string; error?: string }>(`/email/logs/${id}/resend`),

  getOrderEmails: (orderId: string) =>
    api.get<{ logs: EmailLog[] }>(`/email/orders/${orderId}/emails`),

  // Store Email Configs (multi-account per store)
  getStoreConfigs: () =>
    api.get<{ configs: StoreEmailConfig[] }>('/email/store-configs'),

  getStoreConfigsByStore: (storeId: string) =>
    api.get<{ configs: StoreEmailConfig[] }>(`/email/store-configs/store/${storeId}`),

  createStoreConfig: (data: Omit<StoreEmailConfig, 'id' | 'tenantId' | 'createdAt' | 'updatedAt' | 'store'>) =>
    api.post<{ success: boolean; message: string; config: StoreEmailConfig }>('/email/store-configs', data),

  updateStoreConfig: (id: string, data: Partial<Omit<StoreEmailConfig, 'id' | 'tenantId' | 'createdAt' | 'updatedAt' | 'store'>>) =>
    api.put<{ success: boolean; message: string; config: StoreEmailConfig }>(`/email/store-configs/${id}`, data),

  deleteStoreConfig: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/email/store-configs/${id}`),

  testStoreConfig: (id: string, testEmail?: string) =>
    api.post<{ success: boolean; message?: string; error?: string }>(`/email/store-configs/${id}/test`, { testEmail }),

  setStoreConfigDefault: (id: string) =>
    api.post<{ success: boolean; message: string }>(`/email/store-configs/${id}/set-default`),
};

// Automation types
export type TriggerType =
  | 'order.created'
  | 'order.status_changed'
  | 'order.updated'
  | 'shipment.created'
  | 'shipment.status_changed'
  | 'shipment.validation_error'
  | 'shipment.label_printed'
  | 'invoice.created'
  | 'manual.trigger';

export type ActionType =
  | 'change_status'
  | 'sync_status_woo'
  | 'create_shipment'
  | 'send_email'
  | 'create_invoice'
  | 'create_correction'
  | 'create_refund'
  | 'add_note'
  | 'add_tag'
  | 'remove_tag'
  | 'webhook'
  | 'delay';

export interface TriggerMeta {
  type: TriggerType;
  name: string;
  description: string;
  category: string;
  icon: string;
  configFields: Array<{
    name: string;
    label: string;
    type: string;
    required?: boolean;
    options?: Array<{ value: string; label: string }>;
  }>;
}

export interface ActionMeta {
  type: ActionType;
  name: string;
  description: string;
  category: string;
  icon: string;
  configFields: Array<{
    name: string;
    label: string;
    type: string;
    required?: boolean;
    options?: Array<{ value: string; label: string }>;
  }>;
}

export interface ConditionMeta {
  field: string;
  label: string;
  type: string;
  operators: string[];
}

export interface AutomationGroup {
  id: string;
  tenantId: string;
  name: string;
  color: string;
  position: number;
  isExpanded: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { automations: number };
}

export type TriggerMode = 'automatic' | 'manual';

export interface Automation {
  id: string;
  tenantId: string;
  groupId?: string | null;
  name: string;
  description?: string;
  trigger: {
    type: TriggerType;
    config: Record<string, unknown>;
  };
  conditions: Array<{
    field: string;
    operator: string;
    value: unknown;
  }>;
  actions: Array<{
    type: ActionType;
    config: Record<string, unknown>;
    continueOnError?: boolean;
  }>;
  isActive: boolean;
  priority: number;
  position: number;
  runCount: number;
  lastRunAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
  triggerName?: string;
  triggerCategory?: string;
  group?: {
    id: string;
    name: string;
    color: string;
  } | null;
  // Manual trigger mode
  triggerMode?: TriggerMode;
  buttonIcon?: string | null;
  buttonText?: string | null;
  visibleInStores?: string[] | null;
}

export interface ManualAutomation {
  id: string;
  name: string;
  description?: string;
  buttonIcon?: string | null;
  buttonText?: string | null;
  visibleInStores?: string[] | null;
  trigger: {
    type: TriggerType;
    config: Record<string, unknown>;
  };
  conditions: Array<{
    field: string;
    operator: string;
    value: unknown;
  }>;
}

export interface AutomationLog {
  id: string;
  tenantId: string;
  automationId: string;
  orderId?: string;
  status: 'success' | 'failed' | 'skipped' | 'processing';
  triggerType: string;
  triggerData?: Record<string, unknown>;
  conditionsResult?: Record<string, unknown>;
  executedActions?: Record<string, unknown>;
  error?: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  createdAt: string;
  automation?: {
    id: string;
    name: string;
  };
  order?: {
    id: string;
    orderNumber: string;
    customerName: string;
  };
}

// Condition types for automation - supports simple conditions and groups with AND/OR logic
export interface SimpleConditionType {
  field: string;
  operator: string;
  value: unknown;
}

export interface ConditionGroupType {
  logic: 'AND' | 'OR';
  conditions: Array<SimpleConditionType | ConditionGroupType>;
}

export type ConditionItemType = SimpleConditionType | ConditionGroupType;

export interface DashboardSummary {
  finance: {
    revenueMTD: number;
    incomeMTD: number;
    profitMTD: number;
    deltas: Record<string, number>;
    sparklines: Record<string, number[]>;
  };
  logistics: {
    ordersMTD: number;
    itemsSoldMTD: number;
    aovMTD: number;
    toPackCount: number;
    deltas: Record<string, number>;
    sparklines: Record<string, number[]>;
  };
  inbox: { unreadCount: number };
  lab: { toTestCount: number };
  gamification: {
    level: number;
    levelName: string;
    rank: string;
    rankName: string;
    cumulativeRevenue: number;
    currentThreshold: number;
    nextThreshold: number | null;
    progressPct: number;
    color: string;
    imageFile: string;
    imagePath: string;
    description: string;
    maxLevel: number;
    checklistStreak: number;
  };
  goal: {
    revenueGoal: number | null;
    revenueMTD: number;
    progressPct: number;
    expectedPct: number;
    annualRevenueGoal: number | null;
    revenueYTD: number;
    annualProgressPct: number;
    annualExpectedPct: number;
  };
  quote: { text: string; author: string | null };
  tip: { text: string; category: string | null; dayOfYear: number };
}

export type DashboardChecklistScope = 'DAY' | 'WEEK' | 'MONTH' | 'DONE';

export interface DashboardChecklistTask {
  id: string;
  tenantId: string;
  title: string;
  scope: Exclude<DashboardChecklistScope, 'DONE'>;
  sortOrder: number;
  completedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardGoal {
  id: string;
  tenantId: string;
  year: number;
  month: number;
  revenueGoal: string | number;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardAnnualGoal {
  id: string;
  tenantId: string;
  year: number;
  revenueGoal: string | number;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardAchievement {
  code: string;
  label: string;
  description: string;
  category: string;
  unlockedAt: string | null;
}

export interface DashboardLevel {
  level: number;
  name: string | null;
  rank: string;
  rankName: string;
  threshold: number;
  color: string;
  imageFile: string | null;
  imagePath: string;
  description: string | null;
  unlocked: boolean;
}

export interface DashboardHeatmap {
  days: number;
  max: number;
  matrix: Array<{
    day: number;
    hours: Array<{ hour: number; count: number }>;
  }>;
}

export const dashboardApi = {
  getSummary: () =>
    api.get<DashboardSummary>('/dashboard/summary'),
  getChecklist: (scope: DashboardChecklistScope) =>
    api.get<{ data: DashboardChecklistTask[] }>('/dashboard/checklist', { params: { scope } }),
  createChecklistTask: (data: { title: string; scope: Exclude<DashboardChecklistScope, 'DONE'> }) =>
    api.post<DashboardChecklistTask>('/dashboard/checklist', data),
  toggleChecklistTask: (id: string) =>
    api.patch<DashboardChecklistTask>(`/dashboard/checklist/${id}/toggle`),
  updateChecklistTask: (id: string, data: Partial<{ title: string; sortOrder: number }>) =>
    api.patch<DashboardChecklistTask>(`/dashboard/checklist/${id}`, data),
  deleteChecklistTask: (id: string) =>
    api.delete(`/dashboard/checklist/${id}`),
  getGoal: (params: { year: number; month: number }) =>
    api.get<DashboardGoal | null>('/dashboard/goal', { params }),
  updateGoal: (data: { year: number; month: number; revenueGoal: number }) =>
    api.put<DashboardGoal>('/dashboard/goal', data),
  getAnnualGoal: (params: { year: number }) =>
    api.get<DashboardAnnualGoal | null>('/dashboard/annual-goal', { params }),
  updateAnnualGoal: (data: { year: number; revenueGoal: number }) =>
    api.put<DashboardAnnualGoal>('/dashboard/annual-goal', data),
  getAchievements: () =>
    api.get<{ data: DashboardAchievement[] }>('/dashboard/achievements'),
  getLevels: () =>
    api.get<{ data: DashboardLevel[] }>('/dashboard/levels'),
  getHeatmap: (days = 30) =>
    api.get<DashboardHeatmap>('/dashboard/orders-heatmap', { params: { days } }),
};

export type FinanceCostRuleKind = 'PRODUCT_COST' | 'SHIPPING_COST' | 'TRANSACTION_FEE' | 'FIXED_COST';
export interface FinanceCostRuleSource {
  tenantId: string; storeId: string | null; kind: FinanceCostRuleKind; ruleId: string;
  parent: Record<string,string | boolean | null>;
  versions: Array<{revisionId:string;validFrom:string;validTo:string|null;recordedAt:string;fields:Record<string,string|boolean>}>;
}
export interface FinanceCostSnapshotHistory {
  knowledgeAsOf:string;
  snapshot:{id:string;tenantId:string;storeId:string|null;kind:FinanceCostRuleKind;ruleId:string;ordinal:number;contentHash:string;
    recordedAt:string;quality:'OBSERVED_FINANCE_RULES'}|null;
  source:FinanceCostRuleSource|null;
  changes:{parentChanged:boolean;added:string[];removed:string[];changed:string[]}|null;
}
export const financeApi = {
  getCostRuleSource: (kind:FinanceCostRuleKind,ruleId:string,storeId:string|null) =>
    api.get<FinanceCostRuleSource & {contentHash:string;acquiredAt:string}>(`/finance/costs/${kind}/${ruleId}/source-history`,{params:{storeId:storeId??undefined}}),
  archiveCostRule: (kind:FinanceCostRuleKind,ruleId:string,storeId:string|null,expectedContentHash:string,acquisitionKey:string) =>
    api.post<{snapshot:NonNullable<FinanceCostSnapshotHistory['snapshot']>;replayed:boolean}>(`/finance/costs/${kind}/${ruleId}/snapshots`,
      {storeId,expectedContentHash,acquisitionKey}),
  getCostRuleSnapshot: (kind:FinanceCostRuleKind,ruleId:string,storeId:string|null,knowledgeAsOf?:string) =>
    api.get<FinanceCostSnapshotHistory>(`/finance/costs/${kind}/${ruleId}/snapshots`,{params:{storeId:storeId??undefined,knowledgeAsOf}}),
  // Dashboard
  getDashboard: (params?: { dateFrom?: string; dateTo?: string; storeIds?: string; completedOnly?: boolean }) =>
    api.get('/finance/dashboard', { params }),

  getTopProducts: (params?: { dateFrom?: string; dateTo?: string; limit?: number; storeIds?: string; completedOnly?: boolean }) =>
    api.get('/finance/top-products', { params }),

  // Shipping methods (for cashflow filters)
  getShippingMethods: (storeId?: string) =>
    api.get<Array<{ value: string; label: string }>>('/finance/shipping-methods', { params: storeId ? { storeId } : {} }),

  // Cashflow
  getCashflow: (params?: { dateFrom?: string; dateTo?: string; productIds?: string; storeIds?: string; completedOnly?: boolean; shippingMethods?: string; shipmentDateFrom?: string; shipmentDateTo?: string }) =>
    api.get('/finance/cashflow', { params }),

  // Cashflow single-day channel breakdown (expandable day rows, lazy per day)
  getCashflowChannelDay: (params: { date: string; storeIds?: string; completedOnly?: boolean; shippingMethods?: string; shipmentDateFrom?: string; shipmentDateTo?: string }) =>
    api.get<{ date: string; channels: Array<{ channel: string; day: unknown | null }> }>('/finance/cashflow/channel-day', { params }),

  // Ad date range for smart "Maksymalnie"
  getAdDateRange: (params?: { productIds?: string }) => api.get('/finance/ad-date-range', { params }),

  // Config (taxes page)
  getConfig: (params?: { storeId?: string }) => api.get('/finance/config', { params }),
  updateConfig: (data: Record<string, unknown>) => api.put('/finance/config', data),
  getStoreConfigs: () => api.get('/finance/config/stores'),
  updateStoreConfig: (storeId: string, data: Record<string, unknown>) => api.put(`/finance/config/stores/${storeId}`, data),
  getZusPreview: (params?: Record<string, string>) => api.get('/finance/zus-preview', { params }),

  // Rules
  getRules: () => api.get('/finance/rules'),
  updateRules: (overrides: Record<string, unknown>) => api.put('/finance/rules', { overrides }),
  resetRules: () => api.delete('/finance/rules'),

  // Product costs
  getProductCosts: (params?: { page?: number; limit?: number; search?: string; storeId?: string }) =>
    api.get('/finance/costs/products', { params }),
  saveProductCost: (data: { id?: string; storeId?: string | null; externalProductId: string; productName: string; sellingPrice: number; purchasePrice: number; sku?: string }) =>
    api.post('/finance/costs/products', data),
  bulkSaveProductCosts: (products: Array<{ id?: string; storeId?: string | null; externalProductId: string; productName: string; sellingPrice: number; purchasePrice: number; sku?: string }>) =>
    api.put('/finance/costs/products/bulk', { products }),

  // Shipping costs
  getShippingCosts: (params?: { storeId?: string }) => api.get('/finance/costs/shipping', { params }),
  saveShippingCosts: (methods: Array<{ shippingMethodKey: string; shippingMethodName: string; costNet: number; returnCostNet?: number | null; isEnabled?: boolean }>, storeId?: string) =>
    api.put('/finance/costs/shipping', { methods, storeId }),
  // Progi wagowe kosztu wysyłki
  getShippingCostTiers: (shippingCostId: string) =>
    api.get(`/finance/costs/shipping/${shippingCostId}/tiers`),
  saveShippingCostTiers: (shippingCostId: string, tiers: Array<{ weightFromKg: number; weightToKg: number | null; costNet: number }>) =>
    api.put(`/finance/costs/shipping/${shippingCostId}/tiers`, { tiers }),
  getShippingCostTierHistory: (shippingCostId: string) =>
    api.get(`/finance/costs/shipping/${shippingCostId}/tiers/history`),
  addShippingCostTierHistory: (shippingCostId: string, data: { tiers: Array<{ weightFromKg: number; weightToKg: number | null; costNet: number }>; validFrom: string; validTo?: string | null }) =>
    api.post(`/finance/costs/shipping/${shippingCostId}/tiers/history`, data),
  deleteShippingCostTierHistory: (historyId: string) =>
    api.delete(`/finance/costs/shipping/tiers/history/${historyId}`),

  // Transaction fees
  getTransactionFees: (params?: { storeId?: string }) => api.get('/finance/costs/fees', { params }),
  createTransactionFee: (data: { name?: string; paymentMethod?: string | null; percentFee: number; fixedFee: number; chargebackFee?: number; storeId?: string }) =>
    api.post('/finance/costs/fees', data),
  updateTransactionFee: (id: string, data: Partial<{ name: string; paymentMethod: string | null; percentFee: number; fixedFee: number; chargebackFee: number; isActive: boolean }>) =>
    api.patch(`/finance/costs/fees/${id}`, data),
  deleteTransactionFee: (id: string) => api.delete(`/finance/costs/fees/${id}`),

  // Fixed costs
  getFixedCosts: (params?: { storeId?: string }) => api.get('/finance/costs/fixed', { params }),
  createFixedCost: (data: { name: string; category?: string; amountNet: number; storeId?: string }) =>
    api.post('/finance/costs/fixed', data),
  updateFixedCost: (id: string, data: Partial<{ name: string; category: string; amountNet: number; isActive: boolean }>) =>
    api.patch(`/finance/costs/fixed/${id}`, data),
  deleteFixedCost: (id: string) => api.delete(`/finance/costs/fixed/${id}`),

  // Cost history (date-range pricing)
  getProductCostHistory: (productCostId: string) =>
    api.get(`/finance/costs/products/${productCostId}/history`),
  addProductCostHistory: (productCostId: string, data: { purchasePrice: number; validFrom: string; validTo?: string | null }) =>
    api.post(`/finance/costs/products/${productCostId}/history`, data),
  deleteProductCostHistory: (historyId: string) =>
    api.delete(`/finance/costs/products/history/${historyId}`),

  getShippingCostHistory: (shippingCostId: string) =>
    api.get(`/finance/costs/shipping/${shippingCostId}/history`),
  addShippingCostHistory: (shippingCostId: string, data: { costNet: number; validFrom: string; validTo?: string | null }) =>
    api.post(`/finance/costs/shipping/${shippingCostId}/history`, data),
  deleteShippingCostHistory: (historyId: string) =>
    api.delete(`/finance/costs/shipping/history/${historyId}`),

  getTransactionFeeHistory: (feeId: string) =>
    api.get(`/finance/costs/fees/${feeId}/history`),
  addTransactionFeeHistory: (feeId: string, data: { percentFee: number; fixedFee: number; chargebackFee?: number; validFrom: string; validTo?: string | null }) =>
    api.post(`/finance/costs/fees/${feeId}/history`, data),
  deleteTransactionFeeHistory: (historyId: string) =>
    api.delete(`/finance/costs/fees/history/${historyId}`),

  getFixedCostHistory: (fixedCostId: string) =>
    api.get(`/finance/costs/fixed/${fixedCostId}/history`),
  addFixedCostHistory: (fixedCostId: string, data: { amountNet: number; validFrom: string; validTo?: string | null }) =>
    api.post(`/finance/costs/fixed/${fixedCostId}/history`, data),
  deleteFixedCostHistory: (historyId: string) =>
    api.delete(`/finance/costs/fixed/history/${historyId}`),

  // Calculator
  calculate: (data: { price: number; cost: number; shipping: number; feePercent: number; feeFixed: number; other: number }) =>
    api.post('/finance/calculator', data),
  getCalculatorProducts: () => api.get('/finance/calculator/products'),

  // Facebook
  getFacebookConfig: () => api.get<{ appId: string | null; configured: boolean }>('/finance/facebook/config'),
  getFacebookAccounts: () => api.get('/finance/facebook/accounts'),
  getFacebookSyncLogs: () => api.get('/finance/facebook/sync-logs'),
  previewFacebookAccounts: (data: { code?: string; accessToken?: string; redirectUri?: string }) =>
    api.post('/finance/facebook/preview-accounts', data),
  connectFacebook: (data: { connectionId?: string; selectedAccountIds?: string[]; code?: string; accessToken?: string; redirectUri?: string }) =>
    api.post('/finance/facebook/connect', data),
  updateFacebookAccount: (id: string, data: { storeId?: string | null }) =>
    api.patch(`/finance/facebook/accounts/${id}`, data),
  disconnectFacebook: (id: string) => api.delete(`/finance/facebook/accounts/${id}`),
  getFacebookCampaigns: (accountId: string) => api.get(`/finance/facebook/accounts/${accountId}/campaigns`),
  syncFacebookComments: () => api.post('/finance/facebook/sync-comments'),
  syncFacebookAds: () => api.post<{ message: string; lastSyncAt: string | null }>('/finance/facebook/sync'),
  getFacebookSyncStatus: () => api.get<{ lastSyncAt: string | null; canSync: boolean; cooldownRemainingSec: number }>('/finance/facebook/sync-status'),

  // Ad mappings
  getAdMappings: (storeId?: string) => api.get('/finance/costs/ad-mappings', { params: { storeId } }),
  saveAdMapping: (data: { storeId?: string; externalProductId: string; accountId: string; useWholeAccount?: boolean; campaignIds?: string[] }) =>
    api.post('/finance/costs/ad-mappings', data),
  bulkSaveAdMappings: (accountId: string, productIds: string[], storeId?: string) =>
    api.post('/finance/costs/ad-mappings/bulk', { accountId, productIds, storeId }),
  deleteAdMapping: (id: string) => api.delete(`/finance/costs/ad-mappings/${id}`),

  // Finance settings (per-store completed status mapping + snapshot + VAT threshold)
  getSettings: () => api.get('/finance/settings'),
  updateSettings: (data: Record<string, unknown>) => api.put('/finance/settings', data),

  // Cashflow snapshots
  freezeCashflow: (date: string) => api.post('/finance/cashflow/freeze', { date }),
  freezeCashflowRange: (dateFrom: string, dateTo: string) => api.post('/finance/cashflow/freeze-range', { dateFrom, dateTo }),
  unfreezeCashflow: (date: string) => api.delete(`/finance/cashflow/freeze/${date}`),
  getCashflowSnapshots: () => api.get('/finance/cashflow/snapshots'),

  // VAT threshold
  getVatThreshold: () => api.get('/finance/vat-threshold'),

  // Pozyskiwanie dowodów finansowych (kolektor) - samoobsługowe włączanie per sklep
  getCollectorStatus: () => api.get<FinanceCollectorStatus>('/finance/collector/status'),
  enableCollector: (storeId: string) => api.post<FinanceCollectorEnableResult>(`/finance/collector/stores/${storeId}/enable`),
  disableCollector: (storeId: string) => api.post<{ revoked: number }>(`/finance/collector/stores/${storeId}/disable`),
};

export interface FinanceCollectorGrantInfo {
  id: string;
  sourceKinds: string[];
  issuedAt: string;
  expiresAt: string;
}
export interface FinanceCollectorStatus {
  masterEnabled: boolean;
  stores: Array<{ storeId: string; name: string; grant: FinanceCollectorGrantInfo | null; active: boolean }>;
}
export interface FinanceCollectorEnableResult {
  grant: FinanceCollectorGrantInfo & { storeId: string | null };
  renewed: boolean;
}
export interface SuperAdminCollectorOverview {
  enabled: boolean;
  runtime: { state: string; lastTickAt: string | null; lastOutcome: string } | null;
  policy: { intervalMs: number; tenantItemsPerDay: number; globalItemsPerDay: number };
}

// Super Admin API
export const superAdminApi = {
  getTaxSettings: () => api.get('/super-admin/tax-settings'),
  getTaxSettingsForYear: (year: number) => api.get(`/super-admin/tax-settings/${year}`),
  updateTaxSettings: (year: number, data: Record<string, unknown>) => api.put(`/super-admin/tax-settings/${year}`, data),
  cloneTaxSettings: (year: number, targetYear: number) => api.post(`/super-admin/tax-settings/${year}/clone`, { targetYear }),
  activateTaxSettings: (year: number) => api.post(`/super-admin/tax-settings/${year}/activate`),
  getChangelog: (year: number) => api.get(`/super-admin/tax-settings/${year}/changelog`),
  seedTaxSettings: () => api.post('/super-admin/tax-settings-seed'),

  // System Assets — cross-tenant graphics
  listSystemAssets: () => api.get<SystemAsset[]>('/super-admin/system-assets'),
  uploadSystemAsset: (file: File, displayName?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (displayName) fd.append('displayName', displayName);
    return api.post<SystemAsset>('/super-admin/system-assets', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSystemAsset: (id: string) => api.delete(`/super-admin/system-assets/${id}`),

  // Finance collector - globalny kill switch pozyskiwania dowodów
  getFinanceCollector: () => api.get<SuperAdminCollectorOverview>('/super-admin/finance-collector'),
  updateFinanceCollector: (enabled: boolean) => api.put<SuperAdminCollectorOverview>('/super-admin/finance-collector', { enabled }),

  // Page Templates — system templates manager
  listAllPageTemplates: () => api.get<PageTemplateAdminRow[]>('/super-admin/page-templates'),
  updatePageTemplate: (id: string, data: Partial<{ name: string; category: string; scope: 'PAGE' | 'SECTION' | 'ROW'; thumbnail: string | null; displayOrder: number }>) =>
    api.patch<PageTemplateAdminRow>(`/super-admin/page-templates/${id}`, data),
  deletePageTemplate: (id: string) => api.delete(`/super-admin/page-templates/${id}`),
  clearAllSystemPageTemplates: () => api.post<{ deleted: number }>('/super-admin/page-templates/clear-all'),
  reorderSystemPageTemplates: (items: Array<{ id: string; displayOrder: number }>) =>
    api.post<{ reordered: number }>('/super-admin/page-templates/reorder', { items }),
  promotePageTemplate: (id: string) => api.post<PageTemplateAdminRow>(`/super-admin/page-templates/${id}/promote`),
  importPageTemplate: (data: { name: string; scope: 'PAGE' | 'SECTION' | 'ROW'; category?: string; content: unknown[]; thumbnail?: string | null }) =>
    api.post<PageTemplateAdminRow>('/super-admin/page-templates/import', data),

  // Kreator defaults — system-scoped settings dla każdego nowego sklepu
  // i dla "Przywróć domyślne" w panelu sklepu.
  getKreatorDefaults: () => api.get('/super-admin/kreator-defaults'),
  updateKreatorDefaultsSection: (section: string, data: unknown) =>
    api.put(`/super-admin/kreator-defaults/${section}`, data),
  getFooterContentSummary: () =>
    api.get<{ blocks: number }>('/super-admin/kreator-defaults/footer-content/summary'),
  uploadFooterContent: (content: unknown[]) =>
    api.post<{ blocks: number }>('/super-admin/kreator-defaults/footer-content', content),
  getBundleDefaults: () =>
    api.get<{ settings: Record<string, unknown> }>('/super-admin/bundle-defaults'),
  updateBundleDefaults: (settings: Record<string, unknown>) =>
    api.put<{ settings: Record<string, unknown> }>('/super-admin/bundle-defaults', { settings }),
};

export interface PageTemplateAdminRow {
  id: string;
  tenantId: string | null;
  name: string;
  category: string;
  scope: 'PAGE' | 'SECTION' | 'ROW';
  thumbnail: string | null;
  isSystem: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  tenant: { id: string; name: string } | null;
}

export interface SystemAsset {
  id: string;
  filename: string;
  originalName: string;
  displayName: string | null;
  mimeType: string;
  size: number;
  uploadedBy: string | null;
  createdAt: string;
}

export const automationApi = {
  // Get all automations
  getAll: () =>
    api.get<{ automations: Automation[] }>('/automations'),

  // Get single automation
  getOne: (id: string) =>
    api.get<Automation>(`/automations/${id}`),

  // Create automation
  create: (data: {
    name: string;
    description?: string;
    // Support both single trigger (legacy) and array of triggers (new - OR logic)
    trigger: { type: TriggerType; config: Record<string, unknown> } | Array<{ type: TriggerType; config: Record<string, unknown> }>;
    conditions?: ConditionItemType[];
    actions: Array<{ type: ActionType; config: Record<string, unknown>; continueOnError?: boolean }>;
    isActive?: boolean;
    priority?: number;
    triggerMode?: TriggerMode;
    buttonIcon?: string | null;
    buttonText?: string | null;
  }) =>
    api.post<{ success: boolean; message: string; automation: Automation }>('/automations', data),

  // Update automation
  update: (id: string, data: Partial<{
    name: string;
    description?: string;
    // Support both single trigger (legacy) and array of triggers (new - OR logic)
    trigger: { type: TriggerType; config: Record<string, unknown> } | Array<{ type: TriggerType; config: Record<string, unknown> }>;
    conditions?: ConditionItemType[];
    actions: Array<{ type: ActionType; config: Record<string, unknown>; continueOnError?: boolean }>;
    isActive?: boolean;
    priority?: number;
    triggerMode?: TriggerMode;
    buttonIcon?: string | null;
    buttonText?: string | null;
  }>) =>
    api.put<{ success: boolean; message: string; automation: Automation }>(`/automations/${id}`, data),

  // Delete automation
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/automations/${id}`),

  // Toggle automation active state
  toggle: (id: string) =>
    api.put<{ success: boolean; message: string; automation: Automation }>(`/automations/${id}/toggle`),

  // Clear automation error
  clearError: (id: string) =>
    api.delete<{ success: boolean; message: string; automation: Automation }>(`/automations/${id}/error`),

  // Duplicate automation
  duplicate: (id: string) =>
    api.post<{ success: boolean; message: string; automation: Automation }>(`/automations/${id}/duplicate`),

  // Test automation on an order (dry run)
  test: (id: string, orderId: string) =>
    api.post<{ success: boolean; result: Record<string, unknown> }>(`/automations/${id}/test`, { orderId }),

  // Run automation on an order
  run: (id: string, orderId: string) =>
    api.post<{ success: boolean; message: string; result: Record<string, unknown> }>(`/automations/${id}/run`, { orderId }),

  // Get logs for specific automation
  getLogs: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<{
      logs: AutomationLog[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/automations/${id}/logs`, { params }),

  // Get all logs
  getAllLogs: (params?: { page?: number; limit?: number; status?: string; automationId?: string }) =>
    api.get<{
      logs: AutomationLog[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>('/automations/logs/all', { params }),

  // Get log details
  getLog: (logId: string) =>
    api.get<AutomationLog>(`/automations/logs/${logId}`),

  // Get metadata
  getTriggers: () =>
    api.get<{ triggers: TriggerMeta[] }>('/automations/meta/triggers'),

  getActions: () =>
    api.get<{ actions: ActionMeta[] }>('/automations/meta/actions'),

  getConditions: () =>
    api.get<{ conditions: ConditionMeta[] }>('/automations/meta/conditions'),

  getInpostStatuses: () =>
    api.get<{ statuses: Array<{ value: string; label: string }> }>('/automations/meta/inpost-statuses'),

  getValidationErrors: () =>
    api.get<{ errors: Array<{ value: string; label: string }> }>('/automations/meta/validation-errors'),

  getOrderFields: () =>
    api.get<{
      paymentMethods: string[];
      shippingMethods: string[];
      tags: string[];
      paymentMethodsByStore: Array<{ storeId: string | null; storeName: string; methods: string[] }>;
      shippingMethodsByStore: Array<{ storeId: string | null; storeName: string; methods: string[] }>;
      stores: Array<{ id: string; name: string }>;
    }>('/automations/meta/order-fields'),

  getButtonIcons: () =>
    api.get<{ icons: string[] }>('/automations/meta/button-icons'),

  // Manual automations (for custom actions in order details)
  getManualAutomations: (storeId?: string) =>
    api.get<{ automations: ManualAutomation[] }>('/automations/manual', { params: storeId ? { storeId } : undefined }),

  executeManualAutomation: (automationId: string, orderId: string) =>
    api.post<{ success: boolean; message: string; result: Record<string, unknown> }>(`/automations/${automationId}/execute`, { orderId }),

  // Groups
  getGroups: () =>
    api.get<{ groups: AutomationGroup[] }>('/automations/groups/list'),

  createGroup: (data: { name: string; color?: string }) =>
    api.post<{ success: boolean; message: string; group: AutomationGroup }>('/automations/groups', data),

  updateGroup: (id: string, data: Partial<{ name: string; color: string; isExpanded: boolean }>) =>
    api.put<{ success: boolean; message: string; group: AutomationGroup }>(`/automations/groups/${id}`, data),

  deleteGroup: (id: string, deleteAutomations: boolean = false) =>
    api.delete<{ success: boolean; message: string }>(`/automations/groups/${id}?deleteAutomations=${deleteAutomations}`),

  duplicateGroup: (id: string) =>
    api.post<{ success: boolean; message: string; group: AutomationGroup; automations: Automation[] }>(`/automations/groups/${id}/duplicate`),

  // Reordering
  reorder: (items: Array<{ id: string; position: number; groupId: string | null }>) =>
    api.post<{ success: boolean; message: string }>('/automations/reorder', { items }),

  reorderGroups: (items: Array<{ id: string; position: number }>) =>
    api.post<{ success: boolean; message: string }>('/automations/groups/reorder', { items }),

  // Export/Import
  exportAll: () =>
    api.get<{
      version: string;
      exportedAt: string;
      groups: Array<{ name: string; color: string; position: number }>;
      automations: Array<{
        name: string;
        description: string | null;
        trigger: unknown;
        conditions: unknown;
        actions: unknown[];
        isActive: boolean;
        priority: number;
        position: number;
        groupName: string | null;
      }>;
    }>('/automations/export/all'),

  importAll: (data: unknown) =>
    api.post<{
      success: boolean;
      message: string;
      createdGroups: number;
      createdAutomations: number;
      note: string;
    }>('/automations/import', data),
};

// ============================================================================
// PAYMENT API
// ============================================================================

export interface Przelewy24Config {
  id?: string;
  configured: boolean;
  isActive: boolean;
  sandbox: boolean;
  merchantId: string;
  hasCrcKey: boolean;
  hasApiKey: boolean;
  name?: string | null;
  storeId?: string | null;
  store?: {
    id: string;
    name: string;
    url?: string;
  } | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface RefundLimitsConfig {
  configured: boolean;
  hasPassword: boolean;
  limits: {
    enabled: boolean;
    maxDailyCount: number | null;
    maxDailyAmount: number | null;
  };
}

export interface RefundStats {
  today: {
    count: number;
    amount: number;
  };
  limits: {
    maxDailyCount: number | null;
    maxDailyAmount: number | null;
    remainingCount: number | null;
    remainingAmount: number | null;
  } | null;
}

export interface RefundInfo {
  orderId: string;
  orderNumber: string;
  paymentMethod: string;
  gateway: 'PRZELEWY24' | 'PAYU' | 'STRIPE' | 'OTHER' | null;
  transactionData: {
    orderId: number;
    sessionId: string;
    transactionId?: string;
  } | null;
  canRefund: boolean;
  integrationConfigured: boolean;
  integrationSource: 'store' | 'global' | null;
  integrationMerchantId: string | null;
  totalAmount: number;
  currency: string;
  refundedAmount: number;
  availableForRefund: number;
  observedRefundsGross?: number;
  refunds: Array<{
    id: string;
    amount: number;
    status: string;
    description: string;
    isChargeback?: boolean;
    source?: string;
    createdAt: string;
    processedAt: string | null;
    errorMessage: string | null;
  }>;
  productName: string | null;
  storeName: string | null;
}

export interface RefundResult {
  success: boolean;
  refund: {
    id: string;
    amount: number;
    status: string;
    description: string;
    processedAt: string | null;
  };
  message: string;
}

export const paymentApi = {
  // Get all payment integrations
  getAll: () =>
    api.get<Array<{
      id: string;
      type: string;
      isActive: boolean;
      settings: Record<string, unknown>;
      hasCredentials: boolean;
    }>>('/payments'),

  // Przelewy24 - Multi-account support
  getPrzelewy24List: () =>
    api.get<{ configs: Przelewy24Config[] }>('/payments/przelewy24/list'),

  getPrzelewy24ById: (id: string) =>
    api.get<Przelewy24Config>(`/payments/przelewy24/${id}`),

  getPrzelewy24: () =>
    api.get<Przelewy24Config>('/payments/przelewy24'),

  savePrzelewy24: (data: {
    id?: string; // Optional: ID for updating existing config
    merchantId: string;
    apiKey: string;
    crcKey: string;
    sandbox: boolean;
    storeId?: string | null;
    name?: string | null;
  }) =>
    api.post<{
      success: boolean;
      message: string;
      integration: {
        id: string;
        name: string | null;
        type: string;
        storeId: string | null;
        store: { id: string; name: string } | null;
        isActive: boolean;
      };
    }>('/payments/przelewy24', data),

  testPrzelewy24: (data: {
    id?: string;
    merchantId: string;
    apiKey: string;
    crcKey: string;
    sandbox: boolean;
    storeId?: string;
  }) =>
    api.post<{ success: boolean; message?: string; error?: string }>('/payments/przelewy24/test', data),

  togglePrzelewy24: () =>
    api.patch<{ success: boolean; isActive: boolean; message: string }>('/payments/przelewy24/toggle'),

  togglePrzelewy24ById: (id: string) =>
    api.patch<{ success: boolean; isActive: boolean; message: string }>(`/payments/przelewy24/${id}/toggle`),

  deletePrzelewy24: () =>
    api.delete<{ success: boolean; message: string }>('/payments/przelewy24'),

  deletePrzelewy24ById: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/payments/przelewy24/${id}`),

  // Refund Limits
  getRefundLimits: () =>
    api.get<RefundLimitsConfig>('/payments/przelewy24/refund-limits'),

  saveRefundLimits: (data: {
    enabled: boolean;
    maxDailyCount: number | null;
    maxDailyAmount: number | null;
    currentPassword?: string;
  }) =>
    api.post<{ success: boolean; message: string; limits: RefundLimitsConfig['limits'] }>(
      '/payments/przelewy24/refund-limits',
      data
    ),

  getRefundStats: () =>
    api.get<RefundStats>('/payments/przelewy24/refund-stats'),

  verifyRefundLimitsPassword: (password: string) =>
    api.post<{ valid: boolean; error?: string; message?: string }>(
      '/payments/przelewy24/refund-limits/verify-password',
      { password }
    ),

  setRefundLimitsPassword: (data: { currentPassword?: string; newPassword: string }) =>
    api.post<{ success: boolean; message: string }>(
      '/payments/przelewy24/refund-limits/set-password',
      data
    ),

  removeRefundLimitsPassword: (password: string) =>
    api.delete<{ success: boolean; message: string }>(
      '/payments/przelewy24/refund-limits/password',
      { data: { password } }
    ),
};

// ============================================================================
// REFUND API (on orders)
// ============================================================================

export const refundApi = {
  // Get refund info for order
  getInfo: (orderId: string) =>
    api.get<RefundInfo>(`/orders/${orderId}/refund-info`),

  // Create refund
  create: (orderId: string, data: { amount: number; description?: string }) =>
    api.post<RefundResult>(`/orders/${orderId}/refund`, data),

  // Mark or unmark a refund as chargeback (rzadkie zdarzenie, brak API dispute w bramkach)
  markChargeback: (orderId: string, refundId: string, isChargeback: boolean) =>
    api.patch<{ success: boolean; refundId: string; isChargeback: boolean }>(
      `/orders/${orderId}/refunds/${refundId}/chargeback`,
      { isChargeback },
    ),

  // Get refunds history for order
  getHistory: (orderId: string) =>
    api.get<{
      orderId: string;
      refunds: Array<{
        id: string;
        amount: number;
        currency: string;
        status: string;
        gateway: string;
        description: string;
        errorMessage: string | null;
        createdAt: string;
        processedAt: string | null;
      }>;
    }>(`/orders/${orderId}/refunds`),
};

// ============================================================================
// COMPLAINTS API (admin)
// ============================================================================

export interface AdminComplaint {
  id: string;
  tenantId: string;
  orderId: string;
  storeId: string;
  type: string;
  reason: string;
  items: Array<{ name: string; quantity: number; sku?: string }>;
  metadata: Record<string, unknown>;
  attachments: Array<{ filename: string; originalName: string; url: string; size: number; mimeType: string }>;
  bankAccountNumber: string | null;
  status: string;
  customerEmail: string;
  customerName: string | null;
  customerPhone: string | null;
  adminNotes: string | null;
  resolvedAt: string | null;
  resolvedById: string | null;
  createdAt: string;
  updatedAt: string;
  order: {
    id?: string;
    orderNumber: string;
    totalAmount: number | string;
    currency: string;
    storeId: string;
    status?: string;
    externalStatus?: string | null;
    externalCreatedAt?: string;
    orderStatus?: { name: string; color: string } | null;
    shipments?: Array<{ id: string; trackingNumber: string | null; carrier: string; status: string; inpostStatus: string | null; deliveredAt: string | null; statusChangedAt: string | null; createdAt: string }>;
    history?: Array<Pick<OrderHistoryEntry, 'id' | 'action' | 'description' | 'createdAt'>>;
    paymentMethod: string | null;
    customerName?: string | null;
    customerEmail?: string | null;
    customerPhone?: string | null;
    items?: unknown;
    store: { name: string };
  };
}

export const complaintsApi = {
  getTracking: (complaintId: string, shipmentId: string) =>
    api.get<{ status: string; events: Array<{ status: string; date: string }> }>(`/portal/complaints/${complaintId}/shipments/${shipmentId}/tracking`),
  getAll: (params?: { page?: number; limit?: number; status?: string; type?: string; search?: string }) =>
    api.get<{ complaints: AdminComplaint[]; pagination: { page: number; limit: number; total: number; pages: number } }>('/portal/complaints/all', { params }),

  getOne: (id: string) =>
    api.get<AdminComplaint>(`/portal/complaints/${id}/detail`),

  updateStatus: (id: string, data: { status?: string; adminNotes?: string }) =>
    api.patch(`/portal/complaints/${id}`, data),
};

// ============================================================================
// WAREHOUSE API
// ============================================================================

export interface Product {
  id: string;
  name: string;
  sku: string | null;
  description: string | null;
  purchasePrice: number | null;
  sellingPrice: number | null;
  trackStock: boolean;
  quantity: number;
  minQuantity: number | null;
  storeId: string | null;
  parentProductId: string | null;
  parentProduct?: { id: string; name: string; sku: string | null; quantity: number; trackStock?: boolean } | null;
  childProducts?: { id: string }[];
  woocommerceProductId: string | null;
  syncStatus: 'NOT_SYNCED' | 'SYNCED' | 'PENDING' | 'ERROR';
  lastSyncAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ParentProduct {
  id: string;
  name: string;
  sku: string | null;
  quantity: number;
  trackStock: boolean;
  woocommerceProductId: string | null;
  childProducts: { id: string }[];
}

export interface StockMovement {
  id: string;
  tenantId: string;
  productId: string;
  orderId: string | null;
  quantity: number;
  type: 'INCREASE' | 'DECREASE' | 'ADJUSTMENT' | 'SYNC';
  reason: string | null;
  notes: string | null;
  previousQuantity: number;
  newQuantity: number;
  createdById: string | null;
  createdAt: string;
}

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    totalProducts: number;
    lowStockCount: number;
  };
}

export interface ProductFilters {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: 'true' | 'false' | 'all';
  lowStock?: 'true' | 'false';
  storeId?: string;
  sortBy?: 'name' | 'sku' | 'quantity' | 'sellingPrice' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateProductRequest {
  name: string;
  sku?: string | null;
  description?: string | null;
  purchasePrice?: number | null;
  sellingPrice?: number | null;
  quantity?: number;
  minQuantity?: number | null;
  trackStock?: boolean;
  storeId?: string | null;
  woocommerceProductId?: string | null;
  isActive?: boolean;
  parentProductId?: string | null;
}

export interface UpdateProductRequest {
  name?: string;
  sku?: string | null;
  description?: string | null;
  purchasePrice?: number | null;
  sellingPrice?: number | null;
  minQuantity?: number | null;
  trackStock?: boolean;
  storeId?: string | null;
  woocommerceProductId?: string | null;
  isActive?: boolean;
  parentProductId?: string | null;
}

export interface AdjustStockRequest {
  quantity: number;
  reason?: string;
  notes?: string;
}

export interface WarehouseStats {
  totalProducts: number;
  activeProducts: number;
  lowStockCount: number;
  totalItems: number;
}

export interface ImportProductData {
  name: string;
  sku?: string | null;
  description?: string | null;
  purchasePrice?: number | string | null;
  sellingPrice?: number | string | null;
  quantity?: number | string;
  minQuantity?: number | string | null;
}

export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  errors: Array<{ row: number; error: string }>;
  message: string;
}

export interface WarehouseReport {
  totalProducts: number;
  totalItems: number;
  lowStockCount: number;
  outOfStockCount: number;
  totalStockValue: number;
  totalSellingValue: number;
  lowStockProducts: Array<{
    id: string;
    name: string;
    sku: string | null;
    quantity: number;
    minQuantity: number | null;
  }>;
  outOfStockProducts: Array<{
    id: string;
    name: string;
    sku: string | null;
  }>;
  movementsLast30Days: {
    increases: number;
    decreases: number;
    adjustments: number;
  };
}

export interface WarehouseAlert {
  id: string;
  name: string;
  sku: string | null;
  quantity: number;
  minQuantity: number | null;
  severity: 'critical' | 'warning';
}

export interface WarehouseAlerts {
  alerts: WarehouseAlert[];
  totalAlerts: number;
  criticalCount: number;
  warningCount: number;
}

export const warehouseApi = {
  // Products CRUD
  getProducts: (filters?: ProductFilters) =>
    api.get<ProductsResponse>('/warehouse/products', { params: filters }),

  getProduct: (id: string) =>
    api.get<Product & { stockMovements: StockMovement[] }>(`/warehouse/products/${id}`),

  getParentProducts: (excludeId?: string, storeId?: string | null) =>
    api.get<ParentProduct[]>('/warehouse/parent-products', {
      params: {
        ...(excludeId ? { excludeId } : {}),
        ...(storeId === null ? { storeId: 'none' } : storeId ? { storeId } : {}),
      },
    }),

  createProduct: (data: CreateProductRequest) =>
    api.post<Product>('/warehouse/products', data),

  updateProduct: (id: string, data: UpdateProductRequest) =>
    api.patch<Product>(`/warehouse/products/${id}`, data),

  deleteProduct: (id: string) =>
    api.delete(`/warehouse/products/${id}`),

  // Stock management
  adjustStock: (id: string, data: AdjustStockRequest) =>
    api.post<Product>(`/warehouse/products/${id}/adjust`, data),

  getStockMovements: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<{
      movements: StockMovement[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/warehouse/products/${id}/movements`, { params }),

  // Stats
  getStats: () =>
    api.get<WarehouseStats>('/warehouse/stats'),

  // Import/Export
  exportProducts: () =>
    api.get('/warehouse/export', { responseType: 'blob' }),

  importProducts: (products: ImportProductData[], updateExisting?: boolean) =>
    api.post<ImportResult>('/warehouse/import', { products, updateExisting }),

  // Reports
  getReport: (type: 'overview' | 'movements' = 'overview', params?: { dateFrom?: string; dateTo?: string; storeId?: string }) =>
    api.get<WarehouseReport>('/warehouse/reports', { params: { type, ...params } }),

  // Alerts
  getAlerts: (params?: { storeId?: string }) =>
    api.get<WarehouseAlerts>('/warehouse/alerts', { params }),

  // Sync products from WooCommerce
  syncProductsFromWooCommerce: (storeId?: string) =>
    api.post<{
      success: boolean;
      created: number;
      updated: number;
      skipped: number;
      errors: Array<{ store: string; error: string }>;
      message: string;
    }>('/warehouse/sync-products', { storeId }),
};

// ─── User Management API ──────────────────────────────────────
export interface TenantUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  permissions: string[];
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export type InboxSwipeAction = 'none' | 'resolved' | 'pending' | 'important' | 'drafts' | 'archive' | 'trash';

export interface UserPreferences {
  ordersPerPage?: number;
  shipmentsPerPage?: number;
  inboxSwipeEnabled?: boolean;
  inboxSwipeLeftAction?: InboxSwipeAction;
  inboxSwipeRightAction?: InboxSwipeAction;
}

export const userApi = {
  list: () =>
    api.get<TenantUser[]>('/users'),

  get: (id: string) =>
    api.get<TenantUser>(`/users/${id}`),

  create: (data: { email: string; password: string; firstName?: string; lastName?: string; role?: string }) =>
    api.post<TenantUser>('/users', data),

  update: (id: string, data: { firstName?: string; lastName?: string; role?: string; isActive?: boolean; permissions?: string[] }) =>
    api.patch<TenantUser>(`/users/${id}`, data),

  delete: (id: string) =>
    api.delete(`/users/${id}`),

  getPreferences: () =>
    api.get<UserPreferences>('/users/me/preferences'),

  updatePreferences: (data: Partial<UserPreferences>) =>
    api.patch<UserPreferences>('/users/me/preferences', data),
};

// Resend (Ponowna wysyłka) API
export interface ResendRequestItem {
  id: string;
  token: string;
  status: string;
  expiresAt: string;
  amount: number;
  currency: string;
  customerEmail: string;
  shippingMethod: string | null;
  paidAt: string | null;
  resendOrderId: string | null;
  resendOrder?: { id: string; orderNumber: string } | null;
  paymentAttempts?: Array<{ id: string; createdAt: string; paidAt: string | null; p24OrderId: string | null; registrationFailedAt: string | null }>;
  createdAt: string;
  invalidatedAt: string | null;
  link: string | null;
}

export const resendApi = {
  createRequest: (orderId: string) =>
    api.post<{ id: string; token: string; link: string; expiresAt: string }>(`/resend/${orderId}/create`),

  getRequests: (orderId: string) =>
    api.get<ResendRequestItem[]>(`/resend/${orderId}/requests`),

  invalidateRequest: (requestId: string) =>
    api.post(`/resend/${requestId}/invalidate`),
};

// Portal (Panel klienta) API
export interface PortalTokenInfo {
  exists: boolean;
  id?: string;
  token?: string;
  isActive?: boolean;
  customerEmail?: string;
  createdAt?: string;
}

export interface PortalActivity {
  id: string;
  action: string;
  description: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  order?: { id: string; orderNumber: string };
}

// =============================================================================
// Admin Panel
// =============================================================================

export interface SystemEmailTemplate {
  id: string;
  name: string;
  slug: string;
  subject: string;
  bodyHtml: string;
  variables: string[];
  position: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SystemMessageTemplateCategory {
  id: string;
  name: string;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export const adminApi = {
  // System email templates
  getEmailTemplates: () =>
    api.get<SystemEmailTemplate[]>('/admin/email-templates'),

  getEmailTemplate: (id: string) =>
    api.get<SystemEmailTemplate>(`/admin/email-templates/${id}`),

  createEmailTemplate: (data: { name: string; slug: string; subject: string; bodyHtml: string; isActive?: boolean }) =>
    api.post<SystemEmailTemplate>('/admin/email-templates', data),

  updateEmailTemplate: (id: string, data: Partial<{ name: string; slug: string; subject: string; bodyHtml: string; isActive: boolean }>) =>
    api.put<SystemEmailTemplate>(`/admin/email-templates/${id}`, data),

  deleteEmailTemplate: (id: string) =>
    api.delete(`/admin/email-templates/${id}`),

  reorderEmailTemplates: (orderedIds: string[]) =>
    api.put<SystemEmailTemplate[]>('/admin/email-templates/reorder', { orderedIds }),

  seedEmailTemplates: () =>
    api.post<{ success: boolean; message: string; templates: SystemEmailTemplate[] }>('/admin/email-templates/seed'),

  // System message templates (conversation templates)
  getMessageTemplates: () =>
    api.get('/admin/message-templates'),

  getMessageTemplateCategories: () =>
    api.get<SystemMessageTemplateCategory[]>('/admin/message-template-categories'),

  createMessageTemplateCategory: (name: string) =>
    api.post<SystemMessageTemplateCategory>('/admin/message-template-categories', { name }),

  deleteMessageTemplateCategory: (id: string) =>
    api.delete(`/admin/message-template-categories/${id}`),

  createMessageTemplate: (data: { name: string; categoryId?: string | null; subject?: string; bodyHtml: string; bodyText?: string; isActive?: boolean }) =>
    api.post('/admin/message-templates', data),

  updateMessageTemplate: (id: string, data: Partial<{ name: string; categoryId: string | null; subject: string; bodyHtml: string; bodyText: string; isActive: boolean }>) =>
    api.put(`/admin/message-templates/${id}`, data),

  deleteMessageTemplate: (id: string) =>
    api.delete(`/admin/message-templates/${id}`),

  // RODO purge — cross-tenant, SUPER_ADMIN-only with re-auth
  inboxPurge: (data: { userEmail: string; password: string }) =>
    api.post<{
      success: boolean;
      tenant: { id: string; name: string };
      bodiesDeleted: number;
      snippetsCleared: number;
      logId: string;
    }>('/admin/inbox-purge', data),
};

// Customer blacklist API - N2 (2026-04-22).
export interface BlacklistEntry {
  id: string;
  identifier: string;
  identifierType: 'email' | 'phone' | 'address';
  displayName?: string | null;
  reason?: string | null;
  notes?: string | null;
  autoAdded: boolean;
  source?: string | null;
  relatedOrderId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const blacklistApi = {
  list: () => api.get<{ entries: BlacklistEntry[] }>('/blacklist'),

  add: (data: { identifier: string; identifierType: 'email' | 'phone' | 'address'; displayName?: string; reason?: string; notes?: string; orderId?: string }) =>
    api.post<{ entry: BlacklistEntry }>('/blacklist', data),

  remove: (id: string, orderId?: string) =>
    api.delete<{ success: boolean }>(`/blacklist/${id}${orderId ? `?orderId=${orderId}` : ''}`),

  check: (data: { email?: string; phone?: string; address?: string }) =>
    api.post<{ blacklisted: boolean; matches: BlacklistEntry[] }>('/blacklist/check', data),
};

export const portalApi = {
  generateToken: (orderId: string) =>
    api.post<{ token: string; link: string; isNew: boolean }>(`/portal/${orderId}/generate`),

  getToken: (orderId: string) =>
    api.get<PortalTokenInfo>(`/portal/${orderId}/token`),

  invalidateToken: (tokenId: string) =>
    api.post(`/portal/${tokenId}/invalidate`),

  reactivateToken: (tokenId: string) =>
    api.post(`/portal/${tokenId}/reactivate`),

  getActivities: (orderId: string) =>
    api.get<PortalActivity[]>(`/portal/${orderId}/activities`),

  getComplaints: (orderId: string) =>
    api.get<AdminComplaint[]>(`/portal/${orderId}/complaints`),

  updateComplaint: (complaintId: string, data: { status?: string; adminNotes?: string }) =>
    api.patch(`/portal/complaints/${complaintId}`, data),
};

// =============================================================================
// Messages (Wiadomości) API
// =============================================================================

export interface MessageTemplateCategoryDto {
  id: string;
  name: string;
  position: number;
  _count?: { templates: number };
}

export interface MissingTemplateVariableDto {
  key: string;
  label: string;
}

export const messagesApi = {
  getMailboxStores: () =>
    api.get<{ data: Array<{ id: string; name: string; messageInboxPosition: number; unreadCount: number; mailboxCount: number }>; showNavigationBadge?: boolean }>('/messages/stores'),

  reorderMailboxStores: (storeIds: string[]) =>
    api.put<{ data: Array<{ id: string; name: string; messageInboxPosition: number; unreadCount: number; mailboxCount: number }> }>('/messages/stores/reorder', { storeIds }),

  // `folder` to wirtualny identyfikator widoku: 'inbox' | 'sent' | 'drafts' | 'archive' | 'trash' | 'spam'
  // (mapowany po stronie backendu na InboxImapFolder.specialUse).
  // `imapFolderId` to konkretny folder IMAP z drzewa — ma pierwszeństwo nad `folder`.
  getMessages: (params?: { page?: number; limit?: number; source?: string; folder?: string; imapFolderId?: string; search?: string; isRead?: string; isStarred?: string; storeId?: string; conversationStatus?: string; aiCategory?: string; sortBy?: string; customerEmail?: string }) =>
    api.get('/messages', { params }),

  // Zwraca SAME ID pasujące do filtrów — użytkowane przez "Zaznacz wszystkie
  // w folderze" w UI. Ten sam zestaw filtrów co getMessages (backend używa
  // tego samego buildMessagesWhere helpera). Cap 50_000 — dla większych
  // folderów backend zwraca 409 TOO_MANY_IDS (Faza 1).
  getMessageIds: (params?: { source?: string; folder?: string; imapFolderId?: string; search?: string; isRead?: string; isStarred?: string; storeId?: string; conversationStatus?: string; aiCategory?: string; customerEmail?: string }) =>
    api.get<{ ids: string[]; total: number }>('/messages/ids', { params }),

  getStats: (params?: { storeId?: string; conversationStatus?: string }) =>
    api.get('/messages/stats', { params }),

  // Cheap count-by-customer (used by order card to show conversation count).
  // Backend robi Prisma.count z OR na from/to LIKE customerEmail — nic więcej.
  countByCustomer: (customerEmail: string) =>
    api.get<{ count: number }>('/messages/count', { params: { customerEmail } }),

  getMessage: (id: string, opts?: { peek?: boolean }) =>
    api.get(`/messages/${id}`, opts?.peek ? { params: { peek: 'true' } } : undefined),

  // Body z PostgreSQL (InboxMessageBody). 200 = mamy body (source='database' dla maili,
  // 'snippet' dla Facebook comments). 404 = brak rekordu (mail nie zbackfillowany lub zniknął).
  getMessageBody: (id: string) =>
    api.get<{
      bodyHtml: string | null;
      bodyText: string;
      attachmentsMeta: Array<{ partId: string | null; filename: string; mimeType: string; size?: number; contentId?: string | null }>;
      source: 'database' | 'snippet';
    }>(`/messages/${id}/body`),

  // URL do załącznika/inline image — stream z IMAP po metadata z DB.
  getMessagePartUrl: (id: string, partId: string, options?: { download?: boolean }) =>
    `/api/messages/${id}/attachments/${encodeURIComponent(partId)}${options?.download ? '?download=1' : ''}`,

  getMessagePartBlob: (id: string, partId: string) =>
    api.get<Blob>(`/messages/${id}/attachments/${encodeURIComponent(partId)}`, { responseType: 'blob' }),

  createMessage: (data: { source?: string; to?: string; cc?: string; bcc?: string; subject?: string; bodyText?: string; bodyHtml?: string; isDraft?: boolean; parentId?: string; storeId?: string; attachments?: Array<{ storedAs: string; filename: string; mimeType?: string }> }) =>
    api.post('/messages', data),

  uploadAttachments: async (files: File[] | FileList): Promise<Array<{ storedAs: string; filename: string; size: number; mimeType: string }>> => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    const r = await api.post('/messages/attachments', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data.data;
  },

  toggleRead: (id: string, isRead?: boolean) =>
    api.patch(`/messages/${id}/read`, { isRead }),

  toggleStar: (id: string) =>
    api.patch(`/messages/${id}/star`),

  archiveMessage: (id: string) =>
    api.patch(`/messages/${id}/archive`),

  changeStatus: (id: string, data: { status: string }) =>
    api.patch(`/messages/${id}/status`, data),

  getStatusMapping: () =>
    api.get('/messages/status-mapping'),

  updateStatusMapping: (mapping: { pending?: Record<string, string>; important?: Record<string, string>; resolved?: Record<string, string>; starred?: Record<string, string> }) =>
    api.put('/messages/status-mapping', mapping),

  deleteMessage: (id: string) =>
    api.delete(`/messages/${id}`),

  // #R3-2 - diagnostyka: dlaczego mail nie pojawia się w liście Inbox.
  // Zwraca listę filter checks z pass/fail i hint.
  visibilityCheck: (id: string) =>
    api.get<{
      messageId: string;
      subject: string | null;
      from: string;
      conversationStatus: string | null;
      visible: boolean;
      reasons: Array<{ check: string; pass: boolean; detail: string }>;
      hint: string;
    }>(`/messages/${id}/visibility-check`),

  // Atomic undo dla delete/archive — idempotent, odporny na race z IMAP MOVE.
  // Odwraca isDeleted/isArchived i (jeśli mail w \Trash/\Archive) enqueue
  // IMAP move do INBOX.
  restoreAny: (id: string) =>
    api.post(`/messages/${id}/restore-any`),

  bulkAction: (data: { storeId: string; ids: string[]; action: string; folder?: string; labelId?: string }) =>
    api.post('/messages/bulk-action', data),

  // Message templates
  getTemplates: (params?: { storeId?: string }) =>
    api.get('/messages/templates', { params }),

  getAllTemplates: () =>
    api.get('/messages/templates/all'),

  getTemplateCategories: () =>
    api.get<{ data: MessageTemplateCategoryDto[] }>('/messages/template-categories'),

  createTemplateCategory: (name: string) =>
    api.post<{ data: MessageTemplateCategoryDto }>('/messages/template-categories', { name }),

  deleteTemplateCategory: (id: string) =>
    api.delete(`/messages/template-categories/${id}`),

  createTemplate: (data: { name: string; categoryId?: string | null; subject?: string; bodyHtml: string; bodyText?: string; storeId?: string | null; isActive?: boolean; position?: number }) =>
    api.post('/messages/templates', data),

  updateTemplate: (id: string, data: Record<string, unknown>) =>
    api.put(`/messages/templates/${id}`, data),

  deleteTemplate: (id: string) =>
    api.delete(`/messages/templates/${id}`),

  // System templates (opt-in import zarządzanej globalnie biblioteki SUPER_ADMIN)
  getSystemTemplatesAvailable: () =>
    api.get<{ count: number; categoryCount: number }>('/messages/templates/system-available'),

  loadSystemTemplates: () =>
    api.post<{ data: { imported: number; skipped: number; importedCategories: number } }>('/messages/templates/load-system'),

  // Labels
  getLabels: () =>
    api.get('/messages/labels'),

  createLabel: (data: { name: string; color?: string; position?: number }) =>
    api.post('/messages/labels', data),

  updateLabel: (id: string, data: { name?: string; color?: string; position?: number }) =>
    api.put(`/messages/labels/${id}`, data),

  deleteLabel: (id: string) =>
    api.delete(`/messages/labels/${id}`),

  addLabelToMessage: (messageId: string, labelId: string) =>
    api.post(`/messages/${messageId}/labels`, { labelId }),

  removeLabelFromMessage: (messageId: string, labelId: string) =>
    api.delete(`/messages/${messageId}/labels/${labelId}`),

  // Foldery IMAP: CRUD i favorite przeniesione do inboxEmailApi (natywne drzewo IMAP).
  // Custom foldery (InboxFolder) oraz status→folder mapping zostały wycofane.

  moveToFolder: (messageId: string, imapFolderId: string) =>
    api.patch(`/messages/${messageId}/move`, { imapFolderId }),

  // Rules
  getRules: () =>
    api.get('/messages/rules'),

  createRule: (data: Record<string, unknown>) =>
    api.post('/messages/rules', data),

  updateRule: (id: string, data: Record<string, unknown>) =>
    api.put(`/messages/rules/${id}`, data),

  deleteRule: (id: string) =>
    api.delete(`/messages/rules/${id}`),

  reorderRules: (order: Array<{ id: string; position: number }>) =>
    api.put('/messages/rules/reorder', { order }),

  testRule: (id: string, messageId: string) =>
    api.post(`/messages/rules/${id}/test`, { messageId }),

  applyRule: (id: string) =>
    api.post(`/messages/rules/${id}/apply`),

  // Customer history
  getCustomerHistory: (params: { email: string; storeId?: string }) =>
    api.get('/messages/customer-history', { params }),

  // Conversation notes
  getConversationNotes: (messageId: string) =>
    api.get(`/messages/${messageId}/notes`),

  addConversationNote: (messageId: string, data: { content: string }) =>
    api.post(`/messages/${messageId}/notes`, data),

  deleteConversationNote: (messageId: string, noteId: string) =>
    api.delete(`/messages/${messageId}/notes/${noteId}`),

  // Coupons
  createCoupon: (data: { storeId: string; code: string; discountType: 'percent' | 'fixed_cart'; amount: string; singleUse: boolean }) =>
    api.post('/messages/create-coupon', data),

  // Template apply with AI
  applyTemplate: (templateId: string, data: { messageId?: string; orderId?: string; aiPersonalize: boolean }) =>
    api.post<{
      bodyHtml: string;
      subject: string;
      tokensUsed: number;
      missingVariables: MissingTemplateVariableDto[];
    }>(`/messages/templates/${templateId}/apply`, data),

  // AI Category Config
  getCategoryConfigs: () =>
    api.get('/messages/category-configs'),

  updateCategoryConfig: (category: string, data: { priority?: number; autoAction?: string | null; templateId?: string | null; isActive?: boolean }) =>
    api.put(`/messages/category-configs/${category}`, data),

  getCategoryStats: (params?: { storeId?: string }) =>
    api.get('/messages/category-stats', { params }),

  // Auto-Reply
  getAutoReplyConfig: () =>
    api.get('/messages/auto-reply-config'),

  updateAutoReplyConfig: (data: {
    isEnabled?: boolean; globalMinConfidence?: number; reviewMinConfidence?: number;
    maxDailyAutoReplies?: number; reviewQueueEnabled?: boolean; categoryRules?: Record<string, unknown>;
    aiPermissionLevel?: number; allowCategorization?: boolean; allowAutoActions?: boolean;
    allowDraftGeneration?: boolean; allowAutoSend?: boolean; draftsOnlyForInbox?: boolean;
    autoResolveOnSend?: boolean; deleteDraftOnMessageGone?: boolean;
    draftsTargetFolder?: Record<string, string>;
  }) =>
    api.put('/messages/auto-reply-config', data),

  setAiPermissionLevel: (level: number) =>
    api.post('/messages/auto-reply-config/set-level', { level }),

  // Jednorazowe czyszczenie pending draftów dla maili już poza Inboxem
  // (isDeleted/isArchived/specialUse in Trash/Archive/Junk). Opcja
  // deleteDraftOnMessageGone czyści tylko przy NOWYCH akcjach; ten endpoint
  // robi retroaktywnie. User odpala z settings albo kiedykolwiek chce.
  cleanupOrphanedDrafts: () =>
    api.post<{ cleaned: number }>('/messages/auto-reply-config/cleanup-orphaned-drafts'),

  getAutoReplyQueue: (params: { storeId: string; page?: number; limit?: number }) =>
    api.get('/messages/auto-reply-queue', { params }),

  getInboxAiDailyReport: (params: { storeId: string }) =>
    api.get('/messages/ai-daily-report', { params }),

  approveAutoReply: (id: string, attachments?: Array<{ storedAs: string; filename: string; mimeType: string }>) =>
    api.post(`/messages/auto-reply/${id}/approve`, { attachments }),

  updateAutoReplyDraft: (id: string, data: { draftHtml: string; draftText?: string }) =>
    api.patch(`/messages/auto-reply/${id}`, data),

  rejectAutoReply: (id: string) =>
    api.post(`/messages/auto-reply/${id}/reject`),

  regenerateAutoReply: (id: string, data: { suggestion: string; provider?: string }) =>
    api.post(`/messages/auto-reply/${id}/regenerate`, data),

  getAutoReplyStats: () =>
    api.get('/messages/auto-reply-stats'),

  // Quick send
  quickSend: (data: { messageId: string; shortcut?: string; templateId?: string }) =>
    api.post('/messages/quick-send', data),

  // RODO purge audit log (read-only, ADMIN+SUPER_ADMIN)
  getPurgeLogs: (params?: { limit?: number; page?: number }) =>
    api.get('/settings/inbox/purge-logs', { params }),

  // Tenant settings — inbox section (retention itd.)
  getTenantSettings: () => api.get('/settings'),
  updateInboxSettings: (data: {
    emailBodyRetentionDays?: number;
    defaultStoreId?: string | null;
    badgeDisplay?: { navigation?: boolean; inbox?: boolean; imapFolders?: boolean; favorites?: boolean };
  }) =>
    api.put('/settings/inbox', data),
};

// =============================================================================
// Customer Notes API — unified notatki z zakresem kontekstowym.
// =============================================================================
export interface CustomerNote {
  id: string;
  tenantId: string;
  customerEmail: string | null;
  orderId: string | null;
  conversationId: string | null;
  content: string;
  userId: string | null;
  userName: string;
  createdAt: string;
  updatedAt: string;
  order?: { id: string; orderNumber: string } | null;
  conversation?: { id: string; subject: string | null } | null;
}

export const customerNotesApi = {
  // Gdy podany jest orderId/conversationId, backend domyślnie filtruje po
  // tym konkretnym kontekście. Scope=customer służy do widoku po e-mailu.
  list: (params: { customerEmail?: string; orderId?: string; conversationId?: string; scope?: 'context' | 'customer' }) =>
    api.get<{ data: CustomerNote[] }>('/customer-notes', { params }),

  create: (data: { content: string; customerEmail?: string | null; orderId?: string; conversationId?: string; scope?: 'context' | 'customer' }) =>
    api.post<{ data: CustomerNote }>('/customer-notes', data),

  update: (id: string, data: { content: string }) =>
    api.patch<{ data: CustomerNote }>(`/customer-notes/${id}`, data),

  remove: (id: string) =>
    api.delete<{ success: boolean }>(`/customer-notes/${id}`),
};

// =============================================================================
// AI API
// =============================================================================

export const aiApi = {
  // User endpoints
  getProviders: () =>
    api.get('/ai/providers'),

  getBalance: () =>
    api.get('/ai/balance'),

  getTransactions: (params?: { page?: number; limit?: number }) =>
    api.get('/ai/transactions', { params }),

  getUsage: () =>
    api.get('/ai/usage'),

  generateReply: (data: { messageId: string; provider: string; suggestion?: string }) =>
    api.post('/ai/generate-reply', data),

  transcribe: (audioBlob: Blob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    return api.post('/ai/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getSettings: () =>
    api.get('/ai/settings'),

  updateSettings: (data: { preferredProvider?: string; autoReply?: boolean; aiTemplatePersonalization?: boolean; systemPrompt?: string }) =>
    api.put('/ai/settings', data),

  getReviewGeneratorRuntimeSettings: () =>
    api.get<{
      data: {
        defaultImageModel: string;
        reviewImageTemplates: Array<{ id: string; name: string }>;
      };
    }>('/ai/review-generator-settings'),

  // Super Admin endpoints
  getAdminProviders: () =>
    api.get('/ai/admin/providers'),

  updateProvider: (name: string, data: { displayName: string; apiKey?: string; modelId: string; isActive: boolean; maxTokens: number; temperature: number }) =>
    api.put(`/ai/admin/providers/${name}`, data),

  testProvider: (name: string) =>
    api.post<{
      success: true;
      data: { provider: string; model: string; latencyMs: number; totalTokens: number };
    }>(`/ai/admin/providers/${name}/test`),

  getUnlimitedUsers: () =>
    api.get('/ai/admin/unlimited-users'),

  addUnlimitedUser: (data: { userId: string; tenantId: string }) =>
    api.post('/ai/admin/unlimited-users', data),

  removeUnlimitedUser: (userId: string) =>
    api.delete(`/ai/admin/unlimited-users/${userId}`),

  grantTokens: (data: { userId: string; tenantId: string; amount: number; description?: string }) =>
    api.post('/ai/admin/grant-tokens', data),

  getAllUsers: () =>
    api.get('/ai/admin/all-users'),

  // Global default provider
  getDefaultProvider: () =>
    api.get('/ai/admin/default-provider'),

  updateDefaultProvider: (provider: string | null) =>
    api.put('/ai/admin/default-provider', { provider }),

  getActionModels: () =>
    api.get('/ai/admin/action-models'),

  updateActionModels: (mappings: Record<string, { provider: string | null; modelId: string | null }>) =>
    api.put('/ai/admin/action-models', { mappings }),

  getReviewGeneratorSettings: () =>
    api.get<{
      data: {
        defaultImageProvider: string | null;
        defaultImageModel: string;
        reviewImageTemplates: Array<{ id: string; name: string; prompt: string }>;
      };
    }>('/ai/admin/review-generator-settings'),

  updateReviewGeneratorSettings: (data: {
    defaultImageProvider?: string | null;
    defaultImageModel?: string;
    reviewImageTemplates?: Array<{ id: string; name: string; prompt: string }>;
  }) =>
    api.put('/ai/admin/review-generator-settings', data),
};

// =============================================================================
// Knowledge Base API
// =============================================================================

export const knowledgeBaseApi = {
  getItems: (params?: { page?: number; limit?: number; category?: string; search?: string; storeId?: string; sectionType?: string; isPreset?: string }) =>
    api.get('/knowledge-base', { params }),

  getCategories: (params?: { storeId?: string }) =>
    api.get('/knowledge-base/categories', { params }),

  getItem: (id: string) =>
    api.get(`/knowledge-base/${id}`),

  createItem: (data: Record<string, unknown>) =>
    api.post('/knowledge-base', data),

  updateItem: (id: string, data: Record<string, unknown>) =>
    api.put(`/knowledge-base/${id}`, data),

  deleteItem: (id: string) =>
    api.delete(`/knowledge-base/${id}`),

  reorder: (orderedIds: string[]) =>
    api.put('/knowledge-base/reorder', { orderedIds }),

  getSectionDefinitions: () =>
    api.get('/knowledge-base/section-definitions'),

  initPresets: (storeId?: string) =>
    api.post('/knowledge-base/init-presets', { storeId }),

  getLabProducts: () =>
    api.get('/knowledge-base/lab-products'),

  getProductFaqBundles: (storeId: string) =>
    api.get('/knowledge-base/product-faq-bundles', { params: { storeId } }),

  generateFromLab: (labKnowledgeBaseId: string, storeId?: string, warehouseProductId?: string) =>
    api.post('/knowledge-base/generate-from-lab', { labKnowledgeBaseId, storeId, warehouseProductId }),

  createImport: (storeId: string, files: File[]) => {
    const formData = new FormData();
    formData.append('storeId', storeId);
    files.forEach(file => formData.append('files', file));
    return api.post('/knowledge-base/imports', formData);
  },

  getImport: (id: string) =>
    api.get(`/knowledge-base/imports/${id}`),

  applyImport: (id: string, proposals: Array<{ id: string; value?: unknown }>) =>
    api.post(`/knowledge-base/imports/${id}/apply`, { proposals }),
};

// =============================================================================
// Inbox Email (IMAP) API
// =============================================================================

export type ImapFolderNode = {
  id: string;
  path: string;
  name: string;
  parentPath: string | null;
  specialUse: string | null;
  delimiter: string;
  unreadCount: number;
  totalCount: number;
  isFavorite: boolean;
  favoriteOrder: number | null;
  color: string | null;
  icon: string | null;
  children: ImapFolderNode[];
};

export type ImapFoldersTree = {
  data: Array<{
    id: string;
    name: string;
    host: string;
    fromEmail: string | null;
    isActive: boolean;
    supportsCondstore: boolean;
    folders: ImapFolderNode[];
  }>;
};

export const inboxEmailApi = {
  getConfigs: () =>
    api.get('/inbox-email/configs'),

  createConfig: (data: { storeId: string; name: string; host: string; port: number; secure: boolean; user: string; password: string; fromEmail: string; smtpConfigId?: string | null; isActive?: boolean }) =>
    api.post('/inbox-email/configs', data),

  updateConfig: (id: string, data: Record<string, unknown>) =>
    api.put(`/inbox-email/configs/${id}`, data),

  deleteConfig: (id: string) =>
    api.delete(`/inbox-email/configs/${id}`),

  testConfig: (id: string) =>
    api.post(`/inbox-email/configs/${id}/test`),

  syncConfig: (id: string) =>
    api.post(`/inbox-email/configs/${id}/sync`),

  discoverFolders: (id: string) =>
    api.post<{ success: boolean; upserted: number; removed: number }>(`/inbox-email/configs/${id}/discover-folders`),

  syncAll: () =>
    api.post('/inbox-email/sync-all'),

  // Natywne drzewo folderów IMAP (pełny tree per config).
  getFoldersTree: (storeId: string) =>
    api.get<ImapFoldersTree>('/inbox-email/folders-tree', { params: { storeId } }),

  // Legacy flat list per config (tylko z serwera IMAP, bez metadanych ulubionych).
  // Używany do podglądu struktury przy dodawaniu konta.
  listImapFolders: (configId: string) =>
    api.get(`/inbox-email/configs/${configId}/imap-folders`),

  // Tworzy folder na serwerze IMAP (mailboxCreate) — discovery upsertuje do naszej bazy.
  createImapFolder: (configId: string, path: string, delimiter?: string) =>
    api.post(`/inbox-email/configs/${configId}/folders`, { path, delimiter }),

  // Rename folderu na serwerze IMAP (mailboxRename) + synchronizacja path w bazie.
  renameImapFolder: (folderId: string, newPath: string) =>
    api.put(`/inbox-email/folders/${folderId}/rename`, { newPath }),

  // Usuwa folder na serwerze IMAP (mailboxDelete) — operacja nieodwracalna.
  deleteImapFolder: (folderId: string) =>
    api.delete(`/inbox-email/folders/${folderId}`),

  // Metadata EcomFlow-only (nie propagowane na IMAP): isFavorite/favoriteOrder/color/icon.
  setFolderFavorite: (
    folderId: string,
    data: { isFavorite: boolean; favoriteOrder?: number | null; color?: string | null; icon?: string | null },
  ) =>
    api.put(`/inbox-email/folders/${folderId}/favorite`, data),
};

// ─── Kreator API ─────────────────────────────────────────────────────────────

export const kreatorApi = {
  getSettings: (integrationId: string) =>
    api.get(`/kreator/${integrationId}`),

  updateSettings: (integrationId: string, data: Record<string, unknown>) =>
    api.put(`/kreator/${integrationId}/settings`, data),

  updateColors: (integrationId: string, data: Record<string, unknown>) =>
    api.put(`/kreator/${integrationId}/colors`, data),

  updateTypography: (integrationId: string, data: Record<string, unknown>) =>
    api.put(`/kreator/${integrationId}/typography`, data),

  updateNavigation: (integrationId: string, data: Record<string, unknown>) =>
    api.put(`/kreator/${integrationId}/navigation`, data),

  updateFooter: (integrationId: string, data: Record<string, unknown>) =>
    api.put(`/kreator/${integrationId}/footer`, data),

  updatePageAppearance: (integrationId: string, data: Record<string, unknown>) =>
    api.put(`/kreator/${integrationId}/page-appearance`, data),

  updateSmartMenu: (integrationId: string, data: unknown[]) =>
    api.put(`/kreator/${integrationId}/smart-menu`, data),

  resetToDefaults: (integrationId: string) =>
    api.post(`/kreator/${integrationId}/reset-to-defaults`),

  resetSectionToDefaults: (integrationId: string, section: string) =>
    api.post(`/kreator/${integrationId}/reset-to-defaults/${section}`),

  exportSettings: (integrationId: string) =>
    api.get(`/kreator/${integrationId}/settings-export`),

  importSettings: (integrationId: string, settings: Record<string, unknown>) =>
    api.post(`/kreator/${integrationId}/settings-import`, { settings }),

  uploadLogo: (integrationId: string, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post<{ url: string }>(`/kreator/${integrationId}/upload-logo`, formData, {
      headers: { 'Content-Type': undefined as unknown as string },
    });
  },

  uploadProductLogo: (integrationId: string, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post<{ url: string }>(`/kreator/${integrationId}/upload-product-logo`, formData, {
      headers: { 'Content-Type': undefined as unknown as string },
    });
  },

  purgeWpCache: (integrationId: string) =>
    api.post(`/kreator/${integrationId}/purge-wp-cache`),

  diagnoseWp: (integrationId: string) =>
    api.get<{ ok: boolean; step?: string; error?: string; wpUrl?: string; httpStatus?: number; message?: string }>(`/kreator/${integrationId}/diagnose-wp`),

  // Advertoriale (WP posts z tagiem 'advertorial' — funnels content)
  getWpAdvertorials: (integrationId: string, params?: { page?: number; per_page?: number; search?: string }) =>
    api.get<WpAdvertorialItem[]>(`/kreator/${integrationId}/wp-advertorials`, { params }),

  getWpAdvertorial: (integrationId: string, postId: number) =>
    api.get<WpAdvertorialFull>(`/kreator/${integrationId}/wp-advertorials/${postId}`),

  createWpAdvertorial: (integrationId: string, data: Record<string, unknown>) =>
    api.post<WpAdvertorialFull>(`/kreator/${integrationId}/wp-advertorials`, data),

  updateWpAdvertorial: (integrationId: string, postId: number, data: Record<string, unknown>) =>
    api.put<WpAdvertorialFull>(`/kreator/${integrationId}/wp-advertorials/${postId}`, data),

  deleteWpAdvertorial: (integrationId: string, postId: number) =>
    api.delete(`/kreator/${integrationId}/wp-advertorials/${postId}`),

  // WordPress Pages (faktyczny post type 'page' — kontakt, regulamin, polityka)
  getWpPages: (integrationId: string, params?: { page?: number; per_page?: number; search?: string; status?: string }) =>
    api.get<WpPageItem[]>(`/kreator/${integrationId}/wp-pages`, { params }),

  getWpPage: (integrationId: string, pageId: number) =>
    api.get<WpPageFull>(`/kreator/${integrationId}/wp-pages/${pageId}`),

  createWpPage: (integrationId: string, data: Record<string, unknown>) =>
    api.post<WpPageFull>(`/kreator/${integrationId}/wp-pages`, data),

  updateWpPage: (integrationId: string, pageId: number, data: Record<string, unknown>) =>
    api.put<WpPageFull>(`/kreator/${integrationId}/wp-pages/${pageId}`, data),

  duplicateWpPage: (integrationId: string, pageId: number) =>
    api.post<WpPageFull>(`/kreator/${integrationId}/wp-pages/${pageId}/duplicate`),

  deleteWpPage: (integrationId: string, pageId: number) =>
    api.delete(`/kreator/${integrationId}/wp-pages/${pageId}`),
  restoreWpPage: (integrationId: string, pageId: number) =>
    api.post(`/kreator/${integrationId}/wp-pages/${pageId}/restore`),

  getOrCreateBuilderForWpPage: (integrationId: string, pageId: number) =>
    api.post<{ id: string; slug: string; status: string; pageType: string; wpPageId: number }>(
      `/kreator/${integrationId}/wp-pages/${pageId}/builder`,
    ),

  // Homepage / Blog settings (Kreator) + sync z WP page_on_front / page_for_posts
  updateHomepage: (integrationId: string, data: { wpPageId: number | null }) =>
    api.put(`/kreator/${integrationId}/homepage`, data),

  updateBlog: (integrationId: string, data: { wpPageId: number | null }) =>
    api.put(`/kreator/${integrationId}/blog`, data),

  setWpReadingOptions: (integrationId: string, data: { homepageId: number | null; blogPageId: number | null }) =>
    api.put<{ show_on_front: 'page' | 'posts'; page_on_front: number; page_for_posts: number }>(`/kreator/${integrationId}/wp-reading-options`, data),

  getWpReadingOptions: (integrationId: string) =>
    api.get<{ show_on_front: 'page' | 'posts'; page_on_front: number; page_for_posts: number }>(`/kreator/${integrationId}/wp-reading-options`),

  getWpSiteOptions: (integrationId: string) =>
    api.get<WpSiteOptions>(`/kreator/${integrationId}/wp-site-options`),

  updateWpSiteOptions: (integrationId: string, data: {
    siteTitle: string;
    tagline: string;
    siteIconId: number | null;
    timezoneString: string;
    permalinkStructure: string;
  }) => api.put<WpSiteOptions>(`/kreator/${integrationId}/wp-site-options`, data),

  // Legacy HOMEPAGE Page (custom builder homepage z poprzednich testów)
  getLegacyHomepageCount: (integrationId: string) =>
    api.get<{ count: number; pageCount: number; v1BlockCount: number }>(`/kreator/${integrationId}/legacy-homepage`),

  deleteLegacyHomepage: (integrationId: string) =>
    api.delete<{ success: boolean; deleted: number; v1BlocksCleared: number }>(`/kreator/${integrationId}/legacy-homepage`),

  getWcProducts: (integrationId: string, params?: { page?: number; per_page?: number; search?: string; status?: string; include?: string; match?: 'identity' }) =>
    api.get<WcProductItem[]>(`/kreator/${integrationId}/wc-products`, { params }),

  // Product CRUD
  getWcProduct: (integrationId: string, productId: number) =>
    api.get<WcProductFull>(`/kreator/${integrationId}/wc-products/${productId}`),

  createWcProduct: (integrationId: string, data: Record<string, unknown>) =>
    api.post<WcProductFull>(`/kreator/${integrationId}/wc-products`, data),

  updateWcProduct: (integrationId: string, productId: number, data: Record<string, unknown>) =>
    api.put<WcProductFull>(`/kreator/${integrationId}/wc-products/${productId}`, data),

  duplicateWcProduct: (integrationId: string, productId: number) =>
    api.post<WcProductFull>(`/kreator/${integrationId}/wc-products/${productId}/duplicate`),

  resetWcProductOmnibusPrice: (integrationId: string, productId: number) =>
    api.post<{ success: boolean; productId: number }>(`/kreator/${integrationId}/wc-products/${productId}/reset-omnibus-price`),

  getWcProductReviews: (integrationId: string, productId: number, params?: { page?: number; per_page?: number; status?: string }) =>
    api.get<{ data: WcProductReview[]; pagination: { page: number; limit: number; total: number; pages: number } }>(
      `/kreator/${integrationId}/wc-products/${productId}/reviews`,
      { params },
    ),

  createWcProductReview: (integrationId: string, productId: number, data: Partial<WcProductReview>) =>
    api.post<WcProductReview>(`/kreator/${integrationId}/wc-products/${productId}/reviews`, data),

  updateWcProductReview: (integrationId: string, productId: number, reviewId: number, data: Partial<WcProductReview> & { photoIds?: number[] }) =>
    api.put<WcProductReview>(`/kreator/${integrationId}/wc-products/${productId}/reviews/${reviewId}`, data),

  deleteWcProductReview: (integrationId: string, productId: number, reviewId: number) =>
    api.delete<{ success: boolean }>(`/kreator/${integrationId}/wc-products/${productId}/reviews/${reviewId}`),

  replyWcProductReview: (integrationId: string, productId: number, reviewId: number, data: { content: string; author?: string; authorEmail?: string }) =>
    api.post<WcProductReviewReply>(`/kreator/${integrationId}/wc-products/${productId}/reviews/${reviewId}/reply`, data),

  generateWcProductReviews: (integrationId: string, productId: number, data: ProductReviewGenerationConfig) =>
    api.post<{
      success: boolean;
      data: WcProductReview[];
      imageErrors: Array<{ reviewId?: number; message: string }>;
      usage: { provider: string; model: string; tokensUsed: number };
    }>(`/kreator/${integrationId}/wc-products/${productId}/reviews/generate`, data),

  generateWcProductReviewDrafts: (integrationId: string, productId: number, data: ProductReviewGenerationConfig) =>
    api.post<{
      success: boolean;
      data: GeneratedProductReviewDraft[];
      usage: { provider: string; model: string; tokensUsed: number };
    }>(`/kreator/${integrationId}/wc-products/${productId}/reviews/generate-drafts`, data),

  importWcProductReviews: (integrationId: string, productId: number, data: { sourceProductId: number }) =>
    api.post<{
      success: boolean;
      data: WcProductReview[];
      count: number;
    }>(`/kreator/${integrationId}/wc-products/${productId}/reviews/import`, data),

  generateWcProductReviewImage: (integrationId: string, productId: number, reviewId: number, data: {
    templateId: string;
    referenceImageUrl?: string | null;
    referenceImages?: ProductReviewReferenceImages;
    additionalInstruction?: string;
    review: { review: string; reviewer?: string; rating?: number; gender?: 'female' | 'male' | 'unknown'; imagePromptHint?: string };
  }) =>
    api.post<{
      success: boolean;
      media: WpMediaItem;
      review: WcProductReview;
      usage: { provider: string; model: string; tokensUsed: number };
    }>(`/kreator/${integrationId}/wc-products/${productId}/reviews/${reviewId}/generate-image`, data),

  deleteWcProduct: (integrationId: string, productId: number) =>
    api.delete(`/kreator/${integrationId}/wc-products/${productId}`),
  restoreWcProduct: (integrationId: string, productId: number) =>
    api.post(`/kreator/${integrationId}/wc-products/${productId}/restore`),

  getWcCategories: (integrationId: string) =>
    api.get<WcCategory[]>(`/kreator/${integrationId}/wc-categories`),

  getWcShipping: (integrationId: string) =>
    api.get<WcShippingConfig>(`/kreator/${integrationId}/wc-shipping`, {
      params: { refreshedAt: Date.now() },
    }),

  getWcShippingCountries: (integrationId: string) =>
    api.get<WcCountry[]>(`/kreator/${integrationId}/wc-shipping/countries`),

  getWcShippingSettings: (integrationId: string) =>
    api.get<{ settings: WcGeneralSetting[] }>(`/kreator/${integrationId}/wc-settings/shipping`),

  updateWcShippingSettings: (integrationId: string, settings: Record<string, WcGeneralSettingValue>) =>
    api.put<{ settings: WcGeneralSetting[] }>(`/kreator/${integrationId}/wc-settings/shipping`, { settings }),

  getWcGeneralSettings: (integrationId: string) =>
    api.get<{ settings: WcGeneralSetting[] }>(`/kreator/${integrationId}/wc-settings/general`),

  updateWcGeneralSettings: (integrationId: string, settings: Record<string, WcGeneralSettingValue>) =>
    api.put<{ settings: WcGeneralSetting[] }>(`/kreator/${integrationId}/wc-settings/general`, { settings }),

  getWcTaxSettings: (integrationId: string) =>
    api.get<WcTaxConfig>(`/kreator/${integrationId}/wc-settings/taxes`),

  updateWcTaxSettings: (integrationId: string, settings: Record<string, WcGeneralSettingValue>) =>
    api.put<WcTaxConfig>(`/kreator/${integrationId}/wc-settings/taxes`, { settings }),

  updateWcTaxRates: (integrationId: string, taxClass: string, rates: WcTaxRate[]) =>
    api.put<{ rates: WcTaxRate[] }>(`/kreator/${integrationId}/wc-settings/taxes/rates/${taxClass}`, { rates }),

  createWcShippingZone: (
    integrationId: string,
    data: { name: string; order?: number; locations?: WcShippingZoneLocation[] },
  ) =>
    api.post<{ zones: WcShippingZone[] }>(`/kreator/${integrationId}/wc-shipping/zones`, data),

  updateWcShippingZone: (
    integrationId: string,
    zoneId: number,
    data: { name?: string; order?: number; locations?: WcShippingZoneLocation[] },
  ) =>
    api.put<{ zones: WcShippingZone[] }>(`/kreator/${integrationId}/wc-shipping/zones/${zoneId}`, data),

  deleteWcShippingZone: (integrationId: string, zoneId: number) =>
    api.delete<{ zones: WcShippingZone[] }>(`/kreator/${integrationId}/wc-shipping/zones/${zoneId}`),

  createWcShippingZoneMethod: (
    integrationId: string,
    zoneId: number,
    data: { method_id: string; enabled?: boolean; settings?: Record<string, string | number | boolean | null> },
  ) =>
    api.post<{ zones: WcShippingZone[] }>(`/kreator/${integrationId}/wc-shipping/zones/${zoneId}/methods`, data),

  updateWcShippingZoneMethod: (
    integrationId: string,
    zoneId: number,
    instanceId: number,
    data: { enabled?: boolean; order?: number; settings?: Record<string, string | number | boolean | null> },
  ) =>
    api.put<{ zones: WcShippingZone[] }>(`/kreator/${integrationId}/wc-shipping/zones/${zoneId}/methods/${instanceId}`, data),

  deleteWcShippingZoneMethod: (integrationId: string, zoneId: number, instanceId: number) =>
    api.delete<{ zones: WcShippingZone[] }>(
      `/kreator/${integrationId}/wc-shipping/zones/${zoneId}/methods/${instanceId}`,
    ),

  repairWcFlexibleShippingMethod: (
    integrationId: string,
    zoneId: number,
    instanceId: number,
    cost: number,
  ) =>
    api.post<{ zones: WcShippingZone[]; repaired: true }>(
      `/kreator/${integrationId}/wc-shipping/zones/${zoneId}/methods/${instanceId}/repair-flexible-shipping`,
      { cost },
    ),

  getWcPayments: (integrationId: string) => api.get<{ gateways: WcPaymentMethod[] }>(`/kreator/${integrationId}/wc-payments`),
  updateWcPayment: (integrationId: string, id: string, data: Pick<WcPaymentMethod, 'title' | 'description' | 'enabled' | 'order' | 'logoUrl' | 'logoMaxWidth'>) => api.put(`/kreator/${integrationId}/wc-payments/${encodeURIComponent(id)}`, data),

  getWcShippingAssets: (integrationId: string) =>
    api.get<WcShippingAssetsConfig>(`/kreator/${integrationId}/wc-shipping/assets`),

  updateWcShippingAssets: (integrationId: string, data: WcShippingAssetsConfig) =>
    api.put<WcShippingAssetsConfig>(`/kreator/${integrationId}/wc-shipping/assets`, data),

  getWcShippingRules: (integrationId: string) =>
    api.get<WcShippingRulesConfig>(`/kreator/${integrationId}/wc-shipping/rules`),

  updateWcShippingRules: (integrationId: string, data: WcShippingRulesConfig) =>
    api.put<WcShippingRulesConfig>(`/kreator/${integrationId}/wc-shipping/rules`, data),

  // Media library
  getWpMedia: (integrationId: string, params?: { page?: number; per_page?: number; search?: string; mime_type?: string }) =>
    api.get<WpMediaItem[]>(`/kreator/${integrationId}/wp-media`, { params }),

  deleteWpMedia: (integrationId: string, mediaId: number) =>
    api.delete<{ success: boolean }>(`/kreator/${integrationId}/wp-media/${mediaId}`),

  // Media upload — delete Content-Type so Axios auto-sets multipart boundary
  uploadMedia: (integrationId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<WpMediaItem>(`/kreator/${integrationId}/wp-media`, formData, {
      headers: { 'Content-Type': undefined as unknown as string },
      timeout: 180000,
    });
  },
};

export interface WpAdvertorialItem {
  id: number;
  title: string;
  slug: string;
  status: string;
  link: string;
  date: string;
  modified: string;
}

export interface WpAdvertorialFull extends WpAdvertorialItem {
  content: string;
  tags: number[];
  featured_media: number;
}

export interface WpPageItem {
  id: number;
  title: string;
  slug: string;
  status: string;
  link: string;
  date: string;
  modified: string;
  parent: number;
}

export interface WpPageFull extends WpPageItem {
  content: string;
  meta: Record<string, unknown>;
  builderPage: { id: string; status: string; slug: string } | null;
}

export interface WcProductItem {
  id: number;
  name: string;
  slug: string;
  sku: string;
  price: string;
  regularPrice: string;
  salePrice: string;
  stockQuantity: number | null;
  stockStatus: string;
  status: string;
  catalogVisibility: string;
  permalink?: string;
}

export interface WcProductFull {
  id: number;
  name: string;
  slug: string;
  sku: string;
  price: string;
  regular_price: string;
  sale_price: string;
  stock_quantity: number | null;
  stock_status: string;
  manage_stock: boolean;
  status: string;
  catalog_visibility: string;
  description: string;
  short_description: string;
  categories: Array<{ id: number; name: string; slug: string }>;
  images: Array<{ id: number; src: string; name: string; alt: string }>;
  tags: Array<{ id: number; name: string; slug: string }>;
  weight: string;
  dimensions: { length: string; width: string; height: string };
  permalink: string;
  meta_data: Array<{ id: number; key: string; value: string }>;
}

export type WcProductReviewStatus = 'approved' | 'hold' | 'spam' | 'trash';
export type WcProductReviewVerificationType = 'visitor_review' | 'merchant_verified' | 'imported_external' | 'purchased_verified' | 'imported_facebook';

export interface WcProductReviewPhoto {
  id: number;
  src: string;
  srcset?: string;
  alt: string;
}

export interface WcProductReviewReply {
  id: number;
  reviewId: number;
  author: string;
  authorEmail: string;
  content: string;
  status: WcProductReviewStatus;
  dateCreated: string;
}

export interface WcProductReview {
  id: number;
  productId: number;
  status: WcProductReviewStatus;
  reviewer: string;
  reviewerEmail: string;
  review: string;
  rating: number;
  dateCreated: string;
  verified: boolean;
  verificationType: WcProductReviewVerificationType;
  photos: WcProductReviewPhoto[];
  replies: WcProductReviewReply[];
}

export interface ProductReviewGenerationConfig {
  ratings: { 5: number; 4: number; 3: number; 2: number; 1: number };
  femalePercent: number;
  tone: 'natural' | 'enthusiastic' | 'balanced' | 'skeptical';
  lengthMode: 'mixed' | 'short' | 'medium' | 'long';
  objectionPercent: number;
  slightCriticismPercent: number;
  dateFrom?: string;
  dateTo?: string;
  status: 'approved' | 'hold';
  verificationType: 'visitor_review' | 'merchant_verified' | 'imported_external';
  extraGuidance?: string;
  generateImages: boolean;
  imageTemplateId?: string;
  imageCount?: number;
  referenceImageId?: number | null;
  referenceImageUrl?: string | null;
  referenceImages?: ProductReviewReferenceImages;
}

export interface ProductReviewReferenceImage {
  id?: number | null;
  url?: string | null;
}

export interface ProductReviewReferenceImages {
  packaging?: ProductReviewReferenceImage | null;
  product?: ProductReviewReferenceImage | null;
  beforeAfter?: ProductReviewReferenceImage | null;
}

export interface GeneratedProductReviewDraft {
  type: string;
  rating: number;
  reviewer: string;
  reviewerEmail: string;
  review: string;
  dateCreated: string;
  gender: 'female' | 'male' | 'unknown';
  imagePromptHint?: string;
}

export interface WcCategory {
  id: number;
  name: string;
  slug: string;
  parent: number;
  count: number;
}

export interface WpMediaItem {
  id: number;
  src: string;
  name: string;
  alt: string;
  mimeType?: string;
  date?: string;
}

// ─── Engine Integration API ─────────────────────────────────────────────────

export interface EngineIntegration {
  id: string;
  name: string;
  wordpressUrl: string;
  hasApiKey: boolean;
  hasWooKeys: boolean;
  isActive: boolean;
  storeId: string | null;
  store: { id: string; name: string; url: string } | null;
  createdAt: string;
  updatedAt?: string;
  apiKey?: string; // Only returned on creation
}

export interface EngineThemeStatus {
  status: 'current' | 'update' | 'unknown';
  currentVersion?: string;
  latestVersion: string;
  message?: string;
}

export interface EngineThemeUpdateResult {
  success: true;
  updated: boolean;
  message: string;
  currentVersion: string;
  previousVersion?: string;
  remoteVersion?: string;
}

export type StorefrontDeploymentStatus = 'PENDING' | 'PROCESSING' | 'RETRYING' | 'DEPLOYED' | 'SUPERSEDED' | 'INCOMPATIBLE';

export interface StorefrontDeployment {
  id: string;
  snapshotId: string;
  sequence: number;
  status: StorefrontDeploymentStatus;
  attempts: number;
  nextAttemptAt: string;
  deployedAt: string | null;
  lastError: string | null;
  checksum: string;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceJournalSettings {
  state: 'UNCONFIGURED' | 'READY' | 'ENABLED' | 'STOPPED' | 'LEGACY';
  enabled: boolean; canPrepare: boolean; captureGap: boolean;
  journalId: string | null; startedAt: string | null; captureStartedAt: string | null; throughSequence: number; remainingCapacity: number;
  engineVersion: string; wooVersion: string | null;
  storageCheck: { records: number; appendMs: number; exportMs: number; checkedAt: string } | null;
}

export interface FinanceJournalReadReceipt {
  journalId: string; sourceExportId: string; sourceHead: string; sourceExportedAt: string; acquiredAt: string;
  recordCount: number; orderCount: number; refundCount: number; tombstoneCount: number;
  captureGap: boolean; journalIntegrity: 'VERIFIED'; commerceCoverage: 'UNAVAILABLE';
}

export interface FinanceJournalArchiveReceipt {
  id: string; storeId: string; journalId: string; acquisitionKey: string; ordinal: number;
  sourceExportId: string; sourceHead: string; throughSequence: number; captureGap: boolean;
  sourceExportedAt: string; acquiredAt: string; recordedAt: string;
  coverage: 'HOOK_CAPTURE_ONLY'; commerceCoverage: 'UNAVAILABLE';
}
export interface FinanceJournalArchiveHistory {
  receipt: FinanceJournalArchiveReceipt | null; knowledgeAsOf: string; commerceCoverage: 'UNAVAILABLE';
}

export const engineApi = {
  archiveFinanceJournal: (storeId: string, expectedJournalId: string, acquisitionKey: string) =>
    api.post<{ receipt: FinanceJournalArchiveReceipt; replayed: boolean; newRevisionCount: number }>(
      `/finance/stores/${storeId}/source-journal/archive`, { expectedJournalId, acquisitionKey }),
  getFinanceJournalArchive: (storeId: string, journalId: string) =>
    api.get<FinanceJournalArchiveHistory>(`/finance/stores/${storeId}/source-journal/archive`, { params: { journalId } }),
  verifyFinanceJournal: (storeId: string, journalId: string) =>
    api.get<FinanceJournalReadReceipt>(`/finance/stores/${storeId}/source-journal`, { params: { journalId } }),
  getFinanceJournalSettings: (storeId: string) =>
    api.get<FinanceJournalSettings>(`/finance/stores/${storeId}/source-journal/settings`),
  changeFinanceJournalSettings: (storeId: string, action: 'PREPARE' | 'ENABLE' | 'DISABLE', expectedJournalId?: string) =>
    api.post<FinanceJournalSettings>(`/finance/stores/${storeId}/source-journal/settings`, { action, expectedJournalId }),
  downloadTheme: () =>
    api.get('/engine/theme-download', { responseType: 'blob' }),

  getAll: () =>
    api.get<EngineIntegration[]>('/engine'),

  create: (data: { name: string; wordpressUrl: string; wooConsumerKey?: string; wooConsumerSecret?: string }) =>
    api.post<EngineIntegration>('/engine', data),

  update: (id: string, data: { name?: string; wordpressUrl?: string; wooConsumerKey?: string; wooConsumerSecret?: string; isActive?: boolean }) =>
    api.patch<EngineIntegration>(`/engine/${id}`, data),

  delete: (id: string) =>
    api.delete(`/engine/${id}`),

  regenerateKey: (id: string) =>
    api.post<{ apiKey: string }>(`/engine/${id}/regenerate-key`),

  getThemeStatus: (id: string) =>
    api.get<EngineThemeStatus>(`/engine/${id}/theme-status`, { timeout: 20_000 }),

  updateTheme: (id: string) =>
    api.post<EngineThemeUpdateResult>(`/engine/${id}/theme-updates`, {}, { timeout: 100_000 }),

  getLatestStorefrontDeployment: (id: string) =>
    api.get<{ deployment: StorefrontDeployment | null }>(`/engine/${id}/storefront/deployments/latest`),

  rebuildStorefrontSnapshot: (id: string) =>
    api.post<{ deployment: StorefrontDeployment }>(`/engine/${id}/storefront/deployments`),

  retryStorefrontDeployment: (id: string) =>
    api.post<{ deployment: StorefrontDeployment }>(`/engine/${id}/storefront/deployments/retry`),

  downloadStorefrontSnapshot: (id: string) =>
    api.get(`/engine/${id}/storefront/snapshot`, { responseType: 'blob' }),

  rollbackStorefront: (id: string, snapshotId?: string) =>
    api.post(`/engine/${id}/storefront/rollback`, snapshotId ? { snapshotId } : {}),
};

// ─── Google Drive Integration API ────────────────────────────────────────────

export interface GoogleDriveStatus {
  configured: boolean;
  connected: boolean;
  integrationId: string | null;
  rootFolderId: string | null;
  rootFolderName: string | null;
  rootFolderUrl: string | null;
  scopes: string[];
  connectedAt: string | null;
  redirectUri: string;
}

export const googleDriveApi = {
  getStatus: () =>
    api.get<{ data: GoogleDriveStatus }>('/google-drive/status'),
  startOAuth: (data?: { returnTo?: string }) =>
    api.post<{ data: { url: string; redirectUri: string } }>('/google-drive/oauth/start', data || {}),
  setRootFolder: (folder: string) =>
    api.put<{ data: GoogleDriveStatus }>('/google-drive/root-folder', { folder }),
  createRootFolder: (name?: string) =>
    api.post<{ data: GoogleDriveStatus }>('/google-drive/root-folder/create', { name }),
  disconnect: () =>
    api.delete('/google-drive/disconnect'),
};

// ─── Studio API ─────────────────────────────────────────────────────────────

export type StudioAssetKind = 'VIDEO' | 'AUDIO' | 'IMAGE' | 'TEXT' | 'SCRIPT';
export type StudioPanelKey = 'media' | 'audio' | 'text' | 'transitions' | 'ai';

export interface StudioTimelineClip {
  id: string;
  assetId: string;
  name: string;
  kind: StudioAssetKind;
  startMs: number;
  durationMs: number;
  sourceStartMs?: number;
  sourceDurationMs?: number;
  layer: number;
  speed: number;
  position: { x: number; y: number };
  positionSpace?: 'layout';
  scaleX?: number;
  scaleY?: number;
  rotation: number;
  volumeDb?: number;
  fadeInMs?: number;
  fadeOutMs?: number;
  audioDetached?: boolean;
  detachedFromClipId?: string | null;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline' | 'line-through';
  textAlign?: 'left' | 'center' | 'right';
  textColor?: string;
  textBackgroundColor?: string;
  textBackgroundOpacity?: number;
  textBorderRadius?: number;
  textPadding?: number;
  letterSpacing?: number;
  lineHeight?: number;
  textShadow?: boolean;
  textStrokeColor?: string;
  textStrokeWidth?: number;
  content?: string | null;
  captionGroupId?: string | null;
  captionSourceClipId?: string | null;
  captionSourceAssetId?: string | null;
  dynamicCaption?: boolean;
  captionCues?: Array<{ startOffsetMs: number; endOffsetMs: number; text: string }>;
}

export interface StudioTimelineLayer {
  layer: number;
  locked?: boolean;
  hidden?: boolean;
  muted?: boolean;
}

export interface StudioTimelineTransition {
  id: string;
  type: string;
  name: string;
  fromClipId: string;
  toClipId: string;
  boundaryMs: number;
  durationMs: number;
}

export interface StudioTimeline {
  durationMs: number;
  clips: StudioTimelineClip[];
  layers?: StudioTimelineLayer[];
  transitions?: StudioTimelineTransition[];
}

export interface StudioAsset {
  id: string;
  projectId: string;
  source: 'UPLOAD' | 'LAB';
  kind: StudioAssetKind;
  name: string;
  mimeType?: string | null;
  durationMs?: number | null;
  content?: string | null;
  thumbnailUrl?: string | null;
  driveFileId?: string | null;
  driveFileUrl?: string | null;
  externalUrl?: string | null;
  previewUrl?: string | null;
  labProductId?: string | null;
  labContentId?: string | null;
  labAssetId?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface StudioProject {
  id: string;
  name: string;
  labProductId?: string | null;
  labProduct?: { id: string; name: string; imageUrl?: string | null } | null;
  driveFolderId?: string | null;
  driveFolderUrl?: string | null;
  durationMs: number;
  timeline: StudioTimeline;
  settings?: Record<string, unknown> | null;
  assets?: StudioAsset[];
  _count?: { assets: number };
  createdAt: string;
  updatedAt: string;
}

export interface StudioLabProduct {
  id: string;
  name: string;
  imageUrl?: string | null;
  status: string;
  updatedAt: string;
  _count?: {
    contents: number;
    assets: number;
    productImages: number;
    referenceImages: number;
  };
}

export interface StudioLabMaterial {
  id: string;
  materialId: string;
  materialType: 'content' | 'asset' | 'productImage' | 'referenceImage';
  labProductId: string;
  kind: StudioAssetKind;
  name: string;
  label: string;
  status: string;
  contentPreview?: string;
  previewUrl?: string | null;
  updatedAt: string;
}

export interface StudioAvatarReference {
  id: string;
  name: string;
  previewUrl?: string | null;
  status: string;
  isSystem?: boolean;
}

export const studioApi = {
  listProjects: () =>
    api.get<{ data: StudioProject[] }>('/studio/projects'),
  createProject: (data: { name: string; labProductId?: string | null; settings?: Record<string, unknown> }) =>
    api.post<{ success: boolean; data: StudioProject }>('/studio/projects', data),
  getProject: (projectId: string) =>
    api.get<{ data: StudioProject }>(`/studio/projects/${projectId}`),
  updateProject: (projectId: string, data: Partial<Pick<StudioProject, 'name' | 'labProductId' | 'timeline' | 'durationMs' | 'settings'>>) =>
    api.put<{ success: boolean; data: StudioProject }>(`/studio/projects/${projectId}`, data),
  updateProjectKeepalive: (projectId: string, data: Partial<Pick<StudioProject, 'timeline' | 'durationMs'>>) => {
    return fetch(`/api/studio/projects/${encodeURIComponent(projectId)}`, {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },
  duplicateProject: (projectId: string) =>
    api.post<{ success: boolean; data: StudioProject }>(`/studio/projects/${projectId}/duplicate`, {}),
  deleteProject: (projectId: string) =>
    api.delete<{ success: boolean }>(`/studio/projects/${projectId}`),
  uploadAsset: (projectId: string, file: File, durationMs?: number, onUploadProgress?: (progress: number) => void, signal?: AbortSignal) => {
    const formData = new FormData();
    formData.append('file', file);
    if (durationMs) formData.append('durationMs', String(durationMs));
    return api.post<{ success: boolean; data: StudioAsset }>(`/studio/projects/${projectId}/assets/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal,
      onUploadProgress: event => {
        if (event.total) onUploadProgress?.(Math.round((event.loaded / event.total) * 100));
      },
    });
  },
  createAssetFromLab: (projectId: string, data: { materialType: StudioLabMaterial['materialType']; materialId: string; labProductId: string }) =>
    api.post<{ success: boolean; data: StudioAsset }>(`/studio/projects/${projectId}/assets/from-lab`, data),
  createAssetFromAvatar: (projectId: string, avatarId: string) =>
    api.post<{ success: boolean; data: StudioAsset }>(`/studio/projects/${projectId}/assets/from-avatar`, { avatarId }),
  listAvatarReferences: () =>
    api.get<{ success: boolean; data: StudioAvatarReference[] }>('/lab/avatars'),
  listLabProducts: (params?: { search?: string }) =>
    api.get<{ data: StudioLabProduct[] }>('/studio/lab-products', { params }),
  listLabMaterials: (labProductId: string) =>
    api.get<{ data: { product: StudioLabProduct; materials: StudioLabMaterial[] } }>(`/studio/lab-products/${labProductId}/materials`),
  sendVideoPackage: (data: { labProductId: string; contentId: string }) =>
    api.post<{ success: boolean; data: { project: StudioProject; imported: number; failures: string[] } }>('/studio/video-package', data),
  transcribeAsset: (projectId: string, assetId: string, data: { targetLanguage?: string }) =>
    api.post<{ success: boolean; data: { text: string; language: string; segments: Array<{ startMs: number; endMs: number; text: string; words?: Array<{ startMs: number; endMs: number; text: string }> }>; tokensUsed: number } }>(
      `/studio/projects/${projectId}/assets/${assetId}/transcription`,
      data,
    ),
  generateImage: (projectId: string, data: { prompt: string; aspectRatio: '1:1' | '16:9' | '9:16'; referenceAssetIds: string[] }) =>
    api.post<{ success: boolean; data: StudioAsset }>(`/studio/projects/${projectId}/ai/images`, data),
};

// ─── Ad Production Integrations API ─────────────────────────────────────────

export type AdProductionProvider = 'heygen' | 'higgsfield' | 'elevenlabs' | 'kieai';

export interface AdProductionIntegrationStatus {
  provider: AdProductionProvider;
  type: 'HEYGEN' | 'HIGGSFIELD' | 'ELEVENLABS' | 'KIEAI';
  name: string;
  purpose: string;
  integrationId: string | null;
  connected: boolean;
  hasApiKey: boolean;
  isActive: boolean;
  settings: {
    defaultAvatarId?: string;
    defaultVoiceId?: string;
    defaultModel?: string;
    workspaceId?: string;
    webhookSecret?: string;
    notes?: string;
    [key: string]: unknown;
  };
  updatedAt: string | null;
}

export interface SaveAdProductionIntegrationInput {
  apiKey?: string;
  isActive?: boolean;
  settings?: AdProductionIntegrationStatus['settings'];
}

export const adProductionIntegrationsApi = {
  getStatus: () =>
    api.get<{ data: AdProductionIntegrationStatus[] }>('/ad-production-integrations/status'),
  save: (provider: AdProductionProvider, data: SaveAdProductionIntegrationInput) =>
    api.put<{ success: boolean; data: AdProductionIntegrationStatus }>(`/ad-production-integrations/${provider}`, data),
  disconnect: (provider: AdProductionProvider) =>
    api.delete<{ success: boolean }>(`/ad-production-integrations/${provider}`),
};

// ============================================
// Laboratorium API
// ============================================

export const labApi = {
  // Field Config
  getFieldConfig: () =>
    api.get('/lab/field-config'),
  saveFieldConfig: (data: { fields: unknown[]; fieldOptions?: Record<string, unknown[]> }) =>
    api.put('/admin/lab/field-config', data),
  getProductImageStyles: () =>
    api.get('/lab/product-image-styles'),
  getAdminProductImageStyles: () =>
    api.get('/admin/lab/product-image-styles'),
  saveProductImageStyles: (data: { cards: unknown[] }) =>
    api.put('/admin/lab/product-image-styles', data),

  // Products
  getProducts: (params?: { creationSource?: string; page?: number; limit?: number; status?: string; search?: string; brandId?: string; unbranded?: boolean; niche?: string; uncategorized?: boolean; withoutLaboratory?: boolean }) =>
    api.get('/lab/products', { params }),
  getLabProducts: (params?: { page?: number; limit?: number; search?: string; brandId?: string; unbranded?: boolean; niche?: string; uncategorized?: boolean }) =>
    api.get('/lab/products/lab-list', { params }),
  getProduct: (id: string) =>
    api.get(`/lab/products/${id}`),
  createProduct: (data: Record<string, unknown>) =>
    api.post('/lab/products', data),
  updateProduct: (id: string, data: Record<string, unknown>) =>
    api.put(`/lab/products/${id}`, data),
  deleteProduct: (id: string) =>
    api.delete(`/lab/products/${id}`),
  removeLaboratory: (id: string) =>
    api.delete(`/lab/products/${id}/laboratory`),
  uploadProductImage: (productId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/lab/products/${productId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  reorderProducts: (items: { id: string; sortOrder: number }[]) =>
    api.patch('/lab/products/reorder', { items }),
  moveProduct: (id: string, targetProductId: string, placement: 'before' | 'after') =>
    api.patch(`/lab/products/${id}/move`, { targetProductId, placement }),
  duplicateProduct: (id: string) =>
    api.post(`/lab/products/${id}/duplicate`),
  addToLab: (id: string) =>
    api.post(`/lab/products/${id}/add-to-lab`, {}),
  getBrands: () =>
    api.get('/lab/brands'),
  createBrand: (data: { name: string; storeId?: string | null }) =>
    api.post('/lab/brands', data),
  getBrandStore: (productId: string) =>
    api.get(`/lab/products/${productId}/brand-store`),
  startPipelineImport: (productId: string, pipeline: string, file: File) => {
    const form = new FormData();
    form.append('pipeline', pipeline);
    form.append('file', file);
    return api.post(`/lab/products/${productId}/imports`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getPipelineImport: (importId: string) =>
    api.get(`/lab/imports/${importId}`),
  applyPipelineImport: (importId: string, proposals: Array<{ id: string; value: string }>) =>
    api.post(`/lab/imports/${importId}/apply`, { proposals }),
  cancelPipeline: (productId: string, data: { pipeline: string; targetId: string }) =>
    api.post(`/lab/products/${productId}/pipelines/cancel`, data),

  // Knowledge Base (multi-KB per product)
  replaceAssetText: (productId: string, data: { targetId: string; kind: 'knowledge' | 'content'; find: string; replacement: string; apply: boolean; expectedUpdatedAt?: string }) =>
    api.post(`/lab/products/${productId}/replace-text`, data),
  getKnowledgeBases: (productId: string) =>
    api.get(`/lab/products/${productId}/knowledge`),
  getKnowledge: (productId: string, kbId?: string) =>
    kbId
      ? api.get(`/lab/products/${productId}/knowledge/${kbId}`)
      : api.get(`/lab/products/${productId}/knowledge`),
  generateKnowledge: (productId: string, data: { resumeFrom?: string; name?: string; kbId?: string; userInputs?: Record<string, string>; restart?: boolean }) =>
    api.post(`/lab/products/${productId}/knowledge/generate`, data),
  checkKnowledgeSource: (productId: string, data: { sourceName: 'marketplace_links' | 'primaryWholesaleUrl' | 'advertorial' | 'landing'; url: string }) =>
    api.post(`/lab/products/${productId}/knowledge/source-check`, data),
  regenerateKnowledgeField: (productId: string, field: string) =>
    api.post(`/lab/products/${productId}/knowledge/${field}/regenerate`, {}),
  saveKnowledgeEdits: (productId: string, edits: Record<string, string>) =>
    api.put(`/lab/products/${productId}/knowledge`, { edits }),
  deleteKnowledge: (productId: string) =>
    api.delete(`/lab/products/${productId}/knowledge`),
  discardKnowledgeDraft: (productId: string, kbId: string) =>
    api.delete(`/lab/products/${productId}/knowledge/${kbId}/draft`),
  deleteKnowledgeSection: (productId: string, fieldName: string) =>
    api.delete(`/lab/products/${productId}/knowledge/section/${encodeURIComponent(fieldName)}`),
  cleanKnowledge: (productId: string) =>
    api.post(`/lab/products/${productId}/knowledge/clean`, {}),
  stopGeneration: (productId: string) =>
    api.post(`/lab/products/${productId}/knowledge/stop`, {}),
  submitKnowledgeChoice: (productId: string, data: { fieldName: string; selectedOption?: string; cardId?: string; kbId?: string; regenerateOptions?: boolean; skipNamingProposals?: boolean }) =>
    api.post(`/lab/products/${productId}/knowledge/choice`, data),

  // Content
  getContents: (productId: string, params?: { type?: string }) =>
    api.get(`/lab/products/${productId}/content`, { params }),
  getContent: (productId: string, contentId: string) =>
    api.get(`/lab/products/${productId}/content/${contentId}`),
  getContentMissingFields: (productId: string, contentId: string) =>
    api.get<{
      data: {
        pipeline: string;
        missingFields: Array<{ name: string; title: string }>;
      };
    }>(`/lab/products/${productId}/content/${contentId}/missing-fields`),
  generateContent: (productId: string, data: { type: string; strategy?: string; structure?: string; selectedSchemas?: string[]; carouselSchema?: string; autoChain?: boolean; production?: { voiceId?: string; avatarId?: string; voiceSource?: 'heygen' | 'elevenlabs' }; resumeFrom?: string; knowledgeBaseId?: string; userInputs?: Record<string, string> }) =>
    api.post(`/lab/products/${productId}/content/generate`, data),
  resumeContent: (productId: string, contentId: string, data: { resumeFrom?: string; userInputs?: Record<string, string> }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/resume`, data),

  // Social Content Machine — strategie + planer miesięczny
  getSocialStrategies: () =>
    api.get('/lab/social-strategies'),
  getSocialChannels: (productId: string) =>
    api.get(`/lab/products/${productId}/social-channels`),
  configureSocialChannel: (productId: string, pageId: string) =>
    api.put(`/lab/products/${productId}/social-channel`, { pageId }),
  getSocialPosts: (productId: string, month?: string | null) =>
    api.get(`/lab/products/${productId}/social-posts`, { params: month ? { month } : undefined }),
  updateSocialPost: (productId: string, postId: string, data: { scheduledDate?: string; scheduledTime?: string; caption?: string | null; captionFb?: string | null; hashtags?: string | null; hook?: string | null; imagePrompt?: string | null; videoPrompt?: string | null; onImageText?: string | null }) =>
    api.patch(`/lab/products/${productId}/social-posts/${postId}`, data),
  setSocialPostApproval: (productId: string, postId: string, status: 'APPROVED' | 'REJECTED') =>
    api.post(`/lab/products/${productId}/social-posts/${postId}/approval`, { status }),
  approveSocialMonth: (productId: string, month: string) =>
    api.post(`/lab/products/${productId}/social-posts/approve-month`, { month }),
  scheduleSocialMonth: (productId: string, month: string) =>
    api.post(`/lab/products/${productId}/social-posts/schedule-month`, { month }),
  deleteSocialPost: (productId: string, postId: string) =>
    api.delete(`/lab/products/${productId}/social-posts/${postId}`),
  generateSocialMedia: (productId: string, postId: string) =>
    api.post(`/lab/products/${productId}/social-posts/${postId}/generate-media`, {}),
  generateAllSocialMedia: (productId: string, month: string) =>
    api.post(`/lab/products/${productId}/social-posts/generate-media-all`, { month }),
  getSocialPostImageUrl: (productId: string, postId: string, cacheKey?: string) =>
    `/api/lab/products/${productId}/social-posts/${postId}/image${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,

  // Reklamy statyczne — strategie + katalog schematów + kreacje
  getStaticStrategies: () =>
    api.get('/lab/static-strategies'),
  getAdCreatives: (productId: string, contentId: string) =>
    api.get(`/lab/products/${productId}/content/${contentId}/ad-creatives`),
  updateAdCreative: (productId: string, contentId: string, creativeId: string, data: { prompt?: string; onImageText?: string | null }) =>
    api.patch(`/lab/products/${productId}/content/${contentId}/ad-creatives/${creativeId}`, data),
  generateAdCreativeImage: (productId: string, contentId: string, creativeId: string) =>
    api.post(`/lab/products/${productId}/content/${contentId}/ad-creatives/${creativeId}/generate`, {}),
  generateAllAdCreativeImages: (productId: string, contentId: string) =>
    api.post(`/lab/products/${productId}/content/${contentId}/ad-creatives/generate-all`, {}),
  deleteAdCreative: (productId: string, contentId: string, creativeId: string) =>
    api.delete(`/lab/products/${productId}/content/${contentId}/ad-creatives/${creativeId}`),
  getAdCreativeImageUrl: (productId: string, contentId: string, creativeId: string, cacheKey?: string) =>
    `/api/lab/products/${productId}/content/${contentId}/ad-creatives/${creativeId}/image${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,

  // Reklamy wideo — strategie + zasoby pochodne skryptu
  getVideoStrategies: () =>
    api.get('/lab/video-strategies'),
  getVideoAssets: (productId: string, contentId: string) =>
    api.get(`/lab/products/${productId}/content/${contentId}/video-assets`),
  generateVideoAsset: (productId: string, contentId: string, data: { kind: string; instruction?: string }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/video-assets`, data),
  deleteVideoAsset: (productId: string, assetId: string) =>
    api.delete(`/lab/products/${productId}/video-assets/${assetId}`),
  updateVideoAssetScenes: (productId: string, assetId: string, scenes: Array<Record<string, unknown>>) =>
    api.put(`/lab/products/${productId}/video-assets/${assetId}/scenes`, { scenes }),
  getVideoProductionOptions: (provider: string) =>
    api.get('/lab/video-production/options', { params: { provider } }),
  retryKieShot: (productId: string, assetId: string, shotId: string) =>
    api.post(`/lab/products/${productId}/video-assets/${assetId}/kie-shots/retry`, { shotId }),

  // Bank ujęć wideo (powtarzalne klipy b-roll per produkt, image-first)
  getVideoBank: (productId: string) =>
    api.get(`/lab/products/${productId}/video-bank`),
  createVideoBankClip: (productId: string, data: { category: string; name?: string; customImagePrompt?: string; customMotionPrompt?: string; actorAvatarId?: string | null; actorSceneId?: string | null }) =>
    api.post(`/lab/products/${productId}/video-bank`, data),
  createVideoBankSeries: (productId: string, data: { actorAvatarId?: string | null; actorSceneId?: string | null }) =>
    api.post(`/lab/products/${productId}/video-bank/series`, data),
  animateVideoBankClip: (productId: string, clipId: string, data?: { modelKey?: string | null }) =>
    api.post(`/lab/products/${productId}/video-bank/${clipId}/animate`, data || {}),
  retryVideoBankImage: (productId: string, clipId: string, note?: string) =>
    api.post(`/lab/products/${productId}/video-bank/${clipId}/retry-image`, { note: note || undefined }),
  deleteVideoBankClip: (productId: string, clipId: string) =>
    api.delete(`/lab/products/${productId}/video-bank/${clipId}`),
  getVideoBankImageUrl: (productId: string, clipId: string, cacheKey?: string) =>
    `/api/lab/products/${productId}/video-bank/${clipId}/image${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,
  startVideoProduction: (productId: string, contentId: string, data: { kind: string; voiceId?: string; avatarId?: string; instruction?: string; quality?: 'test' | 'final'; scope?: 'full' | 'scenes'; sceneIds?: number[]; modelKey?: string; brollSource?: 'bank' | 'hybrid' | 'generate'; voiceSource?: 'heygen' | 'elevenlabs'; hookImageBankClipId?: string }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/video-production`, data),
  iterateVideoVariant: (productId: string, contentId: string, data: { scriptMode: 'keep' | 'paraphrase' | 'rehook'; instruction?: string; production?: { voiceId?: string; avatarId?: string } | null }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/iterate-variant`, data),
  runCreativeCampaign: (productId: string, contentId: string, data: { creatives: number; voiceId: string; avatarId?: string; voiceSource?: 'heygen' | 'elevenlabs'; quality?: 'test' | 'final' }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/campaign`, data),

  // Biblioteka głosów ElevenLabs (audio-first) + para postać+głos
  getSharedVoices: (params: { language?: string; gender?: string; age?: string; category?: string; search?: string; page?: number }) =>
    api.get('/lab/voice-library/shared', { params }),
  addSharedVoice: (data: { publicOwnerId: string; voiceId: string; name: string }) =>
    api.post('/lab/voice-library/add', data),
  setAvatarVoice: (avatarId: string, data: { voiceId: string | null; voiceName?: string | null }) =>
    api.put(`/lab/avatars/${avatarId}/voice`, data),

  // Biblioteka avatarów UGC (HeyGen photo avatar z promptu)
  getAvatars: () =>
    api.get('/lab/avatars'),
  createAvatar: (data: { name: string; gender?: string | null; ageRange?: string | null; traits?: Record<string, unknown> | null; prompt: string; isSystem?: boolean }) =>
    api.post('/lab/avatars', data),
  createAvatarScene: (avatarId: string, data: { name: string; prompt: string; referenceImageUrl?: string | null; referenceImageIds?: string[] }) =>
    api.post(`/lab/avatars/${avatarId}/scenes`, data),
  createAvatarScenesBatch: (avatarId: string, scenes: Array<{ name: string; prompt: string; referenceImageIds?: string[] }>) =>
    api.post(`/lab/avatars/${avatarId}/scenes/batch`, { scenes }),
  retryAvatar: (avatarId: string) =>
    api.post(`/lab/avatars/${avatarId}/retry`, {}),
  retryAvatarScene: (sceneId: string) =>
    api.post(`/lab/avatars/scenes/${sceneId}/retry`, {}),
  regenerateAvatar: (avatarId: string, instruction?: string) =>
    api.post(`/lab/avatars/${avatarId}/regenerate`, { instruction: instruction || undefined }),
  regenerateAvatarScene: (sceneId: string, instruction?: string) =>
    api.post(`/lab/avatars/scenes/${sceneId}/regenerate`, { instruction: instruction || undefined }),
  deleteAvatar: (avatarId: string) =>
    api.delete(`/lab/avatars/${avatarId}`),
  deleteAvatarScene: (sceneId: string) =>
    api.delete(`/lab/avatars/scenes/${sceneId}`),
  getBrollImages: (productId: string, contentId: string) =>
    api.get(`/lab/products/${productId}/content/${contentId}/broll-images`),
  createBrollImage: (productId: string, contentId: string, data: { name: string; strategyKey?: string | null; customPrompt?: string | null; actorAvatarId?: string | null; actorSceneId?: string | null }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/broll-images`, data),
  retryBrollImage: (imageId: string) =>
    api.post(`/lab/broll-images/${imageId}/retry`, {}),
  deleteBrollImage: (imageId: string) =>
    api.delete(`/lab/broll-images/${imageId}`),
  uploadAvatarReference: (image: File) => {
    const form = new FormData();
    form.append('image', image);
    return api.post('/lab/avatars/upload-reference', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  regenerateContentField: (productId: string, contentId: string, field: string) =>
    api.post(`/lab/products/${productId}/content/${contentId}/regenerate/${field}`, {}),
  saveContentEdits: (productId: string, contentId: string, edits: Record<string, string>) =>
    api.put(`/lab/products/${productId}/content/${contentId}`, { edits }),
  updateContentTitle: (productId: string, contentId: string, title: string) =>
    api.put(`/lab/products/${productId}/content/${contentId}`, { title }),
  deleteContent: (productId: string, contentId: string) =>
    api.delete(`/lab/products/${productId}/content/${contentId}`),
  submitContentChoice: (productId: string, contentId: string, data: { fieldName: string; selectedOption?: string; cardId?: string; regenerateOptions?: boolean }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/choice`, data),
  generateProductPage: (productId: string, data: { mode: 'content' | 'combo' | 'gallery'; knowledgeBaseId?: string; labContentId?: string; language?: string; styleId?: string; quality?: 'low' | 'medium' | 'high' }) =>
    api.post(`/lab/products/${productId}/product-page/generate`, data),

  // Reference Images
  getReferenceImages: (productId: string) =>
    api.get(`/lab/products/${productId}/reference-images`),
  generateReferenceImages: (productId: string, data: { knowledgeBaseId?: string; screenshots: File[]; typographyStyleId?: string; typographyStyle?: string }) => {
    const formData = new FormData();
    if (data.knowledgeBaseId) formData.append('knowledgeBaseId', data.knowledgeBaseId);
    if (data.typographyStyleId?.trim()) formData.append('typographyStyleId', data.typographyStyleId.trim());
    if (data.typographyStyle?.trim()) formData.append('typographyStyle', data.typographyStyle.trim());
    for (const screenshot of data.screenshots) {
      formData.append('screenshots', screenshot);
    }
    return api.post<{
      success: boolean;
      data: { runId: string | null; totalFields: number; targetKinds: string[] };
      message: string;
    }>(`/lab/products/${productId}/reference-images/generate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  cancelReferenceImagesGeneration: (productId: string, runId: string) =>
    api.post(`/lab/products/${productId}/reference-images/generation/${runId}/cancel`, {}),
  generateReferenceTypography: (productId: string, data: { knowledgeBaseId?: string; instruction?: string; typographyStyleId?: string; typographyStyle?: string; typographyMode?: 'style' | 'prompt' }) =>
    api.post(`/lab/products/${productId}/reference-images/typography`, data),
  generateBundleReferenceImage: (productId: string, data: { knowledgeBaseId?: string; quantity: number; custom?: boolean; instruction?: string }) =>
    api.post(`/lab/products/${productId}/reference-images/bundle`, data),
  saveProductImageStyle: (productId: string, styleId: string) =>
    api.put(`/lab/products/${productId}/reference-images/style`, { styleId }),
  uploadReferenceImage: (productId: string, data: { kind: string; image: File; quantity?: number }) => {
    const formData = new FormData();
    formData.append('kind', data.kind);
    if (data.quantity !== undefined) formData.append('quantity', String(data.quantity));
    formData.append('image', data.image);
    return api.post(`/lab/products/${productId}/reference-images/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  regenerateReferenceImage: (productId: string, imageId: string, data: { knowledgeBaseId?: string; screenshots: File[]; instruction?: string; typographyStyleId?: string; typographyStyle?: string; typographyMode?: 'style' | 'prompt' }) => {
    const formData = new FormData();
    if (data.knowledgeBaseId) formData.append('knowledgeBaseId', data.knowledgeBaseId);
    if (data.instruction?.trim()) formData.append('instruction', data.instruction.trim());
    if (data.typographyStyleId?.trim()) formData.append('typographyStyleId', data.typographyStyleId.trim());
    if (data.typographyStyle?.trim()) formData.append('typographyStyle', data.typographyStyle.trim());
    if (data.typographyMode) formData.append('typographyMode', data.typographyMode);
    for (const screenshot of data.screenshots) {
      formData.append('screenshots', screenshot);
    }
    return api.post(`/lab/products/${productId}/reference-images/${imageId}/regenerate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getReferenceImageFileUrl: (productId: string, imageId: string, cacheKey?: string) =>
    `/api/lab/products/${encodeURIComponent(productId)}/reference-images/${encodeURIComponent(imageId)}/file${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,
  deleteReferenceImage: (productId: string, imageId: string) =>
    api.delete(`/lab/products/${productId}/reference-images/${imageId}`),

  // Product Images
  getProductImages: (productId: string, params?: { labContentId?: string }) =>
    api.get(`/lab/products/${productId}/product-images`, { params }),
  prepareProductImageSlots: (productId: string, labContentId: string) =>
    api.post(`/lab/products/${productId}/product-images/slots`, { labContentId }),
  uploadProductImageSlot: (productId: string, imageId: string, image: File) => {
    const body = new FormData();
    body.append('image', image);
    return api.post(`/lab/products/${productId}/product-images/${imageId}/upload`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  generateProductImages: (productId: string, data: { knowledgeBaseId?: string; labContentId: string; language?: string; styleId?: string; quality?: 'low' | 'medium' | 'high'; regenerateImageIds?: string[] }) =>
    api.post(`/lab/products/${productId}/product-images/generate`, data),
  regenerateProductImage: (productId: string, imageId: string, data: { prompt: string; quality?: 'low' | 'medium' | 'high' }) =>
    api.post(`/lab/products/${productId}/product-images/${imageId}/regenerate`, data),
  saveProductImagePrompt: (productId: string, imageId: string, data: { prompt: string }) =>
    api.patch(`/lab/products/${productId}/product-images/${imageId}/prompt`, data),
  editProductImage: (productId: string, imageId: string, data: { instruction: string; reference?: File; editMode?: 'local' | 'reference_rebuild'; quality?: 'low' | 'medium' | 'high' }) => {
    const body = new FormData();
    body.append('instruction', data.instruction);
    if (data.quality) body.append('quality', data.quality);
    if (data.editMode) body.append('editMode', data.editMode);
    if (data.reference) body.append('reference', data.reference);
    return api.post(`/lab/products/${productId}/product-images/${imageId}/edit`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  optimizeProductImagesForWp: (productId: string, data: { integrationId: string; labContentId?: string }) =>
    api.post(`/lab/products/${productId}/product-images/optimize-wp`, data || {}),
  getProductImageReferenceUrl: (productId: string, imageId: string, index: number, sha256: string) => `/api/lab/products/${productId}/product-images/${imageId}/references/${index}?sha256=${encodeURIComponent(sha256)}`,
  getProductImageFileUrl: (productId: string, imageId: string, cacheKey?: string) =>
    `/api/lab/products/${encodeURIComponent(productId)}/product-images/${encodeURIComponent(imageId)}/file${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,
  deleteProductImage: (productId: string, imageId: string) =>
    api.delete(`/lab/products/${productId}/product-images/${imageId}`),

  // Competitors
  getCompetitors: (productId: string) =>
    api.get(`/lab/products/${productId}/competitors`),
  analyzeCompetitor: (productId: string, url: string) =>
    api.post(`/lab/products/${productId}/competitors/analyze`, { url }),
  createCompetitor: (productId: string, data: Record<string, unknown>) =>
    api.post(`/lab/products/${productId}/competitors`, data),
  updateCompetitor: (productId: string, cId: string, data: Record<string, unknown>) =>
    api.put(`/lab/products/${productId}/competitors/${cId}`, data),
  deleteCompetitor: (productId: string, cId: string) =>
    api.delete(`/lab/products/${productId}/competitors/${cId}`),

  // Suppliers
  getSuppliers: (productId: string) =>
    api.get(`/lab/products/${productId}/suppliers`),
  createSupplier: (productId: string, data: Record<string, unknown>) =>
    api.post(`/lab/products/${productId}/suppliers`, data),
  updateSupplier: (productId: string, sId: string, data: Record<string, unknown>) =>
    api.put(`/lab/products/${productId}/suppliers/${sId}`, data),
  deleteSupplier: (productId: string, sId: string) =>
    api.delete(`/lab/products/${productId}/suppliers/${sId}`),

  // Pipeline Config (read-only for all users)
  getPipeline: (pipeline: string) =>
    api.get(`/lab/pipelines/${pipeline}`),

  // Scraping
  startScrape: (productId: string, data: { url: string; type?: string }) =>
    api.post(`/lab/products/${productId}/scrape`, data),
  getScrapeJobs: (productId: string) =>
    api.get(`/lab/products/${productId}/scrape-jobs`),

  // Assets
  getAssets: (productId: string, params?: { type?: string; status?: string; folder?: string; tag?: string }) =>
    api.get(`/lab/products/${productId}/assets`, { params }),
  updateAsset: (productId: string, assetId: string, data: Record<string, unknown>) =>
    api.put(`/lab/products/${productId}/assets/${assetId}`, data),
  deleteAsset: (productId: string, assetId: string) =>
    api.delete(`/lab/products/${productId}/assets/${assetId}`),
  createAssetsFromContent: (productId: string, contentId: string) =>
    api.post(`/lab/products/${productId}/assets/from-content/${contentId}`, {}),
  generateBuilderPageFromContent: (productId: string, contentId: string, data: {
    integrationId: string;
    status: 'DRAFT' | 'PUBLISHED';
    title?: string;
    slug?: string;
    compressImages?: boolean;
  }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/generate-builder-page`, data),
  updateBuilderPageFromContent: (productId: string, contentId: string, data: {
    integrationId?: string;
    scope: 'all' | 'content' | 'graphics';
    compressImages?: boolean;
  }) =>
    api.post(`/lab/products/${productId}/content/${contentId}/update-builder-page`, data),

  // Batch Generation
  startBatch: (productId: string, data: { contentTypes: string[]; variantCount?: number; stylePresets?: string[]; knowledgeBaseId?: string; videoStrategies?: string[] }) =>
    api.post(`/lab/products/${productId}/batch/generate`, data),
  stopBatch: (productId: string, batchId: string) =>
    api.post(`/lab/products/${productId}/batch/${batchId}/stop`, {}),

  // Research
  getResearch: (productId: string) =>
    api.get(`/lab/products/${productId}/research`),
  deleteResearch: (productId: string, reportId: string) =>
    api.delete(`/lab/products/${productId}/research/${reportId}`),

  // Condensed Summary (hero description)
  generateCondensedSummary: (productId: string) =>
    api.post(`/lab/products/${productId}/generate-summary`, {}),

  // Generate All
  generateAll: (productId: string, data: { runResearch?: boolean; contentTypes?: string[]; variantsPerType?: number; stylePresets?: string[]; videoStrategies?: string[] }) =>
    api.post(`/lab/products/${productId}/generate-all`, data),

  // Admin — Pipeline Config
  getPipelines: () =>
    api.get('/admin/lab/pipelines'),
  getAdminPipeline: (pipeline: string) =>
    api.get(`/admin/lab/pipelines/${pipeline}`),
  savePipeline: (pipeline: string, data: {
    items: unknown[];
    builderPage?: {
      [key: string]: unknown;
      pageType: 'ADVERTORIAL' | 'STANDALONE' | 'PRODUCT_PAGE' | 'QUIZ';
      templateId?: string | null;
      templateName?: string | null;
      templateContent?: unknown[] | null;
      templateUploadedAt?: string | null;
      productTitleTemplate?: string | null;
      productGalleryShortcodes?: string[] | null;
      productShortDescriptionTemplate?: string | null;
      productShortDescriptionBuilder?: unknown[] | null;
      productAdditionalElements?: unknown[] | null;
    } | null;
    inputPanelTitle?: string | null;
    inputPanelDescription?: string | null;
    ctaLabel?: string | null;
    backLabel?: string | null;
  }) =>
    api.put(`/admin/lab/pipelines/${pipeline}`, data),
  resetPipeline: (pipeline: string) =>
    api.delete(`/admin/lab/pipelines/${pipeline}`),
  getLogs: (params?: Record<string, unknown>) =>
    api.get('/admin/lab/logs', { params }),
  getLogStats: () =>
    api.get('/admin/lab/logs/stats'),
  getAiCostReport: (params?: { days?: number; limit?: number }) =>
    api.get('/admin/lab/ai-costs', { params }),
  getAiCostDetails: (params: { targetId: string; pipeline: string; days?: number }) =>
    api.get('/admin/lab/ai-costs/details', { params }),
};

// ─── Page Builder v2 API ──────────────────────────────────────────────────────

export const pageBuilderApi = {
  // Pages
  listPages: (integrationId: string, params?: Record<string, string>) =>
    api.get(`/page-builder/${integrationId}/pages`, { params }),
  getPage: (integrationId: string, pageId: string) =>
    api.get(`/page-builder/${integrationId}/pages/${pageId}`),
  createPage: (integrationId: string, data: { title: string; slug: string; pageType: string }) =>
    api.post(`/page-builder/${integrationId}/pages`, data),
  updatePage: (integrationId: string, pageId: string, data: Record<string, unknown>) =>
    api.put(`/page-builder/${integrationId}/pages/${pageId}`, data),
  deletePage: (integrationId: string, pageId: string) =>
    api.delete(`/page-builder/${integrationId}/pages/${pageId}`),
  restorePage: (integrationId: string, pageId: string) =>
    api.post(`/page-builder/${integrationId}/pages/${pageId}/restore`),
  duplicatePage: (integrationId: string, pageId: string) =>
    api.post(`/page-builder/${integrationId}/pages/${pageId}/duplicate`),

  // Content
  getPageContent: (integrationId: string, pageId: string) =>
    api.get(`/page-builder/${integrationId}/pages/${pageId}/content`),
  savePageContent: (integrationId: string, pageId: string, content: unknown[]) =>
    api.put(`/page-builder/${integrationId}/pages/${pageId}/content`, { content }),
  autosave: (integrationId: string, pageId: string, content: unknown[]) =>
    api.post(`/page-builder/${integrationId}/pages/${pageId}/autosave`, { content }),

  // Versions
  listVersions: (integrationId: string, pageId: string) =>
    api.get(`/page-builder/${integrationId}/pages/${pageId}/versions`),
  restoreVersion: (integrationId: string, pageId: string, versionId: string) =>
    api.post(`/page-builder/${integrationId}/pages/${pageId}/versions/${versionId}/restore`),

  // Publish
  publish: (integrationId: string, pageId: string, data?: { content: unknown[]; globalCss: string; globalJs: string }) =>
    api.post(`/page-builder/${integrationId}/pages/${pageId}/publish`, data),
  schedule: (integrationId: string, pageId: string, scheduledAt: string) =>
    api.post(`/page-builder/${integrationId}/pages/${pageId}/schedule`, { scheduledAt }),

  // Templates
  listTemplates: (integrationId: string, scope?: string) =>
    api.get(`/page-builder/${integrationId}/templates`, { params: scope ? { scope } : {} }),
  createTemplate: (integrationId: string, data: { name: string; category: string; scope: string; content: unknown[]; thumbnail?: string }) =>
    api.post(`/page-builder/${integrationId}/templates`, data),
  deleteTemplate: (integrationId: string, templateId: string) =>
    api.delete(`/page-builder/${integrationId}/templates/${templateId}`),

  // Product page (get or create)
  getOrCreateProductPage: (integrationId: string, wcProductId: number, productName: string, wcProductStatus?: string) =>
    api.post(`/page-builder/${integrationId}/product-page/${wcProductId}`, { productName, wcProductStatus }),

  // Footer page (get or create) — each mode has its own independent project.
  // Default mode is seeded with the EcomFlow Footer template; blank mode
  // starts as an empty canvas.
  getOrCreateFooterPage: (integrationId: string, mode: 'default' | 'blank' = 'default') =>
    api.post<{ id: string; title: string; slug: string }>(`/page-builder/${integrationId}/footer-page`, { mode }),

  // Restore the "EcomFlow Footer" (default mode) back to its factory layout.
  // Blank mode is untouched — the user's custom design is preserved.
  resetFooterPage: (integrationId: string) =>
    api.post<{ id: string; title: string; slug: string }>(`/page-builder/${integrationId}/footer-page/reset`),

  // Migration
  migrateLegacy: (integrationId: string) =>
    api.post(`/page-builder/${integrationId}/migrate-legacy`),

  // AI Content Generation
  generateAiContent: (integrationId: string, data: {
    provider?: string;
    elementType: string;
    context?: string;
    productName?: string;
    productDescription?: string;
    tone?: string;
    language?: string;
  }) => api.post(`/page-builder/${integrationId}/ai/generate-content`, data),
};

// ─── Quiz Funnel API ────────────────────────────────────────────────────────

export const quizApi = {
  list: (integrationId: string, search?: string, status?: string) => api.get(`/quizzes/${integrationId}/quizzes`, { params: { search, status } }),
  get: (integrationId: string, quizId: string) => api.get(`/quizzes/${integrationId}/quizzes/${quizId}`),
  create: (integrationId: string, data: { title: string; slug?: string; document?: unknown }) => api.post(`/quizzes/${integrationId}/quizzes`, data),
  update: (integrationId: string, quizId: string, data: Record<string, unknown>) => api.put(`/quizzes/${integrationId}/quizzes/${quizId}`, data),
  saveDocument: (integrationId: string, quizId: string, document: unknown) => api.put(`/quizzes/${integrationId}/quizzes/${quizId}/document`, document),
  publish: (integrationId: string, quizId: string) => api.post(`/quizzes/${integrationId}/quizzes/${quizId}/publish`),
  duplicate: (integrationId: string, quizId: string) => api.post(`/quizzes/${integrationId}/quizzes/${quizId}/duplicate`),
  delete: (integrationId: string, quizId: string) => api.delete(`/quizzes/${integrationId}/quizzes/${quizId}`),
  restore: (integrationId: string, quizId: string) => api.post(`/quizzes/${integrationId}/quizzes/${quizId}/restore`),
  versions: (integrationId: string, quizId: string) => api.get(`/quizzes/${integrationId}/quizzes/${quizId}/versions`),
  restoreVersion: (integrationId: string, quizId: string, versionId: string) => api.post(`/quizzes/${integrationId}/quizzes/${quizId}/versions/${versionId}/restore`),
  analytics: (integrationId: string, quizId: string, params?: Record<string, string>) => api.get(`/quizzes/${integrationId}/quizzes/${quizId}/analytics`, { params }),
  leads: (integrationId: string, quizId: string) => api.get(`/quizzes/${integrationId}/quizzes/${quizId}/leads`),
};

// ─── Funnel API ─────────────────────────────────────────────────────────────

export const funnelApi = {
  // Funnels
  listFunnels: (integrationId: string) =>
    api.get(`/funnels/${integrationId}/funnels`),
  getFunnel: (integrationId: string, funnelId: string) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}`),
  createFunnel: (integrationId: string, data: { name: string; slug: string; description?: string }) =>
    api.post(`/funnels/${integrationId}/funnels`, data),
  updateFunnel: (integrationId: string, funnelId: string, data: Record<string, unknown>) =>
    api.put(`/funnels/${integrationId}/funnels/${funnelId}`, data),
  deleteFunnel: (integrationId: string, funnelId: string) =>
    api.delete(`/funnels/${integrationId}/funnels/${funnelId}`),
  duplicateFunnel: (integrationId: string, funnelId: string) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/duplicate`),

  // Steps
  addStep: (integrationId: string, funnelId: string, data: Record<string, unknown>) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/steps`, data),
  updateStep: (integrationId: string, funnelId: string, stepId: string, data: Record<string, unknown>) =>
    api.put(`/funnels/${integrationId}/funnels/${funnelId}/steps/${stepId}`, data),
  deleteStep: (integrationId: string, funnelId: string, stepId: string) =>
    api.delete(`/funnels/${integrationId}/funnels/${funnelId}/steps/${stepId}`),
  reorderSteps: (integrationId: string, funnelId: string, stepIds: string[]) =>
    api.put(`/funnels/${integrationId}/funnels/${funnelId}/steps/reorder`, { stepIds }),
  duplicateStep: (integrationId: string, funnelId: string, stepId: string) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/steps/${stepId}/duplicate`),

  // Variants
  createVariant: (integrationId: string, funnelId: string, stepId: string, data: Record<string, unknown>) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/steps/${stepId}/variants`, data),
  updateVariant: (integrationId: string, funnelId: string, stepId: string, variantId: string, data: Record<string, unknown>) =>
    api.put(`/funnels/${integrationId}/funnels/${funnelId}/steps/${stepId}/variants/${variantId}`, data),
  deleteVariant: (integrationId: string, funnelId: string, stepId: string, variantId: string) =>
    api.delete(`/funnels/${integrationId}/funnels/${funnelId}/steps/${stepId}/variants/${variantId}`),

  // Stats
  getStats: (integrationId: string, funnelId: string, params?: Record<string, string>) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}/stats`, { params }),
  getStepStats: (integrationId: string, funnelId: string, params?: Record<string, string>) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}/stats/steps`, { params }),
  resetStats: (integrationId: string, funnelId: string) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/stats/reset`),

  // A/B Tests
  listABTests: (integrationId: string, funnelId: string) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests`),
  getABTest: (integrationId: string, funnelId: string, testId: string) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}`),
  getABPlanning: (integrationId: string, funnelId: string, stepId: string, goal: string) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}/ab-planning`, { params: { stepId, goal } }),
  getABTestReport: (integrationId: string, funnelId: string, testId: string, params?: Record<string, string>) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/report`, { params }),
  getABTestTimeline: (integrationId: string, funnelId: string, testId: string, params?: Record<string, string>) =>
    api.get(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/timeline`, { params }),
  createABTest: (integrationId: string, funnelId: string, data: Record<string, unknown>) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests`, data),
  updateABTest: (integrationId: string, funnelId: string, testId: string, data: Record<string, unknown>) =>
    api.put(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}`, data),
  createABTestInsight: (integrationId: string, funnelId: string, testId: string, data: { content: string }) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/insights`, data),
  updateABTestInsight: (integrationId: string, funnelId: string, testId: string, insightId: string, data: { content: string }) =>
    api.put(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/insights/${insightId}`, data),
  deleteABTestInsight: (integrationId: string, funnelId: string, testId: string, insightId: string) =>
    api.delete(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/insights/${insightId}`),
  startABTest: (integrationId: string, funnelId: string, testId: string) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/start`),
  pauseABTest: (integrationId: string, funnelId: string, testId: string) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/pause`),
  endABTest: (integrationId: string, funnelId: string, testId: string) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/end`),
  chooseABTestWinner: (integrationId: string, funnelId: string, testId: string, winnerVariantId: string | null) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/winner`, { winnerVariantId }),
  deleteABTest: (integrationId: string, funnelId: string, testId: string) =>
    api.delete(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}`),
  duplicateABTest: (integrationId: string, funnelId: string, testId: string) =>
    api.post(`/funnels/${integrationId}/funnels/${funnelId}/ab-tests/${testId}/duplicate`),
};

export const bundleApi = {
  exportBundle: (integrationId: string, bundleId: string) =>
    api.get(`/bundles/${integrationId}/bundles/${bundleId}/export`),
  importBundle: (integrationId: string, bundleId: string, data: unknown) =>
    api.post(`/bundles/${integrationId}/bundles/${bundleId}/import`, data),
  // Bundles
  listBundles: (integrationId: string) =>
    api.get(`/bundles/${integrationId}/bundles`),
  getBundle: (integrationId: string, bundleId: string) =>
    api.get(`/bundles/${integrationId}/bundles/${bundleId}`),
  createBundle: (integrationId: string, data: { name: string; slug: string }) =>
    api.post(`/bundles/${integrationId}/bundles`, data),
  updateBundle: (integrationId: string, bundleId: string, data: Record<string, unknown>) =>
    api.put(`/bundles/${integrationId}/bundles/${bundleId}`, data),
  deleteBundle: (integrationId: string, bundleId: string) =>
    api.delete(`/bundles/${integrationId}/bundles/${bundleId}`),
  duplicateBundle: (integrationId: string, bundleId: string) =>
    api.post(`/bundles/${integrationId}/bundles/${bundleId}/duplicate`),
  reorderBundles: (integrationId: string, ids: string[]) =>
    api.put(`/bundles/${integrationId}/bundles/reorder`, { ids }),

  // Packages (tiers)
  addPackage: (integrationId: string, bundleId: string, data: Record<string, unknown>) =>
    api.post(`/bundles/${integrationId}/bundles/${bundleId}/packages`, data),
  updatePackage: (integrationId: string, bundleId: string, packageId: string, data: Record<string, unknown>) =>
    api.put(`/bundles/${integrationId}/bundles/${bundleId}/packages/${packageId}`, data),
  deletePackage: (integrationId: string, bundleId: string, packageId: string) =>
    api.delete(`/bundles/${integrationId}/bundles/${bundleId}/packages/${packageId}`),
  reorderPackages: (integrationId: string, bundleId: string, ids: string[]) =>
    api.put(`/bundles/${integrationId}/bundles/${bundleId}/packages/reorder`, { ids }),

  // Settings
  getSettings: (integrationId: string) =>
    api.get(`/bundles/${integrationId}/bundle-settings`),
  updateSettings: (integrationId: string, settings: Record<string, unknown>) =>
    api.put(`/bundles/${integrationId}/bundle-settings`, { settings }),
  resetSettingsToDefaults: (integrationId: string) =>
    api.post(`/bundles/${integrationId}/bundle-settings/reset-to-defaults`),
};

export const counterApi = {
  listCounters: (integrationId: string) =>
    api.get(`/counters/${integrationId}/counters`),
  getCounter: (integrationId: string, counterId: string) =>
    api.get(`/counters/${integrationId}/counters/${counterId}`),
  createCounter: (integrationId: string, data: { name: string; slug: string }) =>
    api.post(`/counters/${integrationId}/counters`, data),
  updateCounter: (integrationId: string, counterId: string, data: Record<string, unknown>) =>
    api.put(`/counters/${integrationId}/counters/${counterId}`, data),
  deleteCounter: (integrationId: string, counterId: string) =>
    api.delete(`/counters/${integrationId}/counters/${counterId}`),
  duplicateCounter: (integrationId: string, counterId: string) =>
    api.post(`/counters/${integrationId}/counters/${counterId}/duplicate`),
  reorderCounters: (integrationId: string, ids: string[]) =>
    api.put(`/counters/${integrationId}/counters/reorder`, { ids }),
  resetCounter: (integrationId: string, counterId: string) =>
    api.post(`/counters/${integrationId}/counters/${counterId}/reset`),
};

export interface MarketingBrandSummary {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  stores: Array<{
    id: string;
    name: string;
    url: string;
    isActive: boolean;
  }>;
}

export type MarketingMessageStatus = 'DRAFT' | 'ARCHIVED';
export type MarketingMessageMode = 'BLOCKS' | 'PLAIN_TEXT';
export type MarketingMessageAlignment = 'LEFT' | 'CENTER' | 'RIGHT';

export type MarketingMessageBlock =
  | { id: string; type: 'HEADING'; content: string; level: 1 | 2 | 3; align: MarketingMessageAlignment }
  | { id: string; type: 'PARAGRAPH'; content: string; align: MarketingMessageAlignment }
  | { id: string; type: 'BUTTON'; label: string; url: string; align: MarketingMessageAlignment }
  | { id: string; type: 'IMAGE'; url: string; alt: string; href?: string; widthPercent: number; align: MarketingMessageAlignment }
  | { id: string; type: 'DIVIDER'; style: 'SOLID' | 'DASHED' }
  | { id: string; type: 'SPACER'; height: number };

export type MarketingMessageDocument =
  | {
      schemaVersion: 1;
      mode: 'BLOCKS';
      subject: string;
      preheader: string;
      blocks: MarketingMessageBlock[];
    }
  | {
      schemaVersion: 1;
      mode: 'PLAIN_TEXT';
      subject: string;
      preheader: string;
      plainText: string;
    };

export interface MarketingMessageSummary {
  id: string;
  marketingBrandId: string;
  name: string;
  description: string | null;
  status: MarketingMessageStatus;
  latestVersionNumber: number;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingMessageVersionSummary {
  id: string;
  versionNumber: number;
  sourceVersionNumber: number | null;
  schemaVersion: number;
  mode: MarketingMessageMode;
  semanticHash: string;
  document: MarketingMessageDocument;
  createdByUserId: string;
  createdAt: string;
  renderedArtifact: {
    id: string;
    rendererVersion: string;
    sourceSemanticHash: string;
    mjmlHash: string;
    htmlHash: string;
    htmlBytes: number;
    textHash: string;
    textBytes: number;
    warnings: MarketingMessageRenderWarning[];
    createdAt: string;
  } | null;
}

export interface MarketingMessageRenderWarning {
  code: 'HTML_SIZE_WARNING';
  message: string;
}

export interface MarketingMessagePreview {
  rendererVersion: string;
  sourceSemanticHash: string;
  mjmlHash: string;
  htmlHash: string;
  htmlBytes: number;
  text: string;
  textHash: string;
  textBytes: number;
  warnings: MarketingMessageRenderWarning[];
  subject: string;
  preheader: string;
  previewHtml: string;
}

export interface MarketingMessageDetail extends MarketingMessageSummary {
  createdByUserId: string;
  versions: MarketingMessageVersionSummary[];
}

export type MarketingSegmentRule =
  | { field: 'EMAIL'; operator: 'IS_SET' | 'IS_NOT_SET' }
  | { field: 'EMAIL_CONSENT'; operator: 'IS'; value: 'GRANTED' | 'WITHDRAWN' | 'MISSING' }
  | { field: 'EMAIL_PREFERENCE'; operator: 'IS'; value: 'ENABLED' | 'DISABLED' | 'DEFAULT' }
  | { field: 'ACQUISITION_TYPE'; operator: 'IS' | 'IS_NOT'; value: 'ORDER' | 'FORM' | 'QUIZ' | 'CSV_IMPORT' | 'API' | 'MANUAL' }
  | { field: 'CONTACT_DATE'; dateField: 'FIRST_SEEN_AT' | 'LAST_SEEN_AT'; operator: 'BEFORE' | 'AFTER'; value: string }
  | { field: 'PURCHASE_HISTORY'; operator: 'HAS_PAID_ORDER' | 'NO_PAID_ORDER' };

export interface MarketingSegmentDefinition {
  schemaVersion: 1;
  operator: 'ALL' | 'ANY';
  rules: MarketingSegmentRule[];
}

export type MarketingSegmentStatus = 'DRAFT' | 'ARCHIVED';

export interface MarketingSegmentSummary {
  id: string;
  marketingBrandId: string;
  name: string;
  description: string | null;
  status: MarketingSegmentStatus;
  latestVersionNumber: number;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingSegmentVersionSummary {
  id: string;
  versionNumber: number;
  sourceVersionNumber: number | null;
  schemaVersion: number;
  semanticHash: string;
  definition: MarketingSegmentDefinition;
  createdByUserId: string;
  createdAt: string;
}

export interface MarketingSegmentDetail extends MarketingSegmentSummary {
  createdByUserId: string;
  versions: MarketingSegmentVersionSummary[];
}

export type MarketingAudienceEligibilityReason =
  | 'EMAIL_IDENTITY_MISSING'
  | 'CONTACT_NOT_ACTIVE'
  | 'CONSENT_MISSING'
  | 'CONSENT_WITHDRAWN'
  | 'PREFERENCE_DISABLED'
  | 'SCOPED_SUPPRESSION'
  | 'LEGACY_SUPPRESSION';

export interface MarketingSegmentPreview {
  schemaVersion: 1;
  definitionHash: string;
  evaluatedAt: string;
  totalContacts: number;
  matchedContacts: number;
  contactsWithEmail: number;
  contactsWithConsent: number;
  sampleLimit: number;
  sampleTruncated: boolean;
  items: Array<{
    id: string;
    primaryEmail: string | null;
    firstName: string | null;
    lastName: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    consentStatus: 'GRANTED' | 'WITHDRAWN' | 'MISSING';
    consentEffectiveAt: string | null;
    preferenceStatus: 'ENABLED' | 'DISABLED' | 'DEFAULT';
    locale: string | null;
    timezone: string | null;
    acquisitionTypes: Array<'ORDER' | 'FORM' | 'QUIZ' | 'CSV_IMPORT' | 'API' | 'MANUAL'>;
    paidOrderCount: number;
    eligible: boolean;
    reasonCodes: MarketingAudienceEligibilityReason[];
  }>;
}

export interface MarketingTestRecipient {
  id: string;
  marketingBrandId: string;
  normalizedEmail: string;
  label: string | null;
  isActive: boolean;
  ownershipConfirmedAt: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export type MarketingBroadcastStatus = 'DRAFT' | 'SCHEDULED' | 'CANCELLED' | 'COMPLETED' | 'FAILED';
export type MarketingBroadcastScheduleAction = 'SCHEDULED' | 'RESCHEDULED' | 'CANCELLED';

export interface MarketingPreflightDecisionSummary {
  id: string;
  passed: boolean;
  reasonCodes: string[];
  warningCodes: string[];
  complianceRuleVersion: string;
  inputHash: string;
  decidedAt: string;
}

export interface MarketingBroadcastScheduleInput {
  name: string;
  description?: string | null;
  messageId: string;
  messageVersionNumber: number;
  segmentId: string;
  segmentVersionNumber: number;
  scheduledFor: string;
  timezone: string;
}

export interface MarketingBroadcastVersion {
  id: string;
  versionNumber: number;
  sourceVersionNumber: number | null;
  messageId: string;
  messageVersionNumber: number;
  messageName: string;
  messageSubject: string;
  segmentId: string;
  segmentVersionNumber: number;
  segmentName: string;
  scheduledFor: string;
  timezone: string;
  semanticHash: string;
  createdByUserId: string;
  createdAt: string;
}

export interface MarketingBroadcastScheduleEvent {
  id: string;
  versionNumber: number;
  previousVersionNumber: number | null;
  action: MarketingBroadcastScheduleAction;
  scheduledFor: string;
  matchedContacts: number | null;
  contactsWithEmail: number | null;
  contactsWithConsent: number | null;
  audienceEvaluatedAt: string | null;
  reason: string | null;
  preflightDecision: MarketingPreflightDecisionSummary | null;
  actorUserId: string;
  createdAt: string;
}

export interface MarketingBroadcast {
  id: string;
  marketingBrandId: string;
  name: string;
  description: string | null;
  status: MarketingBroadcastStatus;
  latestVersionNumber: number;
  scheduledVersionNumber: number | null;
  scheduledFor: string | null;
  scheduledAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  scheduledVersion: MarketingBroadcastVersion | null;
  versions: MarketingBroadcastVersion[];
  scheduleEvents: MarketingBroadcastScheduleEvent[];
}

export interface MarketingFlowSimulationResult {
  schemaVersion: 1;
  semanticHash: string;
  status: 'COMPLETED' | 'TRIGGER_NOT_MATCHED' | 'STEP_LIMIT_REACHED';
  completedAt: string;
  terminalNodeId: string | null;
  goalKey: string | null;
  steps: Array<{
    sequence: number;
    nodeId: string;
    nodeType: string;
    enteredAt: string;
    exitedAt: string;
    selectedPort?: string;
    decision?: Record<string, unknown>;
    effect?: { type: string; [key: string]: unknown };
  }>;
  effects: Array<{ type: string; [key: string]: unknown }>;
  finalContactProperties: Record<string, unknown>;
  loopExecutions: Record<string, number>;
}

export type MarketingFlowLifecycleStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
export type MarketingFlowActiveRunPolicy = 'KEEP_EXISTING' | 'CANCEL_EXISTING' | 'MIGRATE_COMPATIBLE';

export interface MarketingFlowSummary {
  id: string;
  marketingBrandId: string;
  name: string;
  description: string | null;
  status: MarketingFlowLifecycleStatus;
  latestVersionNumber: number;
  publishedVersionNumber: number | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  _count: { runs: number };
}

export interface MarketingFlowVersionSummary {
  id: string;
  versionNumber: number;
  sourceVersionNumber: number | null;
  schemaVersion: number;
  semanticHash: string;
  graph: unknown;
  createdByUserId: string;
  createdAt: string;
}

export interface MarketingFlowDetail extends MarketingFlowSummary {
  versions: MarketingFlowVersionSummary[];
  publications: Array<{
    id: string;
    versionNumber: number;
    previousVersionNumber: number | null;
    activeRunPolicy: MarketingFlowActiveRunPolicy;
    activeRunCount: number;
    keptRunCount: number;
    cancelledRunCount: number;
    migratedRunCount: number;
    preflightDecision: MarketingPreflightDecisionSummary | null;
    actorUserId: string;
    createdAt: string;
  }>;
}

export type MarketingSequenceIntakeMode = 'GOAL' | 'BLUEPRINT';
export type MarketingSequenceIntakeStatus = 'DRAFT' | 'ARCHIVED';
export type MarketingSequenceIntakeAnswer = string | number;

export interface MarketingSequenceIntakeQuestion {
  key: string;
  label: string;
  description: string;
  type: 'SHORT_TEXT' | 'LONG_TEXT' | 'SINGLE_SELECT' | 'NUMBER';
  required: boolean;
  placeholder?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  options?: Array<{ value: string; label: string }>;
}

export interface MarketingSequenceIntakeChoice {
  key: string;
  label: string;
  description: string;
  questions: MarketingSequenceIntakeQuestion[];
}

export interface MarketingSequenceIntakeCatalog {
  schemaVersion: number;
  definitionVersion: string;
  modes: Array<{
    mode: MarketingSequenceIntakeMode;
    label: string;
    description: string;
    choices: MarketingSequenceIntakeChoice[];
  }>;
  eligibleLabProducts: Array<{
    id: string;
    name: string;
    status: string;
    storeId: string;
    knowledgeBaseCount: number;
    hasCompleteKnowledgeBase: boolean;
  }>;
}

export interface MarketingSequenceIntakeVersion {
  id: string;
  versionNumber: number;
  sourceVersionNumber: number | null;
  schemaVersion: number;
  definitionVersion: string;
  mode: MarketingSequenceIntakeMode;
  selectionKey: string;
  labProductId: string | null;
  semanticHash: string;
  brief: {
    schemaVersion: number;
    definitionVersion: string;
    mode: MarketingSequenceIntakeMode;
    selectionKey: string;
    questions: MarketingSequenceIntakeQuestion[];
    answers: Record<string, MarketingSequenceIntakeAnswer>;
  };
  createdByUserId: string;
  createdAt: string;
}

export interface MarketingSequenceIntakeSummary {
  id: string;
  marketingBrandId: string;
  name: string;
  status: MarketingSequenceIntakeStatus;
  latestVersionNumber: number;
  createdAt: string;
  updatedAt: string;
  versions: MarketingSequenceIntakeVersion[];
}

export type MarketingSequenceIntakeDetail = MarketingSequenceIntakeSummary;

export interface MarketingSequenceIntakeInput {
  mode: MarketingSequenceIntakeMode;
  selectionKey: string;
  labProductId?: string | null;
  answers: Record<string, MarketingSequenceIntakeAnswer>;
}

export interface MarketingFlowPublishResult {
  changed: boolean;
  versionNumber: number;
  migration: {
    activeRunCount: number;
    keptRunCount: number;
    cancelledRunCount: number;
    migratedRunCount: number;
  } | null;
  preflight: {
    passed: boolean;
    reasonCodes: string[];
    warningCodes: string[];
    complianceRuleVersion: string;
    inputHash: string;
    decisionId: string;
    decidedAt: string;
  } | null;
  flow: MarketingFlowDetail;
}

export const marketingApi = {
  listBrands: () => api.get<MarketingBrandSummary[]>('/marketing/brands'),
  listMessages: (marketingBrandId: string) =>
    api.get<MarketingMessageSummary[]>(`/marketing/brands/${marketingBrandId}/messages`),
  getMessage: (marketingBrandId: string, messageId: string) =>
    api.get<MarketingMessageDetail>(`/marketing/brands/${marketingBrandId}/messages/${messageId}`),
  createMessage: (
    marketingBrandId: string,
    data: { name: string; description?: string | null; document: MarketingMessageDocument },
  ) => api.post<MarketingMessageDetail>(`/marketing/brands/${marketingBrandId}/messages`, data),
  previewMessage: (
    marketingBrandId: string,
    document: MarketingMessageDocument,
  ) => api.post<MarketingMessagePreview>(`/marketing/brands/${marketingBrandId}/messages/preview`, { document }),
  createMessageDraftVersion: (
    marketingBrandId: string,
    messageId: string,
    data: { document: MarketingMessageDocument; expectedLatestVersionNumber: number },
  ) => api.post<MarketingMessageDetail>(`/marketing/brands/${marketingBrandId}/messages/${messageId}/draft-versions`, data),
  listSegments: (marketingBrandId: string) =>
    api.get<MarketingSegmentSummary[]>(`/marketing/brands/${marketingBrandId}/segments`),
  getSegment: (marketingBrandId: string, segmentId: string) =>
    api.get<MarketingSegmentDetail>(`/marketing/brands/${marketingBrandId}/segments/${segmentId}`),
  createSegment: (
    marketingBrandId: string,
    data: { name: string; description?: string | null; definition: MarketingSegmentDefinition },
  ) => api.post<MarketingSegmentDetail>(`/marketing/brands/${marketingBrandId}/segments`, data),
  createSegmentDraftVersion: (
    marketingBrandId: string,
    segmentId: string,
    data: { definition: MarketingSegmentDefinition; expectedLatestVersionNumber: number },
  ) => api.post<MarketingSegmentDetail>(`/marketing/brands/${marketingBrandId}/segments/${segmentId}/draft-versions`, data),
  previewSegment: (
    marketingBrandId: string,
    definition: MarketingSegmentDefinition,
    sampleLimit = 25,
  ) => api.post<MarketingSegmentPreview>(`/marketing/brands/${marketingBrandId}/segments/preview`, { definition, sampleLimit }),
  listTestRecipients: (marketingBrandId: string) =>
    api.get<MarketingTestRecipient[]>(`/marketing/brands/${marketingBrandId}/test-recipients`),
  addTestRecipient: (
    marketingBrandId: string,
    data: { email: string; label?: string | null; ownershipConfirmed: true },
  ) => api.post<MarketingTestRecipient>(`/marketing/brands/${marketingBrandId}/test-recipients`, data),
  setTestRecipientActive: (
    marketingBrandId: string,
    recipientId: string,
    isActive: boolean,
  ) => api.patch<MarketingTestRecipient>(`/marketing/brands/${marketingBrandId}/test-recipients/${recipientId}`, { isActive }),
  listBroadcasts: (marketingBrandId: string, from: string, to: string) =>
    api.get<MarketingBroadcast[]>(`/marketing/brands/${marketingBrandId}/broadcasts`, { params: { from, to } }),
  getBroadcast: (marketingBrandId: string, broadcastId: string) =>
    api.get<MarketingBroadcast>(`/marketing/brands/${marketingBrandId}/broadcasts/${broadcastId}`),
  createScheduledBroadcast: (
    marketingBrandId: string,
    data: MarketingBroadcastScheduleInput,
  ) => api.post<MarketingBroadcast>(`/marketing/brands/${marketingBrandId}/broadcasts`, data),
  rescheduleBroadcast: (
    marketingBrandId: string,
    broadcastId: string,
    data: MarketingBroadcastScheduleInput & { expectedScheduledVersionNumber: number },
  ) => api.post<MarketingBroadcast>(`/marketing/brands/${marketingBrandId}/broadcasts/${broadcastId}/reschedule`, data),
  cancelBroadcast: (
    marketingBrandId: string,
    broadcastId: string,
    data: { expectedScheduledVersionNumber: number; reason?: string | null },
  ) => api.post<{ changed: boolean; broadcast: MarketingBroadcast }>(
    `/marketing/brands/${marketingBrandId}/broadcasts/${broadcastId}/cancel`,
    data,
  ),
  listFlows: (marketingBrandId: string) =>
    api.get<MarketingFlowSummary[]>(`/marketing/brands/${marketingBrandId}/flows`),
  getSequenceIntakeCatalog: (marketingBrandId: string) =>
    api.get<MarketingSequenceIntakeCatalog>(`/marketing/brands/${marketingBrandId}/sequence-intakes/catalog`),
  listSequenceIntakes: (marketingBrandId: string) =>
    api.get<MarketingSequenceIntakeSummary[]>(`/marketing/brands/${marketingBrandId}/sequence-intakes`),
  getSequenceIntake: (marketingBrandId: string, intakeId: string) =>
    api.get<MarketingSequenceIntakeDetail>(`/marketing/brands/${marketingBrandId}/sequence-intakes/${intakeId}`),
  createSequenceIntake: (marketingBrandId: string, data: MarketingSequenceIntakeInput) =>
    api.post<MarketingSequenceIntakeDetail>(`/marketing/brands/${marketingBrandId}/sequence-intakes`, data),
  createSequenceIntakeVersion: (
    marketingBrandId: string,
    intakeId: string,
    data: MarketingSequenceIntakeInput & { expectedLatestVersionNumber: number },
  ) => api.post<MarketingSequenceIntakeDetail>(
    `/marketing/brands/${marketingBrandId}/sequence-intakes/${intakeId}/draft-versions`,
    data,
  ),
  getFlow: (marketingBrandId: string, flowId: string) =>
    api.get<MarketingFlowDetail>(`/marketing/brands/${marketingBrandId}/flows/${flowId}`),
  createFlow: (
    marketingBrandId: string,
    data: { name: string; description?: string | null; graph: unknown },
  ) => api.post<MarketingFlowDetail>(`/marketing/brands/${marketingBrandId}/flows`, data),
  createFlowDraftVersion: (
    marketingBrandId: string,
    flowId: string,
    data: { graph: unknown; expectedLatestVersionNumber: number },
  ) => api.post<MarketingFlowDetail>(`/marketing/brands/${marketingBrandId}/flows/${flowId}/draft-versions`, data),
  publishFlow: (
    marketingBrandId: string,
    flowId: string,
    data: {
      versionNumber: number;
      expectedPublishedVersionNumber: number | null;
      activeRunPolicy: MarketingFlowActiveRunPolicy;
    },
  ) => api.post<MarketingFlowPublishResult>(`/marketing/brands/${marketingBrandId}/flows/${flowId}/publish`, data),
  simulateFlow: (
    marketingBrandId: string,
    data: { graph: unknown; scenario: Record<string, unknown> },
  ) => api.post<MarketingFlowSimulationResult>(`/marketing/brands/${marketingBrandId}/flows/simulate`, data),
};

export interface PickupMapConfig {
  store: { id: string; name: string; url: string } | null;
  provider: 'inpost-geowidget';
  publicToken: string;
  isActive: boolean;
  configured: boolean;
  hasPointsApiToken: boolean;
  inheritDefault: boolean;
  effective: { source: 'store' | 'global' | 'none'; isActive: boolean; configured: boolean };
  updatedAt: string | null;
}
export const pickupMapsApi = {
  get: (storeId: string) => api.get<PickupMapConfig>(`/pickup-maps/${storeId}`),
  save: (storeId: string, data: { publicToken?: string; pointsApiToken?: string; isActive?: boolean; inheritDefault?: boolean }) => api.put<PickupMapConfig>(`/pickup-maps/${storeId}`, data),
  disable: (storeId: string) => api.delete(`/pickup-maps/${storeId}`),
};
